// src/score_render/bitmapRenderer/SystemRenderer.ts
import { Container as Container3, Graphics as Graphics3, Point as Point3, Sprite as Sprite4, Text as Text2 } from "pixi.js";
import {
  BeamState as BeamState2,
  ChordSymbol,
  ClefType,
  Dynamic,
  DynamicType,
  EndingType,
  PitchUtils,
  RepeatDirection,
  StemDirection as StemDirection2,
  TieState
} from "score_data";

// src/score_render/textures/TextureSet.ts
import { Rectangle, Sprite, Texture } from "pixi.js";

// src/score_render/textures/Symbol.ts
var _Symbol = class _Symbol {
  constructor(id, x, y, width, height, svg = null) {
    this.id = id;
    this._x = x;
    this._y = y;
    this._width = width;
    this._height = height;
    this._svg = svg;
  }
  get x() {
    return this._x;
  }
  get y() {
    return this._y;
  }
  get scaledX() {
    return Math.floor(_Symbol.coordinateScale * this._x);
  }
  get scaledY() {
    return Math.floor(_Symbol.coordinateScale * this._y);
  }
  get scaledWidth() {
    return Math.ceil(_Symbol.coordinateScale * this._width) + 1;
  }
  get scaledHeight() {
    return Math.ceil(_Symbol.coordinateScale * this._height) + 1;
  }
  get svg() {
    return this._svg;
  }
};
_Symbol.coordinateScale = 1;
var Symbol = _Symbol;

// src/score_render/textures/MusicSymbolsSvg.ts
var _MusicSymbolsSvg = class _MusicSymbolsSvg {
  static setScale(pixelsPerSpace) {
    let promise = new Promise((resolve, reject) => {
      this._currentScale = pixelsPerSpace / _MusicSymbolsSvg._DEFAULT_PIXELS_PER_SPACE;
      Symbol.coordinateScale = this._currentScale * _MusicSymbolsSvg.DEFAULT_COORDINATE_SCALE;
      if (this._scaledAtlases.has(this._currentScale)) {
        this.atlasCanvas = this._scaledAtlases.get(this._currentScale);
        resolve();
        return;
      }
      if (this.symbols == null)
        this._createSymbolsSvg();
      let img = document.createElement("img");
      img.onload = (e) => {
        let canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        let ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        this._scaledAtlases.set(this._currentScale, canvas);
        this.atlasCanvas = canvas;
        resolve();
      };
      let header = this._getSvgHeader(
        _MusicSymbolsSvg._DEFAULT_SVG_WIDTH * this._currentScale,
        _MusicSymbolsSvg._DEFAULT_SVG_HEIGHT * this._currentScale
      );
      img.src = "data:image/svg+xml;charset=utf-8," + header + this.symbols;
    });
    return promise;
  }
  /**combines all of the symbols' SVG strings into a single string. Each
   * symbol is in its own group element. The closing </svg> suffix is
   * appended, but the header svg is left off so that it can be adjusted as
   * needed for scaling.*/
  static _createSymbolsSvg() {
    this.symbols = "";
    for (let key in this.symbolsMap) {
      let val = this.symbolsMap[key];
      this.symbols += `<g transform="translate(${val.x},${val.y})">${val.svg}</g>`;
    }
    this.symbols += "</svg>";
  }
  static _getSvgHeader(width, height) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 1200 600" preserveAspectRatio="xMinYMin meet">`;
  }
};
_MusicSymbolsSvg.DEFAULT_COORDINATE_SCALE = 1;
_MusicSymbolsSvg._DEFAULT_PIXELS_PER_SPACE = 10;
_MusicSymbolsSvg._DEFAULT_SVG_WIDTH = 1200;
_MusicSymbolsSvg._DEFAULT_SVG_HEIGHT = 600;
_MusicSymbolsSvg._scaledAtlases = /* @__PURE__ */ new Map();
_MusicSymbolsSvg._currentScale = 1;
_MusicSymbolsSvg.symbolsMap = {
  "TrebleClef": new Symbol("TrebleClef", 650, 400, 29, 79, '<path id="TrebleClef" d="M16.679,0.495c0.425,0 0.966,0.402 1.628,1.203c1.319,1.514 2.408,3.444 3.255,5.804c0.85,2.361 1.276,4.648 1.276,6.864c0,0.282 0,0.569 0,0.851c-0.332,4.953 -2.549,9.392 -6.652,13.305c-0.521,0.473 -0.898,0.849 -1.136,1.135l-0.496,0.424l0.85,3.894c0.614,2.595 0.922,3.916 0.922,3.962l0.212,-0.072l2.265,0c2.784,0.332 5.07,1.49 6.864,3.47c1.792,1.981 2.735,4.387 2.831,7.217c0,0.095 0,0.213 0,0.354c0,2.221 -0.674,4.307 -2.018,6.263c-1.342,1.96 -3.102,3.387 -5.271,4.282c-0.189,0.095 -0.286,0.151 -0.286,0.179c0,0.021 0.165,0.885 0.496,2.585c0.472,2.121 0.755,3.535 0.849,4.243c0.047,0.614 0.072,1.228 0.072,1.84c0,3.351 -1.131,5.944 -3.398,7.784c-1.699,1.371 -3.659,2.055 -5.875,2.055c-1.415,0 -2.76,-0.294 -4.032,-0.884c-1.275,-0.592 -2.385,-1.429 -3.33,-2.512c-0.754,-0.944 -1.13,-1.962 -1.13,-3.044c0,-1.322 0.472,-2.478 1.416,-3.467c0.941,-0.945 2.051,-1.416 3.326,-1.416c1.228,0 2.311,0.424 3.254,1.272c0.945,0.851 1.418,1.937 1.418,3.258l0,0.068c0,1.182 -0.428,2.195 -1.276,3.044c-0.848,0.849 -1.863,1.277 -3.042,1.277l-0.142,0l0.068,0.141c0.568,0.281 1.274,0.497 2.123,0.636c0.427,0.096 0.849,0.142 1.276,0.142c1.699,0 3.23,-0.482 4.6,-1.451c1.369,-0.97 2.288,-2.205 2.762,-3.714c0.328,-0.992 0.495,-2.054 0.495,-3.185c0,-1.037 -0.141,-2.172 -0.427,-3.4c-0.613,-3.111 -0.945,-4.716 -0.992,-4.811l-0.352,0.074c-1.086,0.281 -2.195,0.423 -3.328,0.423c-2.076,0 -4.115,-0.434 -6.125,-1.308c-2.004,-0.874 -3.714,-2.066 -5.129,-3.574c-2.882,-3.068 -4.316,-6.7 -4.316,-10.902c0,-3.49 1.083,-6.723 3.254,-9.695c2.263,-3.162 4.789,-6.134 7.575,-8.918l0.992,-1.06l-0.354,-1.631c-0.237,-0.939 -0.381,-1.555 -0.426,-1.836c-0.472,-2.03 -0.779,-3.47 -0.92,-4.321c-0.189,-1.038 -0.281,-2.073 -0.281,-3.113c0,-4.294 1.319,-8.044 3.964,-11.255c0.376,-0.47 0.849,-0.976 1.412,-1.52c0.568,-0.542 0.923,-0.839 1.063,-0.883c0.101,-0.051 0.17,-0.077 0.216,-0.077Zm-3.396,30.931c-0.095,0 -0.918,0.919 -2.478,2.76c-1.981,2.359 -3.376,4.225 -4.176,5.594c-1.463,2.311 -2.194,4.788 -2.194,7.43c0,1.699 0.331,3.304 0.992,4.814c0.85,2.264 2.229,4.059 4.142,5.377c1.909,1.323 3.974,1.984 6.191,1.984c0.612,0 1.205,-0.049 1.771,-0.145c1.083,-0.141 1.627,-0.281 1.627,-0.423l-0.567,-2.831c-0.144,-0.567 -0.332,-1.403 -0.566,-2.516c-0.238,-1.105 -0.379,-1.804 -0.426,-2.087c-0.238,-1.132 -0.378,-1.746 -0.425,-1.839c0,-0.047 -0.012,-0.116 -0.036,-0.211c-0.022,-0.096 -0.034,-0.164 -0.034,-0.213c0,-0.045 -0.213,-1.037 -0.636,-2.973l-0.568,-2.761l-0.068,0c-0.096,0 -0.354,0.096 -0.779,0.285c-0.425,0.186 -0.755,0.353 -0.992,0.495c-0.849,0.564 -1.487,1.286 -1.909,2.158c-0.425,0.875 -0.635,1.781 -0.635,2.723c0,1.984 0.848,3.493 2.544,4.53c0.332,0.19 0.496,0.401 0.496,0.636c0,0.098 -0.022,0.145 -0.069,0.145c-0.048,0.377 -0.236,0.564 -0.567,0.564c-0.186,0 -0.354,-0.021 -0.496,-0.069c-1.132,-0.519 -2.111,-1.237 -2.938,-2.161c-0.827,-0.917 -1.426,-1.969 -1.802,-3.148c-0.285,-0.847 -0.427,-1.72 -0.427,-2.617c0,-1.935 0.59,-3.695 1.772,-5.274c1.179,-1.579 2.714,-2.697 4.6,-3.362l0.142,-0.071c0,-0.092 -0.186,-0.965 -0.563,-2.619c0,-0.047 -0.146,-0.777 -0.429,-2.194l-0.497,-1.981Zm5.025,-24.42c-0.707,0 -1.462,0.285 -2.263,0.85c-0.803,0.568 -1.512,1.299 -2.125,2.195c-1.228,1.887 -1.841,4.104 -1.841,6.655c0,0.755 0.071,1.534 0.214,2.335c0,0.188 0.327,1.816 0.99,4.884c0.095,0 0.567,-0.472 1.417,-1.417c3.396,-3.822 5.307,-7.503 5.73,-11.038c0.048,-0.285 0.073,-0.59 0.073,-0.921c0,-1.085 -0.143,-1.909 -0.427,-2.479c-0.237,-0.613 -0.731,-0.967 -1.485,-1.063c-0.093,-0.001 -0.189,-0.001 -0.283,-0.001Zm-0.635,36.099c-0.19,0 -0.285,0.021 -0.285,0.071l1.415,6.368c0.945,4.483 1.418,6.75 1.418,6.795c0.232,1.228 0.35,1.864 0.35,1.908c0.097,0 0.428,-0.211 0.992,-0.636c1.795,-1.272 2.95,-2.899 3.471,-4.884c0.141,-0.61 0.211,-1.25 0.211,-1.911c0,-1.936 -0.636,-3.644 -1.908,-5.131c-1.276,-1.485 -2.881,-2.324 -4.813,-2.512c-0.333,-0.046 -0.615,-0.068 -0.851,-0.068Z" style="fill-rule:nonzero;"/>'),
  "BassClef": new Symbol("BassClef", 50, 0, 24, 30, '<path id="BassClef" d="M8.835,0.781c1.014,0 2.007,0.112 2.982,0.337c2.174,0.414 3.965,1.295 5.372,2.644c1.406,1.351 2.242,3 2.504,4.951c0.074,0.339 0.113,0.733 0.113,1.18c0,1.203 -0.208,2.505 -0.621,3.91c-0.412,1.408 -0.975,2.75 -1.687,4.021c-1.613,2.925 -3.882,5.401 -6.807,7.425c-2.925,2.026 -6.205,3.435 -9.842,4.221c-0.151,0.038 -0.281,0.055 -0.394,0.055c-0.263,0 -0.394,-0.131 -0.394,-0.394c0,-0.147 0.037,-0.251 0.113,-0.31c0.074,-0.057 0.243,-0.121 0.505,-0.197c4.163,-1.575 7.537,-3.909 10.126,-7.004c2.586,-3.092 4.012,-6.495 4.272,-10.208c0.037,-0.412 0.057,-0.824 0.057,-1.238c0,-3.711 -1.107,-6.281 -3.318,-7.707c-0.938,-0.563 -1.97,-0.844 -3.096,-0.844c-1.275,0 -2.482,0.33 -3.628,0.987c-1.146,0.654 -1.996,1.545 -2.559,2.671c-0.15,0.336 -0.224,0.544 -0.224,0.618l0.112,-0.055c0.561,-0.376 1.181,-0.563 1.855,-0.563c1.049,0 1.911,0.375 2.587,1.125c0.638,0.675 0.957,1.483 0.957,2.418c0,0.899 -0.3,1.708 -0.899,2.419c-0.599,0.712 -1.369,1.105 -2.307,1.181c-0.147,0 -0.281,0 -0.394,0c-1.087,0 -2.017,-0.43 -2.784,-1.293c-0.769,-0.862 -1.154,-1.913 -1.154,-3.148c0,-0.077 0,-0.133 0,-0.169c0.037,-1.875 0.815,-3.479 2.333,-4.809c1.518,-1.333 3.383,-2.073 5.596,-2.224c0.194,0 0.4,0 0.624,0Zm13.557,3.543c0.228,0 0.394,0 0.505,0c0.639,0.224 0.957,0.675 0.957,1.351c0,0.527 -0.224,0.92 -0.675,1.182c-0.224,0.113 -0.448,0.169 -0.675,0.169c-0.449,0 -0.806,-0.169 -1.069,-0.505c-0.188,-0.228 -0.28,-0.509 -0.28,-0.846c0,-0.339 0.101,-0.636 0.31,-0.899c0.206,-0.263 0.478,-0.412 0.814,-0.451c0.036,-0.001 0.075,-0.001 0.113,-0.001Zm0,8.494c0.228,0 0.394,0.021 0.505,0.057c0.639,0.224 0.957,0.675 0.957,1.351c0,0.523 -0.224,0.918 -0.675,1.182c-0.224,0.076 -0.448,0.113 -0.675,0.113c-0.449,0 -0.806,-0.151 -1.069,-0.452c-0.188,-0.262 -0.28,-0.543 -0.28,-0.843c0,-0.339 0.101,-0.638 0.31,-0.9c0.206,-0.261 0.478,-0.432 0.814,-0.505c0.036,-0.003 0.075,-0.003 0.113,-0.003Z" style="fill-rule:nonzero;"/>'),
  "CClef": new Symbol("CClef", 0, 0, 24, 38, '<path id="CClef" d="M0.781,0.781l4.331,0l0,36.561l-2.138,0l-2.193,0l0,-18.28l0,-18.281Zm5.963,0l1.124,0l0,17.493l0.169,-0.17c0.339,-0.261 0.675,-0.636 1.012,-1.123c1.202,-1.539 2.101,-3.471 2.702,-5.795c0.076,-0.3 0.13,-0.488 0.168,-0.563c0.037,-0.074 0.13,-0.113 0.281,-0.113c0.261,0 0.412,0.188 0.449,0.563c0.114,1.426 0.582,2.607 1.408,3.543c0.824,0.938 1.78,1.408 2.868,1.408c0.147,0 0.3,0 0.451,0c0.975,-0.151 1.65,-0.591 2.023,-1.323c0.375,-0.73 0.581,-2.033 0.62,-3.91c0.037,-0.636 0.055,-1.238 0.055,-1.799c0,-3.414 -0.47,-5.532 -1.405,-6.356c-0.639,-0.487 -1.482,-0.732 -2.533,-0.732c-0.975,0 -1.874,0.206 -2.701,0.618l-0.112,0.058l0.057,0c0.3,0.039 0.523,0.113 0.675,0.224c0.749,0.339 1.124,0.92 1.124,1.744c0,0.563 -0.188,1.059 -0.561,1.49c-0.375,0.433 -0.902,0.647 -1.578,0.647c-0.636,0 -1.163,-0.225 -1.575,-0.675c-0.412,-0.449 -0.618,-0.957 -0.618,-1.517c0,-0.639 0.224,-1.182 0.675,-1.632c1.238,-1.199 2.774,-1.801 4.612,-1.801c1.426,0 2.765,0.375 4.021,1.125c1.256,0.75 2.223,1.799 2.897,3.149c0.524,1.09 0.788,2.289 0.788,3.6c0,1.014 -0.169,2.007 -0.505,2.982c-0.526,1.462 -1.389,2.653 -2.588,3.57c-1.199,0.919 -2.491,1.378 -3.879,1.378c-0.901,0 -1.744,-0.281 -2.531,-0.842l-0.169,-0.113l-1.012,1.575l-0.901,1.575l0.901,1.575c0.636,1.051 0.974,1.575 1.012,1.575l0.169,-0.112c0.788,-0.563 1.667,-0.845 2.644,-0.845c0.148,0 0.318,0 0.506,0c1.462,0.188 2.755,0.827 3.882,1.914c1.125,1.086 1.893,2.438 2.306,4.049c0.112,0.675 0.169,1.332 0.169,1.969c0,1.652 -0.433,3.133 -1.293,4.444c-0.714,1.087 -1.642,1.932 -2.785,2.531c-1.144,0.599 -2.353,0.899 -3.628,0.899c-1.838,0 -3.375,-0.6 -4.612,-1.799c-0.451,-0.451 -0.675,-0.993 -0.675,-1.632c0,-0.563 0.206,-1.068 0.618,-1.52c0.412,-0.448 0.938,-0.672 1.575,-0.672c0.638,0 1.154,0.215 1.547,0.645c0.394,0.434 0.59,0.929 0.59,1.49c0,0.827 -0.375,1.408 -1.124,1.744c-0.151,0.113 -0.375,0.188 -0.675,0.228l-0.057,0l0.113,0.055c0.827,0.414 1.726,0.619 2.701,0.619c1.051,0 1.893,-0.244 2.531,-0.73c0.936,-0.824 1.405,-2.943 1.405,-6.356c0,-0.563 -0.018,-1.163 -0.055,-1.802c-0.039,-1.874 -0.245,-3.176 -0.62,-3.909c-0.374,-0.73 -1.048,-1.169 -2.022,-1.319c-0.151,0 -0.303,0 -0.452,0c-1.087,0 -2.044,0.469 -2.868,1.405c-0.827,0.938 -1.293,2.12 -1.408,3.545c-0.037,0.375 -0.188,0.561 -0.448,0.561c-0.151,0 -0.245,-0.037 -0.281,-0.113c-0.039,-0.072 -0.094,-0.261 -0.17,-0.563c-0.6,-2.324 -1.498,-4.255 -2.701,-5.793c-0.336,-0.488 -0.672,-0.863 -1.012,-1.124l-0.168,-0.169l0,17.497l-0.563,0l-0.563,0l0,-18.284l0,-18.281Z" style="fill-rule:nonzero;"/>'),
  "Sharp": new Symbol("Sharp", 800, 200, 10, 29, '<path id="Sharp" d="M7.015,0.25c0.268,0 0.433,0.114 0.508,0.339c0.038,0.037 0.055,1.199 0.055,3.488l0,3.374c0.788,-0.188 1.276,-0.284 1.465,-0.284c0.263,0 0.432,0.096 0.505,0.284c0.037,0.074 0.061,0.517 0.061,1.323c0,0.806 -0.021,1.245 -0.061,1.319c-0.037,0.114 -0.109,0.197 -0.225,0.256c-0.11,0.055 -0.433,0.137 -0.957,0.25l-0.789,0.224l0,5.626c0.789,-0.188 1.276,-0.279 1.465,-0.279c0.147,0 0.279,0.037 0.396,0.11c0.073,0.114 0.117,0.207 0.144,0.283c0.018,0.074 0.026,0.375 0.026,0.896l0,0.678c0,0.485 -0.011,0.778 -0.026,0.87c-0.021,0.096 -0.067,0.179 -0.144,0.251c-0.078,0.078 -0.432,0.188 -1.071,0.339l-0.787,0.224l0,3.435c0,2.289 -0.019,3.469 -0.054,3.543c-0.08,0.224 -0.244,0.339 -0.509,0.339c-0.188,0 -0.314,-0.059 -0.395,-0.168l-0.055,0c-0.073,-0.078 -0.11,-0.75 -0.11,-2.028l0,-1.574l0,-3.319c-0.192,0.078 -0.389,0.142 -0.592,0.197c-0.211,0.061 -0.406,0.087 -0.596,0.087c-0.787,0.224 -1.188,0.339 -1.209,0.339c-0.019,0 -0.027,1.182 -0.027,3.543c0,2.362 0,3.562 0,3.598c-0.073,0.225 -0.242,0.339 -0.504,0.339c-0.149,0 -0.282,-0.056 -0.395,-0.169l-0.061,-0.055c-0.073,-0.037 -0.11,-0.696 -0.11,-1.969l0,-1.635l0,-3.373c-0.751,0.188 -1.222,0.279 -1.409,0.279c-0.262,0 -0.432,-0.091 -0.505,-0.279c-0.036,-0.074 -0.055,-0.518 -0.055,-1.324c0,-0.805 0.019,-1.245 0.055,-1.322c0.036,-0.11 0.11,-0.197 0.226,-0.252c0.113,-0.055 0.41,-0.142 0.899,-0.25l0.787,-0.169l0,-2.87l0,-2.811c-0.751,0.188 -1.222,0.279 -1.41,0.279c-0.261,0 -0.432,-0.091 -0.504,-0.279c-0.038,-0.074 -0.055,-0.517 -0.055,-1.322c0,-0.806 0.017,-1.246 0.055,-1.324c0.036,-0.11 0.11,-0.197 0.224,-0.25c0.114,-0.055 0.412,-0.143 0.9,-0.251l0.788,-0.169l0,-3.488c0,-2.289 0.019,-3.469 0.056,-3.543c0.072,-0.225 0.241,-0.339 0.508,-0.339c0.146,0 0.276,0.056 0.395,0.169l0.055,0c0.036,0.078 0.055,0.75 0.055,2.023l0,1.574c0,2.216 0.01,3.319 0.025,3.319c0.019,0 0.422,-0.091 1.21,-0.279l1.188,-0.339c0,-4.725 0.016,-7.104 0.054,-7.142c0.036,-0.151 0.127,-0.243 0.278,-0.284c0.086,-0.04 0.157,-0.058 0.232,-0.058Zm-0.56,10.798l-0.394,0.114c-0.079,0.037 -0.473,0.133 -1.188,0.279l-0.843,0.225l0,2.815c0,1.913 0.011,2.87 0.026,2.87c0.019,0 0.422,-0.096 1.209,-0.284l1.188,-0.339c0.002,-3.784 0.002,-5.68 0.002,-5.68Z" style="fill-rule:nonzero;"/>'),
  "Natural": new Symbol("Natural", 250, 200, 7, 25, '<path id="Natural" d="M0.819,0.25c0.261,0 0.432,0.133 0.505,0.394c0.037,0.042 0.06,1.1 0.06,3.182c0,2.078 0.018,3.122 0.055,3.122c0.824,-0.188 1.575,-0.375 2.248,-0.563c1.539,-0.374 2.366,-0.563 2.477,-0.563c0.037,0 0.092,0 0.169,0c0.114,0.073 0.206,0.188 0.279,0.339l0,17.778c-0.073,0.261 -0.243,0.394 -0.504,0.394c-0.151,0 -0.284,-0.06 -0.394,-0.169l-0.055,-0.055c-0.078,-0.042 -0.114,-0.714 -0.114,-2.028l0,-1.406c0,-2.101 -0.018,-3.149 -0.055,-3.149c-3.041,0.751 -4.614,1.126 -4.729,1.126c-0.037,0 -0.091,-0.018 -0.165,-0.06c-0.114,-0.037 -0.212,-0.128 -0.284,-0.279l0,-0.114l0,-8.775l0,-8.716l0,-0.06c0.037,-0.074 0.087,-0.137 0.142,-0.197c0.055,-0.055 0.101,-0.082 0.142,-0.082c0.073,-0.077 0.147,-0.119 0.223,-0.119Zm4.725,10.185c0,-0.788 -0.01,-1.181 -0.027,-1.181c-0.018,0 -0.723,0.165 -2.11,0.505l-1.97,0.508l-0.055,2.472c0,0.453 0,0.83 0,1.126c0,0.9 0.01,1.351 0.027,1.351c0.018,0 0.719,-0.188 2.11,-0.563l2.022,-0.504l0,-2.422c0.003,-0.601 0.003,-1.031 0.003,-1.292Z" style="fill-rule:nonzero;"/>'),
  "DoubleSharp": new Symbol("DoubleSharp", 200, 300, 10, 10, '<path d="M0,0l3.259,0l0,2.422l0.788,0.842l0.788,0.789l0.842,-0.789l0.787,-0.842l0,-1.239l0,-1.183l3.269,0l0,3.209l-1.236,0l-1.246,0l-0.789,0.844l-0.842,0.788l0.842,0.786l0.789,0.845l2.482,0l0,3.21l-1.63,0l-1.639,0l0,-1.184l0,-1.24l-0.787,-0.842l-0.842,-0.789l-0.788,0.789l-0.788,0.842l0,2.424l-1.629,0l-1.63,0l0,-1.577l0,-1.633l2.472,0l0.789,-0.845l0.788,-0.786l-0.788,-0.788l-0.789,-0.844l-1.236,0l-1.236,0l0,-1.634l0,-1.575Z" style="fill-rule:nonzero;"/>'),
  "QuarterNoteHead": new Symbol("QuarterNoteHead", 500, 300, 12, 11, '<path id="QuarterNotehead" fill="white" d="M7.886,0.375c1.199,0 2.186,0.33 2.952,0.984c0.766,0.658 1.154,1.492 1.154,2.504c0,0.225 -0.021,0.447 -0.061,0.678c-0.188,1.234 -0.832,2.371 -1.938,3.4c-1.104,1.029 -2.393,1.678 -3.853,1.939c-0.453,0.11 -0.92,0.17 -1.412,0.17c-1.198,0 -2.184,-0.329 -2.951,-0.982c-0.769,-0.66 -1.154,-1.492 -1.154,-2.504c0,-0.227 0.018,-0.449 0.059,-0.68c0.225,-1.385 1.002,-2.621 2.334,-3.712c1.327,-1.086 2.783,-1.685 4.356,-1.8c0.157,0.003 0.321,0.003 0.514,0.003Z" style="fill-rule:nonzero;"/>'),
  "HalfNoteHead": new Symbol("HalfNoteHead", 450, 300, 12, 11, '<path id="HalfNotehead" fill="white" d="M7.823,0.375c1.199,0 2.184,0.33 2.952,0.984c0.765,0.658 1.154,1.492 1.154,2.504c0,0.225 -0.021,0.447 -0.059,0.678c-0.187,1.234 -0.832,2.371 -1.939,3.4c-1.104,1.029 -2.392,1.678 -3.851,1.939c-0.453,0.11 -0.92,0.17 -1.411,0.17c-1.199,0 -2.184,-0.329 -2.953,-0.982c-0.768,-0.66 -1.155,-1.492 -1.155,-2.504c0,-0.227 0.018,-0.449 0.061,-0.68c0.224,-1.385 1.001,-2.621 2.333,-3.712c1.327,-1.086 2.784,-1.685 4.357,-1.8c0.154,0.003 0.318,0.003 0.511,0.003Zm2.078,1.295c-0.897,0 -2.078,0.469 -3.543,1.406c-1.199,0.75 -2.243,1.611 -3.122,2.586c-0.879,0.977 -1.358,1.781 -1.433,2.424l0,0.056c0,0.409 0.261,0.615 0.788,0.615c0.601,0 1.351,-0.213 2.248,-0.646c0.901,-0.43 1.822,-1.021 2.755,-1.771c0.901,-0.713 1.644,-1.447 2.226,-2.193c0.581,-0.75 0.87,-1.351 0.87,-1.804c0,-0.336 -0.133,-0.521 -0.395,-0.563c-0.15,-0.073 -0.278,-0.11 -0.394,-0.11Z" style="fill-rule:nonzero;"/>'),
  "WholeNoteHead": new Symbol("WholeNoteHead", 800, 300, 17, 11, '<path id="WholeNote" fill="white" d="M8.153,0.375c2.064,0 3.872,0.402 5.429,1.209c1.557,0.812 2.467,1.831 2.729,3.066c0.036,0.188 0.057,0.375 0.057,0.563c0,1.352 -0.788,2.494 -2.361,3.436c-1.576,0.934 -3.517,1.404 -5.816,1.404c-2.309,0 -4.25,-0.473 -5.823,-1.404c-1.575,-0.938 -2.36,-2.084 -2.36,-3.436c0,-1.234 0.691,-2.324 2.078,-3.265c1.386,-0.934 3.11,-1.461 5.177,-1.575c0.29,0.002 0.592,0.002 0.89,0.002Zm-0.957,0.563c-0.824,0 -1.472,0.303 -1.938,0.9c-0.473,0.601 -0.705,1.37 -0.705,2.308c0,1.012 0.298,1.986 0.901,2.926c0.447,0.788 1.012,1.384 1.684,1.8c0.679,0.411 1.332,0.618 1.97,0.618c1.198,0 2.004,-0.581 2.423,-1.744c0.188,-0.447 0.277,-0.92 0.277,-1.404c0,-1.199 -0.41,-2.344 -1.234,-3.435c-0.83,-1.085 -1.78,-1.727 -2.87,-1.912c-0.186,-0.039 -0.357,-0.057 -0.508,-0.057Z" style="fill-rule:nonzero;"/>'),
  "DoubleWholeNoteHead": new Symbol("DoubleWholeNoteHead", 800, 400, 24, 13, '<path id="DoubleWholeNote" fill="white" d="M0.708,0.006c0.09,0 0.153,0 0.19,0c0.153,0.037 0.282,0.133 0.396,0.279c0.073,0.078 0.109,1.275 0.109,3.602l0,4.726c0,2.326 -0.036,3.524 -0.109,3.603c-0.146,0.184 -0.341,0.278 -0.561,0.278c-0.341,0 -0.568,-0.149 -0.678,-0.448c-0.037,-0.042 -0.055,-2.01 -0.055,-5.908c0,-3.75 0.018,-5.642 0.055,-5.681c0.037,-0.111 0.091,-0.208 0.165,-0.279c0.081,-0.073 0.175,-0.133 0.281,-0.168c0.042,-0.004 0.106,-0.004 0.207,-0.004Zm2.443,0c0.109,0 0.182,0 0.229,0c0.22,0.078 0.366,0.225 0.448,0.447c0,0.043 0,0.9 0,2.59c0,1.646 0.017,2.473 0.055,2.473l0,-0.055c0.303,-1.161 1.219,-2.13 2.754,-2.896c1.539,-0.769 3.297,-1.153 5.283,-1.153c0.533,0 1.055,0.036 1.576,0.112c1.692,0.149 3.132,0.593 4.34,1.321c1.199,0.729 1.923,1.604 2.188,2.616l0.055,0.054l0,-2.472c0,-1.729 0.019,-2.647 0.055,-2.761c0.156,-0.184 0.356,-0.278 0.624,-0.278c0.229,0 0.411,0.097 0.56,0.278l0.063,0c0.037,0.078 0.055,1.276 0.055,3.602l0,4.721c0,2.326 -0.018,3.524 -0.055,3.604l-0.063,0c-0.146,0.183 -0.33,0.277 -0.56,0.277c-0.23,0 -0.412,-0.097 -0.561,-0.277c-0.081,-0.078 -0.118,-0.212 -0.118,-0.396c0,-0.19 0,-0.979 0,-2.365l0,-2.473l-0.055,0.056c-0.267,1.012 -0.988,1.886 -2.188,2.613c-1.208,0.731 -2.646,1.172 -4.34,1.321c-0.521,0.078 -1.043,0.114 -1.576,0.114c-1.986,0 -3.744,-0.384 -5.283,-1.153c-1.538,-0.771 -2.454,-1.735 -2.754,-2.896l0,-0.057c-0.038,0 -0.055,0.826 -0.055,2.475c0,1.387 0,2.174 0,2.364c0,0.184 -0.038,0.315 -0.118,0.396c-0.11,0.183 -0.293,0.278 -0.56,0.278c-0.342,0 -0.539,-0.15 -0.623,-0.448c-0.035,-0.042 -0.056,-2.011 -0.056,-5.909c0,-3.749 0.021,-5.641 0.056,-5.681c0.036,-0.225 0.176,-0.371 0.395,-0.448c0.038,0.006 0.122,0.006 0.229,0.006Zm7.873,1.97c-0.824,0 -1.481,0.304 -1.97,0.9c-0.483,0.602 -0.731,1.369 -0.731,2.308c0,1.014 0.304,1.986 0.904,2.926c0.485,0.786 1.056,1.383 1.712,1.8c0.659,0.411 1.301,0.617 1.939,0.617c1.197,0 2.022,-0.58 2.472,-1.742c0.155,-0.448 0.229,-0.921 0.229,-1.405c0,-1.199 -0.404,-2.345 -1.21,-3.435c-0.806,-1.087 -1.774,-1.728 -2.903,-1.913c-0.176,-0.04 -0.33,-0.056 -0.442,-0.056Zm12.177,-1.97c0.093,0 0.166,0 0.202,0c0.146,0.037 0.282,0.133 0.395,0.279c0.073,0.078 0.109,1.275 0.109,3.602l0,4.719c0,2.326 -0.036,3.525 -0.109,3.603c-0.11,0.184 -0.304,0.279 -0.568,0.279c-0.341,0 -0.561,-0.15 -0.668,-0.448c-0.037,-0.042 -0.063,-2.01 -0.063,-5.908c0,-3.75 0.025,-5.642 0.063,-5.682c0.073,-0.226 0.221,-0.372 0.448,-0.447c0.037,0.003 0.1,0.003 0.191,0.003Z" style="fill-rule:nonzero;"/>'),
  "DoubleWholeRest": new Symbol("DoubleWholeRest", 250, 300, 11, 9, '<path id="DoubleWholeRest" d="M0,0l10.236,0l0,9l-5.118,0l-5.118,0l0,-4.5l0,-4.5Z" style="fill-rule:nonzero;"/>'),
  "WholeRest": new Symbol("WholeRest", 850, 300, 13, 5, '<path id="WholeRest" d="M0.751,0.375l11.926,0l0,4.5l-5.96,0l-5.966,0l0,-2.305l0,-2.195Z" style="fill-rule:nonzero;"/>'),
  "HalfRest": new Symbol("HalfRest", 900, 300, 13, 5, '<path id="WholeRest" d="M0.751,0.375l11.926,0l0,4.5l-5.96,0l-5.966,0l0,-2.305l0,-2.195Z" style="fill-rule:nonzero;"/>'),
  "QuarterRest": new Symbol("QuarterRest", 200, 200, 9, 27, '<path id="QuarterRest" d="M1.655,0.25c0.11,0 0.261,0.094 0.449,0.281c0.151,0.112 0.243,0.188 0.284,0.224c0.11,0.076 0.243,0.207 0.394,0.394l0.394,0.339c0.11,0.077 0.225,0.167 0.334,0.279c0.114,0.078 0.224,0.169 0.339,0.281c0.114,0.114 0.526,0.47 1.236,1.069c0.824,0.714 1.483,1.275 1.969,1.689c0.49,0.449 0.769,0.712 0.847,0.789c0.074,0.076 0.11,0.167 0.11,0.281l0,0.166l-0.224,0.339c-1.314,1.911 -1.969,3.938 -1.969,6.074c0,2.213 0.696,4.012 2.083,5.399c0.224,0.188 0.333,0.357 0.333,0.506c-0.037,0.263 -0.188,0.394 -0.448,0.394c-0.074,0 -0.17,-0.018 -0.279,-0.055c-0.224,-0.076 -0.472,-0.112 -0.732,-0.112c-0.673,0 -1.277,0.188 -1.799,0.561c-1.126,0.751 -1.689,1.783 -1.689,3.095c0,1.238 0.526,2.383 1.575,3.431c0.225,0.188 0.339,0.357 0.339,0.505c0,0.077 -0.055,0.17 -0.169,0.281c-0.074,0.076 -0.15,0.114 -0.225,0.114c-0.15,0 -0.319,-0.094 -0.508,-0.281c-2.361,-1.838 -3.694,-3.676 -3.99,-5.514c-0.037,-0.148 -0.056,-0.354 -0.056,-0.618c0,-0.264 0.018,-0.47 0.056,-0.618c0.188,-0.75 0.626,-1.351 1.322,-1.802c0.691,-0.451 1.547,-0.675 2.559,-0.675c0.521,0 1.066,0.057 1.629,0.168l0.114,0l-0.339,-0.28c-0.078,-0.077 -0.169,-0.159 -0.283,-0.254c-0.11,-0.091 -0.261,-0.224 -0.449,-0.394c-0.188,-0.167 -0.339,-0.289 -0.448,-0.364c-0.339,-0.339 -0.865,-0.827 -1.575,-1.462c-1.053,-0.901 -1.671,-1.435 -1.857,-1.604c-0.188,-0.167 -0.279,-0.31 -0.279,-0.421l0,-0.057c0,-0.073 0.091,-0.224 0.279,-0.448c1.276,-1.726 2.004,-3.564 2.197,-5.514c0,-0.261 0,-0.523 0,-0.788c0,-1.986 -0.563,-3.525 -1.689,-4.611c-0.15,-0.113 -0.224,-0.243 -0.224,-0.394c0,-0.148 0.074,-0.261 0.224,-0.336c0.07,-0.039 0.129,-0.057 0.165,-0.057Z" style="fill-rule:nonzero;"/>'),
  "EighthRest": new Symbol("EighthRest", 650, 200, 11, 16, '<path id="_8thRest" d="M3.623,0.25c0.603,0 1.146,0.188 1.63,0.563c0.49,0.375 0.813,0.862 0.959,1.461c0.078,0.188 0.114,0.394 0.114,0.618c0,0.75 -0.243,1.371 -0.733,1.856l-0.055,0.057l0.11,0c0.714,-0.224 1.369,-0.618 1.973,-1.181c0.602,-0.563 1.064,-1.218 1.404,-1.969c0.038,-0.151 0.074,-0.245 0.111,-0.281c0.114,-0.147 0.268,-0.225 0.452,-0.225c0.147,0 0.278,0.056 0.396,0.167c0.109,0.112 0.17,0.244 0.17,0.394c0,0.039 -0.659,2.23 -1.973,6.583c-1.312,4.348 -1.987,6.562 -2.023,6.637c-0.072,0.188 -0.226,0.281 -0.447,0.281c-0.267,0 -0.453,-0.114 -0.563,-0.339l0,-0.112c0,-0.113 0.976,-3.432 2.925,-9.957c-0.036,0 -0.113,0.037 -0.226,0.113c-0.975,0.75 -2.063,1.125 -3.264,1.125c-1.091,0 -2.005,-0.318 -2.757,-0.957c-0.676,-0.524 -1.011,-1.199 -1.011,-2.025c0,-0.675 0.215,-1.285 0.645,-1.829c0.432,-0.542 1.003,-0.852 1.718,-0.927c0.143,-0.032 0.294,-0.053 0.445,-0.053Z" style="fill-rule:nonzero;"/>'),
  "SixteenthRest": new Symbol("SixteenthRest", 350, 200, 13, 25, '<path id="_16thRest" d="M5.839,0.25c0.6,0 1.145,0.197 1.63,0.593c0.49,0.394 0.806,0.892 0.957,1.49c0.073,0.188 0.114,0.394 0.114,0.618c0,0.714 -0.247,1.314 -0.733,1.802l-0.055,0.055l0.165,0c0.714,-0.188 1.369,-0.563 1.973,-1.123c0.596,-0.563 1.049,-1.238 1.346,-2.026l0.114,-0.224c0.114,-0.188 0.261,-0.281 0.448,-0.281c0.15,0 0.275,0.055 0.367,0.167c0.096,0.114 0.142,0.245 0.142,0.394c0,0.039 -0.536,1.886 -1.603,5.542c-1.071,3.658 -2.156,7.322 -3.264,10.998c-1.107,3.677 -1.68,5.532 -1.716,5.569c-0.073,0.188 -0.225,0.281 -0.448,0.281c-0.261,0 -0.449,-0.113 -0.563,-0.339l0,-0.111c0,-0.148 0.975,-3.488 2.925,-10.011l-0.225,0.167c-0.975,0.75 -2.064,1.125 -3.264,1.125c-1.091,0 -2.005,-0.3 -2.756,-0.898c-0.636,-0.527 -0.957,-1.238 -0.957,-2.139c0,-0.563 0.151,-1.087 0.449,-1.575c0.563,-0.788 1.314,-1.185 2.252,-1.185c1.048,0 1.836,0.452 2.361,1.351c0.225,0.414 0.339,0.862 0.339,1.351c0,0.714 -0.225,1.314 -0.677,1.802l-0.055,0.113l0.114,-0.113c0.746,-0.188 1.423,-0.563 2.023,-1.125c0.6,-0.563 1.029,-1.218 1.294,-1.97c0.037,-0.076 0.636,-1.986 1.799,-5.738c-0.037,0 -0.133,0.039 -0.284,0.113c-0.975,0.75 -2.042,1.125 -3.206,1.125c-1.09,0 -2.022,-0.3 -2.811,-0.898c-0.641,-0.563 -0.957,-1.256 -0.957,-2.083c0,-0.675 0.215,-1.284 0.646,-1.827c0.43,-0.544 1.002,-0.854 1.717,-0.929c0.116,-0.04 0.248,-0.061 0.399,-0.061Z" style="fill-rule:nonzero;"/>'),
  "ThirtySecondRest": new Symbol("ThirtySecondRest", 150, 200, 15, 33, '<path id="_32ndRest" d="M8.229,0.25c0.641,0 1.204,0.197 1.689,0.591c0.485,0.394 0.805,0.872 0.957,1.435c0.037,0.224 0.055,0.448 0.055,0.675c0,0.751 -0.243,1.349 -0.729,1.799l-0.06,0.112l0.169,-0.055c0.75,-0.188 1.424,-0.563 2.023,-1.125c0.6,-0.563 1.034,-1.238 1.295,-2.025c0.074,-0.113 0.133,-0.204 0.17,-0.281c0.114,-0.148 0.261,-0.225 0.448,-0.225c0.114,0 0.224,0.057 0.339,0.169c0.115,0.112 0.17,0.245 0.17,0.394c0,0.076 -1.529,5.242 -4.587,15.498c-3.053,10.255 -4.601,15.422 -4.638,15.494c-0.114,0.188 -0.283,0.281 -0.508,0.281c-0.261,0 -0.43,-0.112 -0.508,-0.337c-0.037,-0.076 -0.056,-0.13 -0.056,-0.168c0,-0.112 0.993,-3.432 2.98,-9.957c-0.037,0 -0.11,0.057 -0.224,0.169c-1.012,0.712 -2.12,1.069 -3.319,1.069c-1.048,0 -1.97,-0.3 -2.755,-0.899c-0.636,-0.526 -0.957,-1.221 -0.957,-2.084c0,-0.6 0.15,-1.123 0.453,-1.575c0.559,-0.824 1.327,-1.238 2.302,-1.238c1.053,0 1.84,0.47 2.362,1.408c0.229,0.412 0.339,0.842 0.339,1.293c0,0.751 -0.243,1.351 -0.733,1.799l-0.055,0.114l0.169,-0.057c0.714,-0.188 1.369,-0.563 1.969,-1.124c0.6,-0.563 1.049,-1.221 1.351,-1.97c0,-0.074 0.582,-1.987 1.744,-5.736c-0.037,0 -0.133,0.037 -0.279,0.113c-0.979,0.75 -2.046,1.124 -3.208,1.124c-1.091,0 -2.005,-0.318 -2.756,-0.956c-0.672,-0.527 -1.012,-1.221 -1.012,-2.083c0,-0.563 0.17,-1.069 0.504,-1.517c0.527,-0.827 1.277,-1.237 2.252,-1.237c1.047,0 1.836,0.451 2.361,1.351c0.225,0.412 0.339,0.842 0.339,1.293c0,0.751 -0.224,1.369 -0.677,1.855l-0.11,0.057l0.168,0c0.709,-0.228 1.369,-0.621 1.97,-1.182c0.6,-0.563 1.047,-1.221 1.351,-1.97c0.037,-0.037 0.618,-1.947 1.744,-5.736l-0.229,0.167c-0.975,0.714 -2.061,1.069 -3.26,1.069c-1.09,0 -2.009,-0.3 -2.755,-0.899c-0.641,-0.524 -0.957,-1.218 -0.957,-2.082c0,-0.675 0.207,-1.275 0.618,-1.802c0.412,-0.524 0.975,-0.861 1.684,-1.012c0.155,0.003 0.288,0.003 0.397,0.003Z" style="fill-rule:nonzero;"/>'),
  "SixtyFourthRest": new Symbol("SixtyFourthRest", 100, 200, 18, 42, '<path id="_64thRest" d="M10.869,0.25c0.636,0 1.199,0.188 1.689,0.561c0.485,0.375 0.807,0.865 0.957,1.461c0.037,0.19 0.055,0.394 0.055,0.62c0,0.751 -0.243,1.369 -0.732,1.856l-0.055,0.055l0.169,-0.055c0.709,-0.188 1.369,-0.563 1.969,-1.126c0.6,-0.561 1.048,-1.236 1.351,-2.022c0.037,-0.114 0.074,-0.188 0.114,-0.228c0.147,-0.147 0.297,-0.224 0.449,-0.224c0.147,0 0.279,0.048 0.394,0.14c0.11,0.094 0.169,0.217 0.169,0.366c0,0.076 -1.978,6.722 -5.938,19.939c-3.956,13.22 -5.951,19.867 -5.988,19.942c-0.114,0.147 -0.266,0.225 -0.453,0.225c-0.261,0 -0.449,-0.095 -0.563,-0.282l-0.055,-0.112c0,-0.147 0.993,-3.488 2.979,-10.014c-0.037,0 -0.11,0.057 -0.224,0.169c-0.975,0.75 -2.06,1.125 -3.259,1.125c-1.09,0 -2.028,-0.32 -2.816,-0.957c-0.636,-0.526 -0.957,-1.22 -0.957,-2.083c0,-0.561 0.169,-1.088 0.508,-1.575c0.527,-0.788 1.273,-1.181 2.248,-1.181c1.053,0 1.84,0.451 2.361,1.351c0.229,0.375 0.339,0.808 0.339,1.293c0,0.751 -0.224,1.371 -0.673,1.855l-0.114,0.057l0.168,-0.057c0.714,-0.188 1.369,-0.563 1.97,-1.124c0.6,-0.563 1.048,-1.218 1.351,-1.969c0,-0.076 0.289,-1.051 0.875,-2.925c0.576,-1.875 0.869,-2.832 0.869,-2.871l-0.225,0.169c-1.012,0.751 -2.101,1.124 -3.264,1.124c-1.091,0 -2.004,-0.297 -2.755,-0.899c-0.636,-0.524 -0.957,-1.236 -0.957,-2.138c0,-0.561 0.151,-1.087 0.449,-1.575c0.563,-0.788 1.314,-1.181 2.251,-1.181c1.049,0 1.836,0.451 2.362,1.351c0.224,0.412 0.339,0.863 0.339,1.351c0,0.711 -0.225,1.314 -0.678,1.799l-0.11,0.112l0.169,-0.112c0.746,-0.188 1.422,-0.563 2.022,-1.124c0.6,-0.563 1.03,-1.218 1.295,-1.969c0.037,-0.077 0.636,-1.989 1.799,-5.738c-0.037,0 -0.133,0.037 -0.284,0.112c-0.975,0.75 -2.06,1.124 -3.259,1.124c-1.053,0 -1.969,-0.297 -2.756,-0.899c-0.641,-0.561 -0.957,-1.275 -0.957,-2.138c0,-0.561 0.147,-1.069 0.449,-1.517c0.563,-0.824 1.332,-1.238 2.307,-1.238c1.048,0 1.836,0.452 2.361,1.351c0.225,0.412 0.339,0.863 0.339,1.351c0,0.711 -0.247,1.313 -0.733,1.798l-0.114,0.057l0.225,0c0.714,-0.188 1.369,-0.563 1.97,-1.125c0.6,-0.561 1.053,-1.218 1.351,-1.97c0,-0.076 0.563,-2.005 1.689,-5.793l-0.224,0.169c-0.975,0.749 -2.046,1.124 -3.208,1.124c-1.085,0 -2.005,-0.318 -2.755,-0.957c-0.673,-0.524 -1.012,-1.199 -1.012,-2.022c0,-0.675 0.215,-1.285 0.646,-1.829c0.435,-0.544 1.007,-0.872 1.715,-0.983c0.117,0.005 0.245,0.005 0.396,0.005Z" style="fill-rule:nonzero;"/>'),
  "EighthFlagUp": new Symbol("EighthFlagUp", 400, 200, 10, 27, '<path id="_8thFlagAbove" d="M0.5,0.25l0.114,0c0.262,0 0.447,0.206 0.563,0.618c0.751,2.852 2.249,5.31 4.501,7.37c1.272,1.199 2.237,2.568 2.897,4.106c0.654,1.537 0.984,3.168 0.984,4.894c0,2.55 -0.696,4.971 -2.083,7.255c-0.563,0.975 -1.109,1.726 -1.631,2.251c-0.114,0.151 -0.266,0.225 -0.453,0.225c-0.146,0 -0.27,-0.055 -0.361,-0.169c-0.096,-0.11 -0.143,-0.243 -0.143,-0.394c0,-0.151 0.092,-0.357 0.278,-0.618c1.241,-1.465 2.1,-3.058 2.59,-4.784c0.297,-1.048 0.448,-2.097 0.448,-3.149c0,-2.623 -0.806,-4.815 -2.417,-6.577c-1.09,-1.126 -2.833,-2.101 -5.231,-2.925c-0.042,0 -0.06,-1.351 -0.06,-4.051l0.004,-4.052Z" style="fill-rule:nonzero;"/>'),
  "SixteenthFlagUp": new Symbol("SixteenthFlagUp", 600, 200, 10, 31, '<path id="_16thFlagAbove" d="M0.751,0.25l0.11,0c0.224,0 0.395,0.133 0.508,0.394c0.374,0.938 1.012,1.969 1.913,3.095c0.896,1.126 1.984,2.307 3.259,3.543c2.175,2.065 3.266,4.591 3.266,7.595c0,1.272 -0.226,2.513 -0.673,3.712l-0.06,0.279l0.114,0.284c0.41,1.123 0.618,2.327 0.618,3.598c0,2.737 -0.922,5.214 -2.755,7.425c-0.226,0.266 -0.433,0.394 -0.618,0.394c-0.375,0 -0.563,-0.17 -0.563,-0.505c0,-0.151 0.074,-0.32 0.225,-0.508c1.611,-1.877 2.417,-4.015 2.417,-6.414c0,-0.563 -0.036,-1.125 -0.108,-1.689c-0.037,-0.11 -0.057,-0.202 -0.057,-0.279c-0.041,-0.074 -0.078,-0.188 -0.112,-0.339c-0.49,-1.908 -1.521,-3.41 -3.096,-4.5c-0.375,-0.261 -0.947,-0.485 -1.717,-0.672c-0.769,-0.187 -1.506,-0.284 -2.221,-0.284c-0.188,0 -0.319,0 -0.396,0l-0.056,0l0,-3.653l0,-3.658l0.11,0c0.225,0 0.395,0.151 0.508,0.449c0.788,1.876 2.514,4.087 5.173,6.638c0.714,0.676 1.277,1.369 1.688,2.083c0.035,0 0.063,0 0.081,0c0.019,0 0.031,-0.055 0.031,-0.168c0.11,-0.563 0.164,-1.146 0.164,-1.744c0,-0.563 -0.035,-1.125 -0.108,-1.689c-0.036,-0.146 -0.056,-0.243 -0.056,-0.279c-0.042,-0.073 -0.078,-0.188 -0.113,-0.339c-0.489,-1.914 -1.521,-3.41 -3.096,-4.5c-0.375,-0.261 -0.947,-0.485 -1.717,-0.673c-0.77,-0.188 -1.507,-0.284 -2.221,-0.284c-0.188,0 -0.319,0 -0.395,0l-0.057,-0.055l0,-3.658l0,-3.597l0.01,0l0,-0.002Z" style="fill-rule:nonzero;"/>'),
  "ThirtySecondFlagUp": new Symbol("ThirtySecondFlagUp", 700, 200, 10, 40, '<path id="_32ndFlagAbove" d="M0.875,0.25l0.114,0c0.182,0 0.372,0.188 0.56,0.563c0.863,1.684 2.385,3.543 4.561,5.566c2.174,2.022 3.409,4.668 3.713,7.933l0,2.136c-0.08,0.938 -0.226,1.799 -0.449,2.586l-0.059,0.339l0.059,0.165c0.226,0.751 0.372,1.616 0.449,2.591l0,2.137c-0.08,0.896 -0.226,1.781 -0.449,2.642l-0.059,0.339l0.059,0.114c0.226,0.788 0.372,1.667 0.449,2.642l0,2.136c-0.226,2.627 -1.035,5.062 -2.424,7.31c-0.188,0.303 -0.396,0.453 -0.617,0.453c-0.11,0 -0.243,-0.059 -0.396,-0.168c-0.107,-0.114 -0.168,-0.225 -0.168,-0.339c0,-0.147 0.078,-0.339 0.229,-0.563c1.424,-2.284 2.132,-4.67 2.132,-7.142c0,-0.563 -0.036,-1.108 -0.11,-1.635c-0.225,-1.422 -0.695,-2.705 -1.404,-3.85c-0.715,-1.145 -1.6,-2.001 -2.646,-2.559c-0.602,-0.339 -1.576,-0.508 -2.925,-0.508l-0.056,0l-0.561,0l0,-3.603l0,-3.598l0.114,0c0.182,0 0.372,0.168 0.56,0.508c0.863,1.685 2.387,3.563 4.561,5.621c0.933,0.865 1.685,1.822 2.248,2.87l0.113,0.225l0.055,-0.225c0.038,-0.339 0.055,-0.709 0.055,-1.125c0,-1.763 -0.385,-3.374 -1.149,-4.834c-0.769,-1.465 -1.773,-2.513 -3.011,-3.154c-0.602,-0.334 -1.575,-0.504 -2.926,-0.504l-0.055,0l-0.563,0l0,-3.603l0,-3.598l0.113,0c0.182,0 0.371,0.168 0.56,0.508c0.268,0.522 0.622,1.094 1.07,1.711c0.447,0.623 0.976,1.258 1.574,1.914c0.602,0.66 1.235,1.323 1.914,1.996c0.933,0.865 1.684,1.804 2.247,2.815l0.113,0.279l0.057,-0.224c0.036,-0.375 0.055,-0.75 0.055,-1.125c0,-1.764 -0.386,-3.375 -1.151,-4.834c-0.767,-1.465 -1.772,-2.531 -3.011,-3.208c-0.602,-0.297 -1.575,-0.448 -2.925,-0.448l-0.055,0l-0.563,0l0,-3.658l0,-3.594l-0.002,0Z" style="fill-rule:nonzero;"/>'),
  "SixtyFourthFlagUp": new Symbol("SixtyFourthFlagUp", 750, 200, 10, 45, '<path id="_64thFlagAbove" d="M0.938,0.25l0.114,0c0.109,0 0.206,0.06 0.277,0.169c0.078,0.037 0.149,0.188 0.224,0.449c0.603,1.767 1.822,3.434 3.659,5.008c1.012,0.824 1.857,1.762 2.53,2.815c1.387,2.06 2.083,4.331 2.083,6.802c0,1.241 -0.17,2.458 -0.508,3.658l-0.11,0.225l0.11,0.339c0.34,1.199 0.508,2.417 0.508,3.658c0,1.236 -0.17,2.436 -0.508,3.598l-0.11,0.284l0.11,0.334c0.34,1.204 0.508,2.403 0.508,3.602c0,1.236 -0.17,2.436 -0.508,3.598l-0.11,0.284l0.11,0.334c0.34,1.204 0.508,2.422 0.508,3.658c0,1.762 -0.357,3.434 -1.07,5.008c-0.147,0.334 -0.356,0.503 -0.618,0.503c-0.073,0 -0.149,-0.018 -0.226,-0.055c-0.188,-0.114 -0.276,-0.261 -0.276,-0.447c0,-0.15 0.036,-0.319 0.109,-0.507c0.678,-1.387 1.012,-2.829 1.012,-4.331c0,-1.689 -0.401,-3.255 -1.208,-4.697c-0.806,-1.443 -1.938,-2.596 -3.4,-3.461c-0.603,-0.339 -1.108,-0.504 -1.521,-0.504c-0.413,0 -0.864,0.128 -1.352,0.394c-0.073,0.037 -0.149,0.055 -0.224,0.055l-0.115,0l0,-3.603l0,-3.654l0.113,0c0.11,0 0.206,0.055 0.277,0.168c0.078,0.074 0.15,0.224 0.225,0.449c0.602,1.762 1.821,3.429 3.657,5.008c1.352,1.122 2.397,2.38 3.149,3.767c0.078,0.151 0.133,0.225 0.169,0.225l0.114,-0.901c0.071,-0.333 0.109,-0.708 0.109,-1.122c0,-1.652 -0.411,-3.208 -1.235,-4.668c-0.824,-1.465 -1.95,-2.627 -3.372,-3.488c-0.603,-0.339 -1.107,-0.508 -1.521,-0.508c-0.412,0 -0.863,0.133 -1.352,0.394c-0.072,0.042 -0.148,0.06 -0.223,0.06l-0.113,0.056l0,-3.659l0,-3.598l0.113,0c0.109,0 0.206,0.037 0.276,0.11c0.078,0.078 0.15,0.224 0.225,0.453c0.601,1.763 1.822,3.429 3.658,5.003c1.352,1.125 2.397,2.403 3.147,3.826c0.079,0.151 0.134,0.225 0.17,0.225c0,-0.037 0.038,-0.339 0.114,-0.902c0.072,-0.371 0.109,-0.746 0.109,-1.122c0,-1.689 -0.411,-3.255 -1.235,-4.697c-0.824,-1.447 -1.949,-2.6 -3.374,-3.461c-0.601,-0.339 -1.105,-0.508 -1.52,-0.508c-0.411,0 -0.863,0.133 -1.351,0.394c-0.072,0.037 -0.148,0.059 -0.225,0.059l-0.112,0.055l0,-3.658l0,-3.598l0.112,0c0.11,0 0.207,0.037 0.277,0.11c0.078,0.078 0.15,0.224 0.226,0.453c0.602,1.762 1.821,3.41 3.657,4.948c1.352,1.163 2.397,2.435 3.148,3.823c0.077,0.151 0.134,0.229 0.17,0.229l0.113,-0.902c0.073,-0.339 0.109,-0.714 0.109,-1.125c0,-1.647 -0.409,-3.205 -1.234,-4.669c-0.823,-1.46 -1.949,-2.604 -3.373,-3.429c-0.601,-0.375 -1.105,-0.563 -1.52,-0.563c-0.411,0 -0.863,0.15 -1.351,0.448c-0.073,0 -0.149,0.018 -0.226,0.056l-0.115,0l0,-3.653l0,-3.603l0.017,0l0,-0.001Z" style="fill-rule:nonzero;"/>'),
  "EighthFlagDown": new Symbol("EighthFlagDown", 100, 300, 11, 28, '<path id="_8thFlagBelow" d="M6.991,0.375c0.183,0 0.394,0.17 0.618,0.504c1.909,2.89 2.866,5.946 2.866,9.17c0,0.525 -0.037,1.033 -0.11,1.52c-0.151,1.465 -0.537,2.844 -1.155,4.135c-0.618,1.295 -1.396,2.39 -2.333,3.291c-1.538,1.387 -2.811,2.718 -3.827,3.996c-1.123,1.422 -1.908,2.656 -2.361,3.713c-0.11,0.261 -0.279,0.394 -0.504,0.394l-0.06,0l0,-4.053l0,-4.053l0.114,0c0.9,0 1.913,-0.169 3.035,-0.509c1.726,-0.559 3.122,-1.702 4.193,-3.429c1.065,-1.727 1.602,-3.677 1.602,-5.851c0,-0.41 -0.018,-0.806 -0.055,-1.183c-0.224,-2.36 -1.012,-4.538 -2.361,-6.523c-0.151,-0.229 -0.225,-0.398 -0.225,-0.507c0,-0.261 0.11,-0.448 0.333,-0.563c0.08,-0.034 0.152,-0.052 0.23,-0.052Z" style="fill-rule:nonzero;"/>'),
  "SixteenthFlagDown": new Symbol("SixteenthFlagDown", 50, 300, 11, 28, '<path id="_16thFlagBelow" d="M8.783,0.375c0.224,0 0.412,0.17 0.563,0.504c0.563,1.162 0.842,2.439 0.842,3.826c0,1.125 -0.207,2.248 -0.618,3.374l-0.114,0.224l0.114,0.282c0.412,1.05 0.618,2.14 0.618,3.261c0,2.59 -0.92,4.916 -2.756,6.978c-0.375,0.411 -0.842,0.864 -1.404,1.353c-1.164,0.938 -2.233,2.005 -3.208,3.207c-0.975,1.199 -1.721,2.326 -2.248,3.373c-0.114,0.225 -0.279,0.34 -0.508,0.34l0,-3.547l0,-3.484l0.229,-0.059c2.061,-0.299 3.635,-0.77 4.725,-1.406c1.236,-0.75 2.221,-1.743 2.952,-2.979c0.733,-1.24 1.094,-2.551 1.094,-3.938c0,-0.602 -0.073,-1.182 -0.224,-1.741l-0.055,-0.171l-0.114,0.224c-0.636,1.05 -1.515,2.046 -2.641,2.979c-1.163,0.975 -2.233,2.055 -3.208,3.234c-0.975,1.184 -1.721,2.317 -2.248,3.402c-0.114,0.188 -0.279,0.282 -0.508,0.282l0,-3.488l0,-3.543l0.229,0c2.06,-0.303 3.634,-0.786 4.724,-1.465c1.272,-0.75 2.266,-1.743 2.979,-2.979c0.709,-1.236 1.066,-2.55 1.066,-3.938c0,-1.05 -0.225,-2.045 -0.673,-2.979c-0.114,-0.266 -0.168,-0.436 -0.168,-0.507c0,-0.263 0.114,-0.448 0.339,-0.563c0.07,-0.038 0.144,-0.056 0.221,-0.056Z" style="fill-rule:nonzero;"/>'),
  "ThirtySecondFlagDown": new Symbol("ThirtySecondFlagDown", 550, 200, 11, 36, '<path id="_32ndFlagBelow" d="M8.785,0.25c0.188,0 0.394,0.169 0.618,0.508c0.524,0.934 0.919,1.932 1.181,2.979c0.266,0.861 0.395,1.799 0.395,2.811c0,1.163 -0.188,2.289 -0.559,3.378l-0.06,0.165l0.113,0.283c0.036,0.151 0.073,0.32 0.109,0.504c0.267,0.901 0.396,1.84 0.396,2.816c0,1.163 -0.188,2.289 -0.56,3.374l-0.061,0.168l0.113,0.279c0.037,0.15 0.073,0.339 0.109,0.563c0.267,0.901 0.396,1.822 0.396,2.755c0,1.615 -0.324,3.154 -0.982,4.614c-0.656,1.465 -1.605,2.737 -2.84,3.827c-0.188,0.147 -0.488,0.394 -0.9,0.728c-2.36,1.913 -4.028,3.772 -5.003,5.571c-0.15,0.261 -0.319,0.394 -0.507,0.394l-0.059,0l0,-3.488l0,-3.434l0.111,0c2.138,-0.298 3.938,-0.935 5.4,-1.909c1.126,-0.714 1.997,-1.68 2.615,-2.897c0.621,-1.218 0.928,-2.522 0.928,-3.91c0,-0.375 -0.019,-0.75 -0.055,-1.125l-0.055,-0.224l-0.06,0.055c-0.673,1.199 -1.479,2.233 -2.417,3.094c-0.188,0.151 -0.49,0.394 -0.901,0.733c-2.36,1.912 -4.028,3.748 -5.004,5.512c-0.148,0.261 -0.318,0.394 -0.506,0.394l-0.06,0l0,-3.435l0,-3.429l0.11,-0.055c2.138,-0.267 3.938,-0.884 5.401,-1.858c1.126,-0.75 1.997,-1.726 2.615,-2.925c0.621,-1.199 0.928,-2.495 0.928,-3.882c0,-0.375 -0.019,-0.75 -0.056,-1.122l-0.055,-0.229l-0.06,0.06c-0.672,1.199 -1.479,2.211 -2.417,3.035c-0.188,0.188 -0.49,0.448 -0.9,0.788c-2.36,1.876 -4.028,3.712 -5.004,5.513c-0.148,0.266 -0.319,0.394 -0.507,0.394l-0.059,0l0,-3.48l0,-3.378l0.109,-0.055c2.479,-0.339 4.428,-1.066 5.852,-2.193c0.977,-0.788 1.734,-1.744 2.278,-2.87c0.544,-1.126 0.814,-2.326 0.814,-3.598c0,-1.651 -0.448,-3.208 -1.352,-4.669c-0.149,-0.261 -0.226,-0.453 -0.226,-0.563c0,-0.224 0.114,-0.394 0.34,-0.508c0.124,-0.041 0.221,-0.059 0.294,-0.059Z" style="fill-rule:nonzero;"/>'),
  "SixtyFourthFlagDown": new Symbol("SixtyFourthFlagDown", 500, 200, 11, 41, '<path id="_64thFlagBelow" d="M8.221,0.25c0.146,0 0.261,0.037 0.334,0.11c1.611,1.277 2.424,2.984 2.424,5.118c0,1.164 -0.282,2.289 -0.848,3.378l-0.165,0.279l0.055,0.055c0.636,0.979 0.958,2.101 0.958,3.378c0,1.236 -0.282,2.38 -0.848,3.429l-0.165,0.283l0.055,0.11c0.636,0.975 0.958,2.101 0.958,3.378c0,1.199 -0.282,2.344 -0.848,3.429l-0.165,0.285l0.055,0.055c0.636,0.975 0.958,2.119 0.958,3.429c0,1.017 -0.208,1.991 -0.617,2.925c-0.604,1.393 -1.6,2.573 -2.984,3.548c-1.497,1.012 -2.812,2.12 -3.937,3.319c-1.126,1.199 -1.876,2.303 -2.248,3.32c-0.078,0.182 -0.229,0.297 -0.453,0.333l-0.11,0l0,-3.374l0,-3.374l0.11,0c0.526,0 1.199,-0.064 2.028,-0.197c0.826,-0.133 1.48,-0.293 1.97,-0.48c1.46,-0.485 2.642,-1.327 3.542,-2.531c0.899,-1.199 1.352,-2.491 1.352,-3.882c0,-0.595 -0.097,-1.158 -0.282,-1.685l-0.11,-0.284l-0.17,0.17c-0.41,0.525 -0.977,1.033 -1.688,1.52c-1.497,0.975 -2.812,2.072 -3.937,3.291c-1.125,1.218 -1.875,2.316 -2.249,3.292c-0.078,0.224 -0.229,0.357 -0.453,0.394l-0.11,0l0,-3.378l0,-3.429l0.11,0c0.527,0 1.199,-0.064 2.03,-0.197c0.824,-0.133 1.479,-0.271 1.969,-0.421c1.46,-0.49 2.641,-1.341 3.543,-2.559c0.899,-1.217 1.351,-2.504 1.351,-3.854c0,-0.6 -0.097,-1.181 -0.282,-1.744l-0.109,-0.225l-0.17,0.11c-0.411,0.526 -0.977,1.053 -1.688,1.575c-1.497,0.979 -2.812,2.073 -3.937,3.291c-1.125,1.222 -1.876,2.316 -2.249,3.292c-0.078,0.224 -0.229,0.339 -0.453,0.339l-0.11,0l0,-3.374l0,-3.378l0.11,0c0.527,0 1.199,-0.064 2.028,-0.197c0.826,-0.128 1.479,-0.271 1.969,-0.421c1.46,-0.485 2.642,-1.342 3.543,-2.56c0.899,-1.218 1.351,-2.504 1.351,-3.854c0,-0.6 -0.097,-1.182 -0.28,-1.744l-0.112,-0.279l-0.169,0.169c-0.411,0.527 -0.977,1.048 -1.688,1.575c-1.497,0.975 -2.812,2.073 -3.937,3.292c-1.125,1.218 -1.875,2.316 -2.248,3.291c-0.078,0.188 -0.229,0.297 -0.453,0.334l-0.11,0.061l0,-3.435l0,-3.374l0.11,0c1.09,0 2.252,-0.169 3.488,-0.508c1.351,-0.375 2.495,-1.021 3.434,-1.94c0.936,-0.916 1.538,-1.996 1.799,-3.232c0.114,-0.453 0.171,-0.901 0.171,-1.351c0,-1.612 -0.544,-2.888 -1.636,-3.827c-0.146,-0.11 -0.242,-0.224 -0.276,-0.339c-0.038,-0.074 -0.055,-0.128 -0.055,-0.165c0,-0.266 0.109,-0.435 0.335,-0.508c0.056,-0.039 0.131,-0.057 0.209,-0.057Z" style="fill-rule:nonzero;"/>'),
  "DoubleFlat": new Symbol("DoubleFlat", 350, 300, 14, 23, '<path id="DoubleFlat" d="M0.944,0.375c0.261,0 0.432,0.109 0.505,0.334c0.037,0.078 0.055,2.14 0.055,6.189l0,6.02l0.06,0c1.012,-0.861 2.06,-1.295 3.149,-1.295c0.672,0 1.254,0.171 1.744,0.508l0.11,0.109l0,-5.734l0,-5.735l0.06,-0.062c0.073,-0.225 0.243,-0.334 0.504,-0.334c0.224,0 0.394,0.109 0.508,0.334l0,12.209l0.055,0c1.012,-0.861 2.023,-1.295 3.041,-1.295c0.412,0 0.824,0.059 1.236,0.171c0.563,0.225 1.001,0.58 1.323,1.069c0.315,0.486 0.476,1.065 0.476,1.744c0,0.746 -0.207,1.479 -0.618,2.191c-0.261,0.527 -0.627,1.039 -1.099,1.547c-0.467,0.505 -1.228,1.227 -2.275,2.164c-0.9,0.787 -1.575,1.406 -2.028,1.854c-0.261,0.264 -0.476,0.396 -0.646,0.396c-0.17,0 -0.289,-0.055 -0.366,-0.172l-0.056,0c-0.078,-0.073 -0.114,-0.635 -0.114,-1.686l0,-0.955l0,-2.422l-0.11,0.169c-0.526,0.679 -1.465,1.614 -2.815,2.815c-0.861,0.787 -1.538,1.404 -2.022,1.854c-0.302,0.262 -0.526,0.395 -0.673,0.395c-0.266,0 -0.436,-0.111 -0.508,-0.336l0,-0.113l0,-10.744l0,-10.742l0,-0.113c0.074,-0.11 0.188,-0.207 0.339,-0.279c0.034,-0.037 0.089,-0.055 0.165,-0.055Zm2.588,12.938c-0.037,0 -0.096,0.019 -0.17,0.055c-0.714,0.113 -1.277,0.489 -1.689,1.126l-0.169,0.225l0,6.131l0.114,-0.109c0.938,-1.015 1.697,-2.083 2.279,-3.209c0.582,-1.123 0.87,-2.078 0.87,-2.865c0,-0.411 -0.064,-0.715 -0.197,-0.902c-0.133,-0.187 -0.366,-0.313 -0.7,-0.394c-0.114,-0.041 -0.228,-0.058 -0.338,-0.058Zm6.13,0c-0.073,0 -0.133,0.019 -0.168,0.055c-0.709,0.113 -1.291,0.489 -1.744,1.126l-0.11,0.225l0,6.131l0.11,-0.109c0.938,-1.015 1.708,-2.083 2.307,-3.209c0.6,-1.123 0.901,-2.078 0.901,-2.865c0,-0.411 -0.078,-0.715 -0.224,-0.902c-0.151,-0.188 -0.375,-0.313 -0.677,-0.394c-0.153,-0.041 -0.281,-0.058 -0.395,-0.058Z" style="fill-rule:nonzero;"/>'),
  "Flat": new Symbol("Flat", 750, 400, 10, 29, '<path id="Flat" d="M1.502,0.5c0.261,0 0.431,0.11 0.507,0.334l0,12.209l0.056,0c1.012,-0.861 2.039,-1.295 3.095,-1.295c0.41,0 0.806,0.059 1.182,0.171c0.563,0.225 1.003,0.58 1.318,1.071c0.319,0.483 0.479,1.063 0.479,1.742c0,0.746 -0.188,1.479 -0.563,2.191c-0.304,0.527 -0.696,1.039 -1.183,1.547c-0.488,0.504 -1.234,1.227 -2.254,2.164c-0.86,0.788 -1.538,1.406 -2.022,1.854c-0.304,0.263 -0.504,0.396 -0.618,0.396c-0.261,0 -0.431,-0.11 -0.506,-0.336l-0.057,-0.113l0,-10.743l0,-10.745l0.057,-0.113c0.037,-0.109 0.133,-0.207 0.283,-0.277c0.074,-0.039 0.147,-0.057 0.226,-0.057Zm2.53,12.938c-0.037,0 -0.098,0.019 -0.171,0.055c-0.714,0.114 -1.277,0.489 -1.688,1.126l-0.165,0.225l0,6.129l0.11,-0.107c0.976,-1.015 1.742,-2.083 2.307,-3.209c0.563,-1.123 0.842,-2.078 0.842,-2.865c0,-0.412 -0.063,-0.715 -0.195,-0.902c-0.133,-0.187 -0.365,-0.313 -0.699,-0.394c-0.117,-0.041 -0.229,-0.058 -0.341,-0.058Z" style="fill-rule:nonzero;"/>'),
  "Accent": new Symbol("Accent", 450, 0, 17, 10, '<path id="Accent" d="M1.237,0.781c0.113,0 0.244,0.018 0.394,0.056c0.411,0.114 2.345,0.636 5.794,1.575c1.801,0.449 3.358,0.865 4.668,1.236c1.316,0.375 2.101,0.582 2.362,0.624c1.052,0.261 1.595,0.431 1.632,0.504c0.224,0.151 0.34,0.339 0.34,0.563c0,0.224 -0.114,0.412 -0.34,0.563c0,0.038 -2.888,0.824 -8.663,2.362c-3.449,0.938 -5.383,1.46 -5.794,1.575c-0.15,0.037 -0.28,0.055 -0.394,0.055c-0.114,0 -0.245,-0.037 -0.394,-0.11c-0.188,-0.114 -0.281,-0.293 -0.281,-0.536c0,-0.243 0.113,-0.439 0.34,-0.59c0.038,-0.037 1.724,-0.508 5.062,-1.405c0.34,-0.114 0.958,-0.283 1.856,-0.508c0.902,-0.225 1.502,-0.375 1.803,-0.449c0.374,-0.151 0.881,-0.311 1.517,-0.479c0.637,-0.165 1.014,-0.251 1.124,-0.251l0.789,-0.224l-0.789,-0.225c-0.112,0 -0.487,-0.086 -1.124,-0.25c-0.636,-0.169 -1.146,-0.33 -1.517,-0.48c-0.3,-0.073 -0.898,-0.224 -1.803,-0.448c-0.898,-0.224 -1.516,-0.394 -1.856,-0.508c-3.298,-0.896 -4.968,-1.351 -5.005,-1.351c-0.265,-0.146 -0.396,-0.354 -0.396,-0.619c0,-0.339 0.169,-0.563 0.505,-0.673c0.037,-0.007 0.096,-0.007 0.17,-0.007Z" style="fill-rule:nonzero;"/>'),
  "F": new Symbol("F", 0, 400, 20, 23, '<path id="F" d="M16.083,0.5l0.11,0c0.261,0 0.472,0.018 0.618,0.055c0.114,0.039 0.247,0.058 0.394,0.058c0.527,0.038 1.043,0.233 1.547,0.592c0.508,0.355 0.815,0.761 0.929,1.208c0,0.148 0,0.28 0,0.395c0,0.487 -0.179,0.921 -0.536,1.293c-0.357,0.375 -0.778,0.563 -1.264,0.563c-0.266,0 -0.49,-0.056 -0.677,-0.17c-0.636,-0.299 -0.953,-0.788 -0.953,-1.46c0,-0.526 0.183,-0.959 0.56,-1.295l0.06,0c-0.078,0 -0.266,0.094 -0.563,0.279c-1.351,0.938 -2.403,2.476 -3.149,4.609l-0.06,0.281l0.957,0c0.714,0 1.125,0.039 1.24,0.112c0.188,0.149 0.279,0.356 0.279,0.62c0,0.225 -0.091,0.411 -0.279,0.563c-0.114,0.075 -0.525,0.112 -1.24,0.112l-0.169,0l-1.181,0l0,0.168c-0.751,3.825 -2.101,7.182 -4.047,10.068c-1.314,1.949 -2.888,3.096 -4.729,3.434c-0.188,0.037 -0.375,0.055 -0.559,0.055c-0.604,0 -1.117,-0.147 -1.547,-0.447c-0.435,-0.303 -0.741,-0.715 -0.929,-1.236c-0.078,-0.268 -0.114,-0.528 -0.114,-0.789c0,-0.527 0.169,-0.968 0.508,-1.323c0.338,-0.356 0.751,-0.533 1.236,-0.533c0.188,0 0.375,0.017 0.563,0.054c0.412,0.114 0.733,0.33 0.957,0.646c0.224,0.316 0.339,0.668 0.339,1.043c0,0.601 -0.266,1.049 -0.788,1.351l-0.114,0.056c0.037,0 0.091,-0.02 0.169,-0.056c0.901,-0.265 1.647,-0.958 2.252,-2.083c0.559,-1.123 1.422,-4.143 2.586,-9.057l0.279,-1.352l-1.066,0c-0.829,0 -1.277,-0.036 -1.351,-0.111c-0.266,-0.114 -0.394,-0.301 -0.394,-0.564c0,-0.263 0.128,-0.471 0.394,-0.62c0.073,-0.072 0.563,-0.111 1.46,-0.111l1.351,0l0.114,-0.28c0.6,-1.725 1.52,-3.142 2.756,-4.246c1.236,-1.109 2.588,-1.735 4.051,-1.882Z" style="fill-rule:nonzero;"/>'),
  "FF": new Symbol("FF", 900, 400, 27, 23, '<path id="FF" d="M15.428,0.494l0.11,0c0.261,0 0.472,0.019 0.618,0.055c0.113,0.04 0.247,0.058 0.394,0.058c0.527,0.037 1.045,0.233 1.547,0.592c0.508,0.354 0.815,0.761 0.93,1.208c0,0.149 0,0.281 0,0.396c0,0.487 -0.18,0.92 -0.536,1.293c-0.356,0.374 -0.777,0.563 -1.264,0.563c-0.268,0 -0.489,-0.055 -0.678,-0.17c-0.636,-0.299 -0.953,-0.787 -0.953,-1.459c0,-0.527 0.184,-0.959 0.561,-1.297l0.061,0c-0.08,0 -0.268,0.094 -0.563,0.28c-1.352,0.938 -2.401,2.476 -3.147,4.61l-0.06,0.281l0.958,0c0.714,0 1.124,0.039 1.238,0.111c0.188,0.149 0.278,0.356 0.278,0.619c0,0.227 -0.091,0.412 -0.278,0.564c-0.113,0.076 -0.524,0.112 -1.238,0.112l-0.172,0l-1.183,0l0,0.167c-0.751,3.826 -2.1,7.184 -4.047,10.068c-1.315,1.95 -2.888,3.096 -4.73,3.436c-0.187,0.036 -0.374,0.055 -0.559,0.055c-0.603,0 -1.118,-0.148 -1.546,-0.448c-0.438,-0.302 -0.74,-0.714 -0.928,-1.238c-0.078,-0.264 -0.114,-0.525 -0.114,-0.787c0,-0.526 0.17,-0.967 0.508,-1.321c0.338,-0.357 0.751,-0.534 1.235,-0.534c0.188,0 0.375,0.017 0.563,0.054c0.412,0.114 0.733,0.33 0.958,0.647c0.226,0.315 0.34,0.667 0.34,1.041c0,0.602 -0.266,1.049 -0.788,1.351l-0.113,0.057c0.037,0 0.092,-0.02 0.17,-0.057c0.9,-0.264 1.646,-0.959 2.253,-2.083c0.56,-1.123 1.423,-4.143 2.588,-9.057l0.279,-1.351l-1.068,0c-0.83,0 -1.277,-0.035 -1.352,-0.112c-0.267,-0.114 -0.396,-0.3 -0.396,-0.563c0,-0.262 0.129,-0.47 0.396,-0.621c0.074,-0.071 0.563,-0.11 1.461,-0.11l1.351,0l0.114,-0.28c0.602,-1.725 1.519,-3.142 2.755,-4.247c1.23,-1.108 2.582,-1.734 4.047,-1.883Zm7.637,0l0.109,0c0.261,0 0.473,0.019 0.618,0.055c0.113,0.04 0.245,0.058 0.394,0.058c0.526,0.037 1.046,0.233 1.548,0.592c0.508,0.354 0.813,0.761 0.929,1.208c0,0.149 0,0.281 0,0.396c0,0.487 -0.18,0.92 -0.537,1.293c-0.357,0.374 -0.776,0.563 -1.262,0.563c-0.27,0 -0.49,-0.055 -0.679,-0.17c-0.636,-0.299 -0.951,-0.787 -0.951,-1.459c0,-0.527 0.181,-0.959 0.559,-1.297l0.061,0c-0.081,0 -0.269,0.094 -0.564,0.28c-1.352,0.938 -2.403,2.476 -3.149,4.61l-0.058,0.281l0.958,0c0.713,0 1.125,0.039 1.24,0.111c0.188,0.149 0.277,0.356 0.277,0.619c0,0.227 -0.091,0.412 -0.277,0.564c-0.113,0.076 -0.526,0.112 -1.24,0.112l-0.17,0l-1.183,0l0,0.167c-0.752,3.826 -2.101,7.184 -4.05,10.068c-1.313,1.95 -2.887,3.096 -4.729,3.436c-0.188,0.036 -0.375,0.055 -0.561,0.055c-0.604,0 -1.116,-0.148 -1.547,-0.448c-0.436,-0.302 -0.74,-0.714 -0.928,-1.238c-0.078,-0.264 -0.114,-0.525 -0.114,-0.787c0,-0.526 0.169,-0.967 0.507,-1.321c0.339,-0.357 0.751,-0.534 1.236,-0.534c0.188,0 0.374,0.017 0.563,0.054c0.41,0.114 0.732,0.33 0.957,0.647c0.226,0.315 0.34,0.667 0.34,1.041c0,0.602 -0.267,1.049 -0.787,1.351l-0.114,0.057c0.037,0 0.092,-0.02 0.171,-0.057c0.899,-0.264 1.646,-0.959 2.253,-2.083c0.56,-1.123 1.423,-4.143 2.588,-9.057l0.276,-1.351l-1.066,0c-0.831,0 -1.276,-0.035 -1.352,-0.112c-0.267,-0.114 -0.396,-0.3 -0.396,-0.563c0,-0.262 0.129,-0.47 0.396,-0.621c0.074,-0.071 0.563,-0.11 1.461,-0.11l1.351,0l0.114,-0.28c0.602,-1.725 1.521,-3.142 2.754,-4.247c1.239,-1.108 2.591,-1.734 4.054,-1.883Z" style="fill-rule:nonzero;"/>'),
  "FFF": new Symbol("FFF", 850, 400, 35, 23, '<path id="FFF" d="M15.365,0.494l0.109,0c0.262,0 0.473,0.019 0.617,0.055c0.114,0.04 0.247,0.058 0.396,0.058c0.526,0.037 1.044,0.233 1.547,0.592c0.507,0.354 0.814,0.761 0.929,1.208c0,0.149 0,0.281 0,0.396c0,0.487 -0.18,0.92 -0.536,1.293c-0.356,0.374 -0.776,0.563 -1.264,0.563c-0.268,0 -0.489,-0.055 -0.677,-0.17c-0.637,-0.299 -0.953,-0.787 -0.953,-1.459c0,-0.527 0.184,-0.959 0.56,-1.297l0.061,0c-0.079,0 -0.269,0.094 -0.564,0.28c-1.351,0.938 -2.401,2.476 -3.146,4.61l-0.06,0.281l0.957,0c0.714,0 1.125,0.039 1.24,0.111c0.188,0.149 0.277,0.356 0.277,0.619c0,0.227 -0.092,0.412 -0.277,0.564c-0.114,0.076 -0.526,0.112 -1.24,0.112l-0.17,0l-1.183,0l0,0.167c-0.751,3.826 -2.102,7.184 -4.048,10.068c-1.313,1.95 -2.889,3.096 -4.729,3.436c-0.188,0.036 -0.375,0.055 -0.56,0.055c-0.604,0 -1.117,-0.148 -1.547,-0.448c-0.436,-0.302 -0.74,-0.714 -0.93,-1.238c-0.077,-0.264 -0.113,-0.525 -0.113,-0.787c0,-0.526 0.17,-0.967 0.507,-1.321c0.341,-0.357 0.751,-0.534 1.235,-0.534c0.188,0 0.375,0.017 0.564,0.054c0.409,0.114 0.731,0.33 0.957,0.647c0.225,0.315 0.341,0.667 0.341,1.041c0,0.602 -0.267,1.049 -0.788,1.351l-0.114,0.057c0.036,0 0.091,-0.02 0.169,-0.057c0.901,-0.264 1.647,-0.959 2.253,-2.083c0.56,-1.123 1.424,-4.143 2.588,-9.057l0.278,-1.351l-1.067,0c-0.829,0 -1.277,-0.035 -1.352,-0.112c-0.267,-0.114 -0.394,-0.3 -0.394,-0.563c0,-0.262 0.127,-0.47 0.394,-0.621c0.073,-0.071 0.563,-0.11 1.46,-0.11l1.352,0l0.113,-0.28c0.603,-1.725 1.52,-3.142 2.755,-4.247c1.237,-1.106 2.588,-1.734 4.053,-1.883Zm8.223,0l0.109,0c0.262,0 0.473,0.019 0.617,0.055c0.113,0.04 0.247,0.058 0.395,0.058c0.527,0.037 1.044,0.233 1.547,0.592c0.508,0.354 0.815,0.761 0.93,1.208c0,0.149 0,0.281 0,0.396c0,0.487 -0.18,0.92 -0.536,1.293c-0.357,0.374 -0.777,0.563 -1.265,0.563c-0.267,0 -0.488,-0.055 -0.676,-0.17c-0.637,-0.299 -0.953,-0.787 -0.953,-1.459c0,-0.527 0.184,-0.959 0.56,-1.297l0.06,0c-0.079,0 -0.268,0.094 -0.563,0.28c-1.353,0.938 -2.402,2.476 -3.148,4.61l-0.06,0.281l0.957,0c0.715,0 1.125,0.039 1.24,0.111c0.188,0.149 0.278,0.356 0.278,0.619c0,0.227 -0.092,0.412 -0.278,0.564c-0.114,0.076 -0.525,0.112 -1.24,0.112l-0.169,0l-1.183,0l0,0.167c-0.751,3.826 -2.101,7.184 -4.047,10.068c-1.315,1.95 -2.888,3.096 -4.73,3.436c-0.188,0.036 -0.374,0.055 -0.559,0.055c-0.604,0 -1.117,-0.148 -1.547,-0.448c-0.437,-0.302 -0.739,-0.714 -0.928,-1.238c-0.078,-0.264 -0.114,-0.525 -0.114,-0.787c0,-0.526 0.17,-0.967 0.507,-1.321c0.34,-0.357 0.752,-0.534 1.237,-0.534c0.188,0 0.374,0.017 0.563,0.054c0.411,0.114 0.732,0.33 0.958,0.647c0.226,0.315 0.34,0.667 0.34,1.041c0,0.602 -0.266,1.049 -0.788,1.351l-0.113,0.057c0.037,0 0.092,-0.02 0.17,-0.057c0.899,-0.264 1.646,-0.959 2.253,-2.083c0.56,-1.123 1.423,-4.143 2.588,-9.057l0.276,-1.351l-1.065,0c-0.831,0 -1.278,-0.035 -1.352,-0.112c-0.267,-0.114 -0.396,-0.3 -0.396,-0.563c0,-0.262 0.129,-0.47 0.396,-0.621c0.073,-0.071 0.563,-0.11 1.461,-0.11l1.351,0l0.114,-0.28c0.602,-1.725 1.519,-3.142 2.754,-4.247c1.232,-1.108 2.584,-1.734 4.049,-1.883Zm7.64,0l0.11,0c0.261,0 0.472,0.019 0.617,0.055c0.113,0.04 0.246,0.058 0.395,0.058c0.526,0.037 1.044,0.233 1.547,0.592c0.508,0.354 0.813,0.761 0.93,1.208c0,0.149 0,0.281 0,0.396c0,0.487 -0.181,0.92 -0.538,1.293c-0.356,0.374 -0.776,0.563 -1.262,0.563c-0.269,0 -0.489,-0.055 -0.679,-0.17c-0.635,-0.299 -0.952,-0.787 -0.952,-1.459c0,-0.527 0.182,-0.959 0.56,-1.297l0.061,0c-0.08,0 -0.268,0.094 -0.564,0.28c-1.352,0.938 -2.402,2.476 -3.148,4.61l-0.059,0.281l0.958,0c0.714,0 1.125,0.039 1.241,0.111c0.188,0.149 0.276,0.356 0.276,0.619c0,0.227 -0.091,0.412 -0.276,0.564c-0.114,0.076 -0.527,0.112 -1.241,0.112l-0.169,0l-1.184,0l0,0.167c-0.752,3.826 -2.101,7.184 -4.05,10.068c-1.313,1.95 -2.887,3.096 -4.729,3.436c-0.188,0.036 -0.375,0.055 -0.561,0.055c-0.604,0 -1.117,-0.148 -1.547,-0.448c-0.436,-0.302 -0.739,-0.714 -0.928,-1.238c-0.078,-0.264 -0.114,-0.525 -0.114,-0.787c0,-0.526 0.17,-0.967 0.507,-1.321c0.339,-0.357 0.751,-0.534 1.236,-0.534c0.188,0 0.374,0.017 0.563,0.054c0.41,0.114 0.732,0.33 0.957,0.647c0.227,0.315 0.34,0.667 0.34,1.041c0,0.602 -0.267,1.049 -0.786,1.351l-0.114,0.057c0.037,0 0.092,-0.02 0.17,-0.057c0.901,-0.264 1.646,-0.959 2.254,-2.083c0.559,-1.123 1.422,-4.143 2.588,-9.057l0.276,-1.351l-1.066,0c-0.83,0 -1.276,-0.035 -1.351,-0.112c-0.268,-0.114 -0.396,-0.3 -0.396,-0.563c0,-0.262 0.129,-0.47 0.396,-0.621c0.072,-0.071 0.563,-0.11 1.459,-0.11l1.352,0l0.113,-0.28c0.602,-1.725 1.52,-3.142 2.755,-4.247c1.235,-1.108 2.588,-1.734 4.053,-1.883Z" style="fill-rule:nonzero;"/>'),
  "Tenuto": new Symbol("Tenuto", 300, 0, 12, 2, '<path id="Tenuto" d="M5.999,0.781c3.336,0 5.045,0.018 5.121,0.056c0.261,0.078 0.394,0.247 0.394,0.508c0,0.15 -0.077,0.279 -0.227,0.394l0,0.055c-0.076,0.078 -1.012,0.114 -2.811,0.114l-5.063,0c-1.802,0 -2.738,-0.037 -2.813,-0.114l0,-0.057c-0.151,-0.114 -0.224,-0.242 -0.224,-0.394c0,-0.261 0.13,-0.431 0.394,-0.508c0.072,-0.037 1.816,-0.054 5.229,-0.054Z" style="fill-rule:nonzero;"/>'),
  "MF": new Symbol("MF", 950, 0, 31, 23, '<path id="MF" d="M4.012,5.112c0.261,0 0.452,0.021 0.564,0.057c0.636,0.187 1.106,0.524 1.406,1.012c0.147,0.227 0.229,0.375 0.229,0.451c0,0.037 0.005,0.054 0.026,0.054c0.018,0 0.063,-0.054 0.139,-0.167c0.229,-0.339 0.505,-0.637 0.847,-0.9c0.11,-0.074 0.188,-0.131 0.226,-0.168c0.187,-0.228 0.505,-0.339 0.958,-0.339c0.261,0 0.504,0.039 0.729,0.113c0.489,0.189 0.884,0.508 1.188,0.958c0.109,0.227 0.184,0.375 0.225,0.452l0,0.054l0.17,-0.167c0.225,-0.339 0.484,-0.637 0.787,-0.9c0.112,-0.074 0.184,-0.13 0.225,-0.167c0.188,-0.228 0.521,-0.34 1.014,-0.34c0.225,0 0.432,0.039 0.616,0.113c0.485,0.15 0.881,0.44 1.183,0.871c0.305,0.434 0.449,0.894 0.449,1.379c0,0.414 -0.055,0.733 -0.165,0.957c-0.078,0.076 -0.341,0.872 -0.788,2.393c-0.453,1.517 -0.679,2.316 -0.679,2.391c-0.036,0.076 -0.055,0.169 -0.055,0.28c0,0.076 0,0.15 0,0.224c0,0.375 0.092,0.564 0.278,0.564l0.06,0c0.448,-0.037 0.906,-0.318 1.378,-0.842c0.471,-0.526 0.833,-1.125 1.096,-1.802c0.077,-0.224 0.169,-0.336 0.282,-0.336l0.056,0c0.188,0 0.276,0.076 0.276,0.224c0,0.188 -0.128,0.543 -0.394,1.069c-0.604,1.162 -1.443,2.043 -2.531,2.644l-0.277,0.168c-0.225,0.148 -0.489,0.224 -0.787,0.224c-0.34,0 -0.678,-0.103 -1.013,-0.309c-0.34,-0.207 -0.581,-0.478 -0.731,-0.817c-0.188,-0.336 -0.279,-0.711 -0.279,-1.124c0,-0.374 0.074,-0.675 0.226,-0.898c0,-0.037 0.225,-0.825 0.673,-2.361c0.445,-1.538 0.688,-2.354 0.702,-2.449c0.019,-0.092 0.048,-0.178 0.082,-0.251c0,-0.076 0,-0.169 0,-0.281c0,-0.336 -0.15,-0.58 -0.448,-0.73c-0.036,0 -0.078,0 -0.113,0l-0.223,0.113c-0.188,0.077 -0.387,0.261 -0.594,0.562c-0.207,0.303 -0.33,0.563 -0.367,0.79c-0.035,0.224 -0.092,0.412 -0.17,0.561c-0.036,0.076 -0.36,1.154 -0.982,3.235c-0.617,2.082 -0.948,3.142 -0.984,3.178c-0.036,0.036 -0.375,0.057 -1.013,0.057l-0.225,0l-0.17,0c-0.673,0 -1.028,-0.021 -1.065,-0.057l-0.061,-0.112c0,-0.113 0.342,-1.293 1.018,-3.545c0.673,-2.287 1.013,-3.467 1.013,-3.543c0.037,-0.074 0.073,-0.147 0.11,-0.224c0,-0.076 0,-0.169 0,-0.281c0,-0.337 -0.128,-0.563 -0.396,-0.674c-0.071,-0.037 -0.133,-0.055 -0.17,-0.055c-0.036,0 -0.109,0.037 -0.224,0.111c-0.227,0.078 -0.432,0.261 -0.618,0.562c-0.188,0.302 -0.304,0.563 -0.34,0.79c-0.036,0.225 -0.091,0.413 -0.165,0.561c-0.042,0.076 -0.366,1.156 -0.988,3.235c-0.618,2.083 -0.946,3.141 -0.984,3.178c-0.036,0.037 -0.374,0.057 -1.012,0.057l-0.226,0l-0.169,0c-0.673,0 -1.05,-0.02 -1.126,-0.057l0,-0.112c0,-0.113 0.34,-1.293 1.017,-3.545c0.674,-2.287 1.014,-3.467 1.014,-3.543c0,-0.073 0.018,-0.147 0.055,-0.224c0,-0.077 0,-0.169 0,-0.281c0,-0.337 -0.133,-0.563 -0.394,-0.674c-0.038,-0.037 -0.093,-0.055 -0.171,-0.055c-0.109,0 -0.278,0.076 -0.505,0.224c-0.831,0.375 -1.465,1.146 -1.913,2.306c-0.113,0.264 -0.208,0.394 -0.282,0.394c-0.037,0 -0.073,0 -0.109,0c-0.151,0 -0.226,-0.074 -0.226,-0.224c0,-0.188 0.109,-0.505 0.335,-0.957c0.601,-1.199 1.429,-2.081 2.477,-2.646c0.114,-0.074 0.188,-0.13 0.225,-0.167c0.15,-0.076 0.357,-0.15 0.618,-0.228c0.026,-0.014 0.086,-0.014 0.16,-0.014Zm23.134,-4.331l0.11,0c0.261,0 0.472,0.018 0.617,0.056c0.113,0.039 0.246,0.055 0.395,0.055c0.526,0.039 1.044,0.234 1.547,0.591c0.508,0.357 0.813,0.76 0.93,1.209c0,0.15 0,0.279 0,0.394c0,0.487 -0.181,0.92 -0.538,1.293c-0.356,0.374 -0.776,0.563 -1.262,0.563c-0.27,0 -0.489,-0.055 -0.679,-0.17c-0.636,-0.298 -0.951,-0.788 -0.951,-1.46c0,-0.526 0.181,-0.958 0.559,-1.294l0.061,0c-0.08,0 -0.269,0.094 -0.564,0.28c-1.352,0.938 -2.402,2.474 -3.148,4.611l-0.059,0.281l0.957,0c0.715,0 1.126,0.039 1.242,0.113c0.188,0.151 0.276,0.357 0.276,0.62c0,0.224 -0.091,0.412 -0.276,0.564c-0.114,0.076 -0.527,0.113 -1.242,0.113l-0.168,0l-1.184,0l0,0.167c-0.752,3.827 -2.101,7.182 -4.05,10.07c-1.313,1.95 -2.887,3.094 -4.729,3.434c-0.188,0.037 -0.375,0.055 -0.561,0.055c-0.604,0 -1.117,-0.147 -1.547,-0.449c-0.436,-0.301 -0.739,-0.714 -0.928,-1.237c-0.078,-0.264 -0.114,-0.527 -0.114,-0.788c0,-0.527 0.17,-0.966 0.508,-1.323c0.338,-0.357 0.751,-0.535 1.235,-0.535c0.188,0 0.374,0.018 0.563,0.054c0.411,0.114 0.732,0.33 0.957,0.647c0.226,0.318 0.34,0.667 0.34,1.041c0,0.602 -0.267,1.049 -0.787,1.351l-0.114,0.055c0.037,0 0.092,-0.018 0.171,-0.055c0.9,-0.263 1.645,-0.958 2.254,-2.083c0.56,-1.124 1.422,-4.143 2.587,-9.057l0.277,-1.349l-1.066,0c-0.831,0 -1.276,-0.037 -1.352,-0.113c-0.267,-0.114 -0.396,-0.3 -0.396,-0.564c0,-0.262 0.129,-0.47 0.396,-0.619c0.074,-0.074 0.563,-0.113 1.46,-0.113l1.352,0l0.113,-0.281c0.602,-1.724 1.52,-3.142 2.754,-4.246c1.235,-1.106 2.589,-1.732 4.054,-1.881Z" style="fill-rule:nonzero;"/>'),
  "MP": new Symbol("MP", 950, 100, 29, 17, '<path id="MP" d="M4.012,0.126c0.261,0 0.452,0.02 0.564,0.057c0.635,0.187 1.105,0.524 1.404,1.012c0.149,0.228 0.229,0.375 0.229,0.451c0,0.037 0.006,0.054 0.027,0.054c0.019,0 0.063,-0.054 0.139,-0.167c0.229,-0.339 0.506,-0.639 0.847,-0.901c0.111,-0.074 0.188,-0.13 0.226,-0.168c0.188,-0.228 0.505,-0.339 0.959,-0.339c0.261,0 0.505,0.039 0.729,0.113c0.489,0.19 0.884,0.508 1.188,0.959c0.109,0.227 0.184,0.375 0.225,0.451l0,0.054l0.17,-0.167c0.226,-0.339 0.484,-0.638 0.787,-0.9c0.111,-0.074 0.184,-0.131 0.225,-0.167c0.188,-0.227 0.521,-0.34 1.014,-0.34c0.225,0 0.432,0.039 0.618,0.113c0.483,0.15 0.879,0.441 1.181,0.871c0.305,0.435 0.449,0.895 0.449,1.379c0,0.414 -0.056,0.733 -0.165,0.957c-0.078,0.076 -0.339,0.872 -0.788,2.393c-0.453,1.517 -0.677,2.315 -0.677,2.39c-0.038,0.076 -0.057,0.169 -0.057,0.281c0,0.076 0,0.15 0,0.223c0,0.375 0.092,0.564 0.278,0.564l0.06,0c0.448,-0.037 0.906,-0.318 1.378,-0.842c0.471,-0.527 0.833,-1.126 1.096,-1.802c0.077,-0.225 0.169,-0.338 0.282,-0.338l0.056,0c0.187,0 0.276,0.078 0.276,0.225c0,0.188 -0.129,0.544 -0.396,1.069c-0.602,1.163 -1.441,2.043 -2.53,2.644l-0.276,0.169c-0.226,0.147 -0.49,0.225 -0.788,0.225c-0.342,0 -0.678,-0.104 -1.013,-0.31c-0.341,-0.207 -0.581,-0.479 -0.732,-0.817c-0.188,-0.337 -0.277,-0.711 -0.277,-1.124c0,-0.374 0.074,-0.675 0.225,-0.897c0,-0.038 0.225,-0.825 0.674,-2.362c0.445,-1.538 0.687,-2.353 0.701,-2.449c0.02,-0.091 0.049,-0.177 0.082,-0.251c0,-0.076 0,-0.17 0,-0.281c0,-0.336 -0.149,-0.581 -0.447,-0.73c-0.036,0 -0.078,0 -0.113,0l-0.223,0.113c-0.188,0.077 -0.387,0.261 -0.594,0.562c-0.206,0.303 -0.33,0.563 -0.366,0.79c-0.036,0.224 -0.092,0.412 -0.17,0.561c-0.035,0.076 -0.36,1.155 -0.983,3.235c-0.617,2.082 -0.947,3.142 -0.984,3.178c-0.035,0.037 -0.375,0.057 -1.012,0.057l-0.224,0l-0.171,0c-0.673,0 -1.028,-0.021 -1.065,-0.057l-0.061,-0.112c0,-0.113 0.341,-1.293 1.018,-3.545c0.673,-2.287 1.013,-3.467 1.013,-3.543c0.037,-0.073 0.072,-0.147 0.11,-0.224c0,-0.077 0,-0.168 0,-0.281c0,-0.337 -0.128,-0.563 -0.396,-0.674c-0.073,-0.037 -0.133,-0.055 -0.17,-0.055c-0.037,0 -0.109,0.037 -0.226,0.112c-0.224,0.078 -0.43,0.261 -0.616,0.563c-0.188,0.302 -0.303,0.563 -0.341,0.79c-0.036,0.225 -0.091,0.412 -0.164,0.561c-0.042,0.076 -0.367,1.155 -0.989,3.235c-0.617,2.083 -0.947,3.142 -0.983,3.178c-0.037,0.037 -0.374,0.057 -1.014,0.057l-0.224,0l-0.17,0c-0.672,0 -1.05,-0.02 -1.125,-0.057l0,-0.113c0,-0.113 0.34,-1.293 1.017,-3.545c0.672,-2.287 1.014,-3.467 1.014,-3.543c0,-0.073 0.018,-0.147 0.055,-0.224c0,-0.076 0,-0.169 0,-0.28c0,-0.336 -0.133,-0.563 -0.396,-0.674c-0.035,-0.037 -0.091,-0.055 -0.17,-0.055c-0.108,0 -0.276,0.076 -0.504,0.225c-0.831,0.374 -1.465,1.145 -1.913,2.305c-0.113,0.262 -0.207,0.394 -0.283,0.394c-0.036,0 -0.072,0 -0.109,0c-0.149,0 -0.225,-0.073 -0.225,-0.224c0,-0.188 0.11,-0.505 0.336,-0.957c0.601,-1.199 1.427,-2.082 2.477,-2.646c0.113,-0.074 0.187,-0.129 0.225,-0.167c0.149,-0.076 0.356,-0.15 0.617,-0.228c0.025,-0.014 0.085,-0.014 0.159,-0.014Zm15.951,0c0.978,0 1.709,0.43 2.193,1.293c0.188,0.375 0.283,0.657 0.283,0.844l0,0.113l0.17,-0.169c0.373,-0.451 0.824,-0.845 1.351,-1.182c0.522,-0.34 0.952,-0.524 1.29,-0.564c0.074,0 0.17,0 0.283,0c0.637,0 1.219,0.245 1.743,0.733c0.521,0.487 0.879,1.105 1.065,1.855c0.036,0.188 0.055,0.469 0.055,0.842c0,0.563 -0.055,1.145 -0.17,1.743c-0.263,1.462 -0.843,2.691 -1.742,3.687c-0.896,0.993 -1.908,1.546 -3.035,1.659c-0.114,0 -0.207,0 -0.284,0c-0.483,0 -0.861,-0.093 -1.122,-0.281c-0.078,-0.076 -0.148,-0.147 -0.225,-0.224c-0.526,-0.412 -0.885,-0.938 -1.07,-1.575l-0.055,-0.169c-1.277,4.276 -1.934,6.431 -1.97,6.468l0,0.057l0.055,0.055c0.149,0.112 0.431,0.226 0.843,0.339c0.079,0 0.188,0.017 0.34,0.055c0.188,0.037 0.283,0.169 0.283,0.394c0,0.187 -0.079,0.3 -0.23,0.339c-0.073,0.037 -1.146,0.055 -3.204,0.055c-2.065,0 -3.112,-0.018 -3.149,-0.055c-0.187,-0.039 -0.283,-0.151 -0.283,-0.339c0,-0.148 0.08,-0.264 0.229,-0.338c0.036,-0.039 0.146,-0.077 0.335,-0.113c0.938,-0.189 1.466,-0.451 1.576,-0.788c0.077,-0.19 0.693,-2.23 1.856,-6.133c1.162,-3.901 1.743,-5.869 1.743,-5.909c0,-0.113 0.018,-0.224 0.055,-0.336c0.036,-0.076 0.055,-0.169 0.055,-0.281c0,-0.375 -0.092,-0.563 -0.278,-0.563l-0.054,0c-0.374,0.039 -0.802,0.301 -1.27,0.787c-0.469,0.488 -0.832,1.051 -1.098,1.689c-0.074,0.261 -0.166,0.394 -0.279,0.394c-0.037,0 -0.073,0 -0.113,0c-0.109,-0.04 -0.166,-0.133 -0.166,-0.281c0,-0.151 0.131,-0.487 0.396,-1.014c0.521,-1.088 1.234,-1.93 2.137,-2.531c0.146,-0.073 0.262,-0.147 0.335,-0.224c0.188,-0.187 0.473,-0.3 0.849,-0.336c0.109,0.004 0.206,0.004 0.277,0.004Zm4.557,1.969c-0.601,0 -1.197,0.281 -1.799,0.842c-0.226,0.263 -0.356,0.451 -0.395,0.564c-0.037,0.113 -0.055,0.207 -0.055,0.281c-0.078,0.039 -0.284,0.675 -0.618,1.912l-0.563,1.799l0,0.17c0.037,0.411 0.146,0.787 0.34,1.124c0.262,0.375 0.504,0.563 0.729,0.563c0.038,0 0.08,0 0.114,0c0.034,0 0.108,-0.037 0.224,-0.112c0.602,-0.261 1.163,-0.731 1.689,-1.405c0.521,-0.675 0.879,-1.41 1.064,-2.196c0.188,-0.712 0.283,-1.369 0.283,-1.97c0,-0.338 -0.043,-0.601 -0.114,-0.787c-0.037,-0.148 -0.144,-0.311 -0.312,-0.479c-0.164,-0.169 -0.309,-0.272 -0.421,-0.309c-0.036,0.003 -0.09,0.003 -0.166,0.003Z" style="fill-rule:nonzero;"/>'),
  "P": new Symbol("P", 150, 400, 16, 17, '<path id="P" d="M6.771,0.5c0.975,0 1.708,0.432 2.193,1.293c0.188,0.374 0.285,0.657 0.285,0.845l0,0.112l0.168,-0.17c0.371,-0.45 0.824,-0.844 1.351,-1.182c0.522,-0.342 0.953,-0.523 1.291,-0.564c0.074,0 0.17,0 0.284,0c0.636,0 1.218,0.245 1.744,0.733c0.521,0.485 0.879,1.104 1.065,1.854c0.037,0.188 0.055,0.471 0.055,0.843c0,0.563 -0.055,1.146 -0.169,1.742c-0.261,1.463 -0.842,2.691 -1.744,3.688c-0.897,0.993 -1.909,1.547 -3.035,1.657c-0.114,0 -0.206,0 -0.284,0c-0.485,0 -0.861,-0.093 -1.122,-0.278c-0.078,-0.075 -0.151,-0.148 -0.225,-0.226c-0.526,-0.41 -0.883,-0.938 -1.071,-1.574l-0.055,-0.172c-1.276,4.277 -1.932,6.433 -1.969,6.47l0,0.057l0.055,0.055c0.151,0.113 0.43,0.228 0.842,0.341c0.078,0 0.188,0.018 0.339,0.055c0.188,0.037 0.283,0.171 0.283,0.394c0,0.188 -0.078,0.303 -0.229,0.341c-0.074,0.036 -1.146,0.056 -3.206,0.056c-2.065,0 -3.113,-0.02 -3.149,-0.056c-0.188,-0.038 -0.284,-0.149 -0.284,-0.341c0,-0.146 0.078,-0.263 0.229,-0.337c0.037,-0.039 0.147,-0.076 0.333,-0.111c0.938,-0.189 1.465,-0.45 1.575,-0.788c0.078,-0.189 0.696,-2.23 1.858,-6.135c1.163,-3.898 1.744,-5.867 1.744,-5.906c0,-0.113 0.018,-0.226 0.055,-0.338c0.037,-0.076 0.055,-0.171 0.055,-0.279c0,-0.375 -0.091,-0.563 -0.279,-0.563l-0.056,0c-0.375,0.039 -0.801,0.305 -1.268,0.787c-0.467,0.487 -0.833,1.051 -1.098,1.688c-0.074,0.261 -0.165,0.395 -0.279,0.395c-0.037,0 -0.073,0 -0.114,0c-0.11,-0.041 -0.165,-0.134 -0.165,-0.281c0,-0.148 0.128,-0.485 0.394,-1.014c0.521,-1.09 1.236,-1.93 2.137,-2.531c0.147,-0.071 0.261,-0.146 0.334,-0.225c0.188,-0.188 0.472,-0.3 0.847,-0.338c0.115,0.003 0.211,0.003 0.285,0.003Zm4.554,1.97c-0.6,0 -1.199,0.28 -1.799,0.841c-0.224,0.266 -0.357,0.452 -0.394,0.565c-0.037,0.111 -0.055,0.206 -0.055,0.279c-0.078,0.039 -0.283,0.675 -0.618,1.913l-0.563,1.8l0,0.17c0.037,0.411 0.147,0.788 0.339,1.124c0.261,0.375 0.504,0.563 0.728,0.563c0.037,0 0.078,0 0.114,0c0.036,0 0.11,-0.035 0.225,-0.11c0.6,-0.263 1.162,-0.731 1.689,-1.405c0.522,-0.675 0.879,-1.408 1.066,-2.196c0.188,-0.712 0.284,-1.368 0.284,-1.97c0,-0.338 -0.042,-0.602 -0.114,-0.787c-0.037,-0.146 -0.143,-0.31 -0.311,-0.479c-0.165,-0.17 -0.307,-0.272 -0.421,-0.311c-0.037,0.003 -0.094,0.003 -0.17,0.003Z" style="fill-rule:nonzero;"/>'),
  "PP": new Symbol("PP", 950, 200, 27, 17, '<path id="PP" d="M6.771,0.251c0.975,0 1.706,0.43 2.191,1.293c0.188,0.375 0.283,0.657 0.283,0.844l0,0.113l0.17,-0.169c0.371,-0.451 0.824,-0.845 1.351,-1.182c0.522,-0.34 0.953,-0.524 1.29,-0.564c0.074,0 0.172,0 0.283,0c0.636,0 1.22,0.245 1.743,0.733c0.521,0.487 0.879,1.105 1.065,1.855c0.036,0.188 0.055,0.469 0.055,0.842c0,0.563 -0.055,1.145 -0.17,1.743c-0.262,1.462 -0.842,2.691 -1.742,3.687c-0.896,0.993 -1.908,1.546 -3.035,1.659c-0.114,0 -0.207,0 -0.283,0c-0.483,0 -0.861,-0.093 -1.123,-0.281c-0.078,-0.076 -0.148,-0.147 -0.225,-0.224c-0.526,-0.412 -0.884,-0.938 -1.07,-1.575l-0.055,-0.169c-1.277,4.276 -1.933,6.431 -1.97,6.468l0,0.057l0.055,0.055c0.148,0.112 0.431,0.226 0.843,0.339c0.078,0 0.188,0.017 0.339,0.055c0.188,0.037 0.283,0.169 0.283,0.394c0,0.188 -0.079,0.3 -0.229,0.339c-0.074,0.037 -1.146,0.055 -3.206,0.055c-2.065,0 -3.111,-0.018 -3.148,-0.055c-0.188,-0.039 -0.282,-0.151 -0.282,-0.339c0,-0.148 0.079,-0.264 0.229,-0.338c0.036,-0.039 0.146,-0.076 0.335,-0.112c0.938,-0.19 1.465,-0.451 1.575,-0.788c0.078,-0.19 0.696,-2.23 1.857,-6.133c1.161,-3.901 1.743,-5.869 1.743,-5.909c0,-0.113 0.019,-0.224 0.055,-0.337c0.035,-0.076 0.055,-0.168 0.055,-0.281c0,-0.375 -0.091,-0.563 -0.277,-0.563l-0.055,0c-0.375,0.039 -0.801,0.302 -1.269,0.788c-0.47,0.487 -0.833,1.051 -1.099,1.689c-0.074,0.261 -0.165,0.394 -0.279,0.394c-0.034,0 -0.072,0 -0.113,0c-0.108,-0.04 -0.164,-0.133 -0.164,-0.281c0,-0.151 0.128,-0.488 0.395,-1.014c0.521,-1.088 1.234,-1.931 2.138,-2.531c0.146,-0.074 0.263,-0.147 0.334,-0.224c0.188,-0.188 0.473,-0.3 0.848,-0.336c0.111,0.003 0.205,0.003 0.279,0.003Zm4.555,1.969c-0.603,0 -1.199,0.281 -1.801,0.842c-0.225,0.263 -0.355,0.451 -0.395,0.564c-0.037,0.113 -0.055,0.207 -0.055,0.281c-0.078,0.039 -0.283,0.675 -0.618,1.912l-0.563,1.799l0,0.17c0.037,0.411 0.146,0.787 0.341,1.124c0.261,0.375 0.505,0.563 0.728,0.563c0.039,0 0.08,0 0.114,0c0.034,0 0.11,-0.037 0.226,-0.112c0.601,-0.261 1.161,-0.731 1.688,-1.406c0.521,-0.675 0.879,-1.409 1.064,-2.196c0.188,-0.712 0.283,-1.369 0.283,-1.969c0,-0.338 -0.043,-0.601 -0.113,-0.789c-0.036,-0.147 -0.145,-0.309 -0.313,-0.478c-0.164,-0.169 -0.308,-0.272 -0.421,-0.309c-0.032,0.004 -0.089,0.004 -0.165,0.004Zm6.512,-1.969c0.976,0 1.707,0.43 2.192,1.293c0.188,0.375 0.284,0.657 0.284,0.844l0,0.113l0.169,-0.169c0.372,-0.451 0.824,-0.845 1.351,-1.182c0.523,-0.34 0.954,-0.524 1.29,-0.564c0.074,0 0.172,0 0.284,0c0.636,0 1.219,0.245 1.742,0.733c0.521,0.487 0.879,1.105 1.065,1.855c0.036,0.188 0.055,0.469 0.055,0.842c0,0.563 -0.055,1.145 -0.171,1.743c-0.261,1.462 -0.841,2.691 -1.741,3.687c-0.896,0.993 -1.907,1.546 -3.035,1.659c-0.114,0 -0.207,0 -0.282,0c-0.485,0 -0.863,-0.093 -1.124,-0.281c-0.078,-0.076 -0.149,-0.147 -0.224,-0.224c-0.527,-0.412 -0.884,-0.938 -1.07,-1.575l-0.055,-0.169c-1.277,4.276 -1.935,6.431 -1.97,6.468l0,0.057l0.056,0.055c0.148,0.112 0.43,0.226 0.842,0.339c0.078,0 0.188,0.017 0.339,0.055c0.188,0.037 0.284,0.169 0.284,0.394c0,0.188 -0.078,0.3 -0.229,0.339c-0.071,0.037 -1.146,0.055 -3.205,0.055c-2.064,0 -3.111,-0.018 -3.147,-0.055c-0.188,-0.039 -0.283,-0.151 -0.283,-0.339c0,-0.148 0.078,-0.264 0.229,-0.338c0.035,-0.039 0.147,-0.076 0.335,-0.112c0.938,-0.19 1.465,-0.451 1.574,-0.788c0.079,-0.19 0.697,-2.23 1.859,-6.133c1.161,-3.901 1.742,-5.869 1.742,-5.909c0,-0.113 0.018,-0.224 0.055,-0.337c0.036,-0.076 0.056,-0.168 0.056,-0.281c0,-0.375 -0.093,-0.563 -0.278,-0.563l-0.056,0c-0.374,0.039 -0.8,0.302 -1.268,0.788c-0.469,0.487 -0.832,1.051 -1.099,1.689c-0.073,0.261 -0.166,0.394 -0.278,0.394c-0.036,0 -0.073,0 -0.114,0c-0.107,-0.04 -0.164,-0.133 -0.164,-0.281c0,-0.151 0.128,-0.488 0.396,-1.014c0.521,-1.088 1.234,-1.931 2.138,-2.531c0.146,-0.074 0.261,-0.147 0.334,-0.224c0.188,-0.188 0.472,-0.3 0.847,-0.336c0.105,0.003 0.201,0.003 0.275,0.003Zm4.555,1.969c-0.602,0 -1.197,0.281 -1.8,0.842c-0.224,0.263 -0.354,0.451 -0.394,0.564c-0.037,0.113 -0.056,0.207 -0.056,0.281c-0.078,0.039 -0.282,0.675 -0.618,1.912l-0.563,1.799l0,0.17c0.037,0.411 0.147,0.787 0.34,1.124c0.263,0.375 0.505,0.563 0.73,0.563c0.037,0 0.078,0 0.113,0c0.035,0 0.109,-0.037 0.226,-0.112c0.6,-0.261 1.161,-0.731 1.688,-1.406c0.52,-0.675 0.879,-1.409 1.063,-2.196c0.188,-0.712 0.284,-1.369 0.284,-1.969c0,-0.338 -0.043,-0.601 -0.113,-0.789c-0.037,-0.147 -0.144,-0.309 -0.313,-0.478c-0.164,-0.169 -0.308,-0.272 -0.422,-0.309c-0.033,0.004 -0.088,0.004 -0.165,0.004Z" style="fill-rule:nonzero;"/>'),
  "PPP": new Symbol("PPP", 950, 300, 37, 17, '<path id="PPP" d="M6.771,0.377c0.975,0 1.706,0.433 2.191,1.293c0.188,0.375 0.283,0.656 0.283,0.846l0,0.111l0.17,-0.169c0.371,-0.451 0.824,-0.846 1.351,-1.183c0.522,-0.341 0.953,-0.523 1.29,-0.563c0.074,0 0.172,0 0.283,0c0.636,0 1.22,0.244 1.743,0.731c0.521,0.486 0.879,1.104 1.065,1.855c0.036,0.188 0.055,0.472 0.055,0.842c0,0.563 -0.055,1.146 -0.17,1.743c-0.262,1.464 -0.842,2.69 -1.742,3.687c-0.896,0.993 -1.908,1.547 -3.035,1.658c-0.114,0 -0.207,0 -0.283,0c-0.483,0 -0.861,-0.092 -1.123,-0.278c-0.078,-0.077 -0.148,-0.147 -0.225,-0.226c-0.526,-0.411 -0.884,-0.938 -1.07,-1.576l-0.055,-0.17c-1.277,4.276 -1.933,6.432 -1.97,6.469l0,0.057l0.055,0.055c0.148,0.113 0.431,0.229 0.843,0.341c0.078,0 0.188,0.017 0.339,0.056c0.188,0.035 0.283,0.169 0.283,0.395c0,0.188 -0.079,0.301 -0.229,0.34c-0.074,0.037 -1.146,0.055 -3.206,0.055c-2.065,0 -3.111,-0.018 -3.148,-0.055c-0.188,-0.039 -0.282,-0.149 -0.282,-0.34c0,-0.147 0.079,-0.265 0.229,-0.339c0.036,-0.04 0.146,-0.074 0.335,-0.111c0.938,-0.189 1.465,-0.451 1.575,-0.787c0.078,-0.189 0.696,-2.231 1.857,-6.134c1.161,-3.901 1.743,-5.868 1.743,-5.908c0,-0.113 0.019,-0.225 0.055,-0.338c0.035,-0.075 0.055,-0.171 0.055,-0.28c0,-0.375 -0.091,-0.563 -0.277,-0.563l-0.055,0c-0.375,0.04 -0.801,0.305 -1.269,0.789c-0.47,0.487 -0.833,1.052 -1.099,1.688c-0.074,0.262 -0.165,0.396 -0.279,0.396c-0.034,0 -0.072,0 -0.113,0c-0.108,-0.041 -0.164,-0.134 -0.164,-0.281c0,-0.149 0.128,-0.487 0.395,-1.016c0.521,-1.088 1.234,-1.93 2.138,-2.529c0.146,-0.073 0.263,-0.147 0.334,-0.225c0.188,-0.188 0.473,-0.302 0.848,-0.337c0.111,0.001 0.205,0.001 0.279,0.001Zm4.555,1.969c-0.603,0 -1.199,0.281 -1.801,0.842c-0.225,0.265 -0.355,0.451 -0.395,0.564c-0.037,0.111 -0.055,0.206 -0.055,0.28c-0.078,0.037 -0.283,0.674 -0.618,1.912l-0.563,1.8l0,0.171c0.037,0.411 0.146,0.786 0.341,1.123c0.261,0.374 0.505,0.563 0.728,0.563c0.039,0 0.08,0 0.114,0c0.034,0 0.11,-0.037 0.226,-0.113c0.601,-0.26 1.161,-0.73 1.688,-1.404c0.521,-0.676 0.879,-1.409 1.064,-2.195c0.188,-0.713 0.283,-1.369 0.283,-1.97c0,-0.339 -0.043,-0.601 -0.113,-0.788c-0.036,-0.148 -0.145,-0.309 -0.313,-0.479c-0.164,-0.169 -0.308,-0.271 -0.421,-0.31c-0.032,0.004 -0.089,0.004 -0.165,0.004Zm6.512,-1.969c0.976,0 1.707,0.433 2.192,1.293c0.188,0.375 0.284,0.656 0.284,0.846l0,0.111l0.169,-0.169c0.372,-0.451 0.824,-0.846 1.351,-1.183c0.523,-0.341 0.954,-0.523 1.29,-0.563c0.074,0 0.172,0 0.284,0c0.636,0 1.219,0.244 1.742,0.731c0.521,0.486 0.879,1.104 1.065,1.855c0.036,0.188 0.055,0.472 0.055,0.842c0,0.563 -0.055,1.146 -0.171,1.743c-0.261,1.464 -0.841,2.69 -1.741,3.687c-0.896,0.993 -1.907,1.547 -3.035,1.658c-0.114,0 -0.207,0 -0.282,0c-0.485,0 -0.863,-0.092 -1.124,-0.278c-0.078,-0.077 -0.149,-0.147 -0.224,-0.226c-0.527,-0.411 -0.884,-0.938 -1.07,-1.576l-0.055,-0.17c-1.277,4.276 -1.935,6.432 -1.97,6.469l0,0.057l0.056,0.055c0.148,0.113 0.43,0.229 0.842,0.341c0.078,0 0.188,0.017 0.339,0.056c0.188,0.035 0.284,0.169 0.284,0.395c0,0.188 -0.078,0.301 -0.229,0.34c-0.071,0.037 -1.146,0.055 -3.205,0.055c-2.064,0 -3.111,-0.018 -3.147,-0.055c-0.188,-0.039 -0.283,-0.149 -0.283,-0.34c0,-0.147 0.078,-0.265 0.229,-0.339c0.035,-0.04 0.147,-0.074 0.335,-0.111c0.938,-0.189 1.465,-0.451 1.574,-0.787c0.079,-0.189 0.697,-2.231 1.859,-6.134c1.161,-3.902 1.742,-5.868 1.742,-5.908c0,-0.113 0.018,-0.225 0.055,-0.338c0.036,-0.075 0.056,-0.171 0.056,-0.28c0,-0.375 -0.093,-0.563 -0.278,-0.563l-0.056,0c-0.374,0.04 -0.8,0.305 -1.268,0.789c-0.469,0.487 -0.832,1.052 -1.099,1.688c-0.073,0.262 -0.166,0.396 -0.278,0.396c-0.036,0 -0.073,0 -0.114,0c-0.107,-0.041 -0.164,-0.134 -0.164,-0.281c0,-0.149 0.128,-0.487 0.396,-1.016c0.521,-1.088 1.234,-1.93 2.138,-2.529c0.146,-0.073 0.261,-0.147 0.334,-0.225c0.188,-0.188 0.472,-0.302 0.847,-0.337c0.105,0.001 0.201,0.001 0.275,0.001Zm4.555,1.969c-0.602,0 -1.197,0.281 -1.8,0.842c-0.224,0.265 -0.354,0.451 -0.394,0.564c-0.037,0.111 -0.056,0.206 -0.056,0.28c-0.078,0.037 -0.282,0.674 -0.618,1.912l-0.563,1.8l0,0.171c0.037,0.411 0.147,0.786 0.34,1.123c0.263,0.374 0.505,0.563 0.73,0.563c0.037,0 0.078,0 0.113,0c0.035,0 0.109,-0.037 0.226,-0.113c0.6,-0.26 1.161,-0.73 1.688,-1.404c0.52,-0.676 0.879,-1.409 1.063,-2.195c0.188,-0.713 0.284,-1.369 0.284,-1.97c0,-0.339 -0.043,-0.601 -0.113,-0.788c-0.037,-0.148 -0.144,-0.309 -0.313,-0.479c-0.164,-0.169 -0.308,-0.271 -0.422,-0.31c-0.033,0.004 -0.088,0.004 -0.165,0.004Zm6.168,-1.969c0.977,0 1.707,0.433 2.192,1.293c0.188,0.375 0.284,0.656 0.284,0.846l0,0.111l0.168,-0.169c0.373,-0.451 0.826,-0.846 1.352,-1.183c0.522,-0.341 0.953,-0.523 1.289,-0.563c0.074,0 0.172,0 0.284,0c0.636,0 1.22,0.244 1.743,0.731c0.521,0.486 0.879,1.104 1.064,1.855c0.037,0.188 0.056,0.472 0.056,0.842c0,0.563 -0.056,1.146 -0.17,1.743c-0.262,1.464 -0.842,2.69 -1.743,3.687c-0.896,0.993 -1.907,1.547 -3.035,1.658c-0.113,0 -0.207,0 -0.282,0c-0.483,0 -0.862,-0.092 -1.124,-0.278c-0.078,-0.077 -0.148,-0.147 -0.224,-0.226c-0.526,-0.411 -0.884,-0.938 -1.07,-1.576l-0.055,-0.17c-1.277,4.276 -1.934,6.432 -1.97,6.469l0,0.057l0.055,0.055c0.149,0.113 0.431,0.229 0.842,0.341c0.078,0 0.188,0.017 0.34,0.056c0.188,0.035 0.283,0.169 0.283,0.395c0,0.188 -0.078,0.301 -0.229,0.34c-0.074,0.037 -1.146,0.055 -3.205,0.055c-2.066,0 -3.112,-0.018 -3.148,-0.055c-0.188,-0.039 -0.283,-0.149 -0.283,-0.34c0,-0.147 0.078,-0.265 0.229,-0.339c0.036,-0.04 0.146,-0.074 0.334,-0.111c0.938,-0.189 1.465,-0.451 1.575,-0.787c0.079,-0.189 0.696,-2.231 1.858,-6.134c1.161,-3.902 1.742,-5.868 1.742,-5.908c0,-0.113 0.02,-0.225 0.056,-0.338c0.035,-0.075 0.055,-0.171 0.055,-0.28c0,-0.375 -0.091,-0.563 -0.278,-0.563l-0.055,0c-0.374,0.04 -0.8,0.305 -1.27,0.789c-0.468,0.487 -0.831,1.052 -1.098,1.688c-0.074,0.262 -0.166,0.396 -0.278,0.396c-0.036,0 -0.072,0 -0.113,0c-0.108,-0.041 -0.164,-0.134 -0.164,-0.281c0,-0.149 0.128,-0.487 0.395,-1.016c0.521,-1.088 1.234,-1.93 2.138,-2.529c0.146,-0.073 0.262,-0.147 0.334,-0.225c0.188,-0.188 0.473,-0.302 0.848,-0.337c0.11,0.001 0.204,0.001 0.278,0.001Zm4.554,1.969c-0.603,0 -1.199,0.281 -1.801,0.842c-0.226,0.265 -0.355,0.451 -0.395,0.564c-0.038,0.111 -0.055,0.206 -0.055,0.28c-0.079,0.037 -0.283,0.674 -0.618,1.912l-0.563,1.8l0,0.171c0.036,0.411 0.146,0.786 0.34,1.123c0.261,0.374 0.505,0.563 0.729,0.563c0.038,0 0.079,0 0.114,0c0.035,0 0.11,-0.037 0.226,-0.113c0.602,-0.26 1.161,-0.73 1.688,-1.404c0.521,-0.676 0.879,-1.409 1.064,-2.195c0.188,-0.713 0.283,-1.369 0.283,-1.97c0,-0.339 -0.043,-0.601 -0.113,-0.788c-0.036,-0.148 -0.144,-0.309 -0.313,-0.479c-0.164,-0.169 -0.307,-0.271 -0.421,-0.31c-0.032,0.004 -0.087,0.004 -0.165,0.004Z" style="fill-rule:nonzero;"/>'),
  "Staccato": new Symbol("Staccato", 900, 200, 4, 4, '<path id="Staccato" d="M1.869,0.25c0.635,0 1.124,0.243 1.465,0.733c0.223,0.297 0.334,0.636 0.334,1.012c0,0.636 -0.261,1.125 -0.787,1.46c-0.298,0.188 -0.637,0.284 -1.012,0.284c-0.603,0 -1.091,-0.243 -1.461,-0.733c-0.188,-0.297 -0.283,-0.636 -0.283,-1.012c0,-0.412 0.133,-0.788 0.394,-1.125c0.263,-0.334 0.618,-0.54 1.071,-0.618c0.11,-0.001 0.206,-0.001 0.279,-0.001Z" style="fill-rule:nonzero;"/>'),
  "Trill": new Symbol("Trill", 100, 400, 18, 14, '<path id="Trill" d="M8.264,0l0.056,0.055l0.06,0.058c0,0.112 -0.188,0.843 -0.563,2.192l-0.618,2.083c0.521,0 1.065,0 1.629,0c1.236,-0.077 2.379,-0.245 3.434,-0.507c0.11,0 0.225,0 0.334,0c0.453,0 0.751,0.133 0.901,0.395c0.037,0 0.431,-0.094 1.182,-0.28c0.901,-0.187 1.465,-0.278 1.689,-0.278c0.073,-0.039 0.128,-0.057 0.169,-0.057c0.559,0 0.952,0.244 1.181,0.731c0.11,0.262 0.165,0.524 0.165,0.788c0,0.3 -0.037,0.471 -0.11,0.505c-0.037,0.036 -0.444,0.142 -1.208,0.31c-0.769,0.171 -1.172,0.254 -1.212,0.254c-0.11,-0.039 -0.165,-0.113 -0.165,-0.228c0,-0.036 0,-0.072 0,-0.112c0.037,-0.076 0.055,-0.146 0.055,-0.225c0,-0.41 -0.15,-0.712 -0.449,-0.897l-0.114,-0.058l-0.169,0.058c-0.563,0.374 -1.012,0.897 -1.351,1.575c-0.11,0.146 -0.467,1.234 -1.065,3.262c-0.6,1.95 -0.92,2.944 -0.957,2.981c-0.037,0 -0.431,0.081 -1.181,0.251c-0.751,0.17 -1.164,0.254 -1.236,0.254c-0.078,0 -0.114,-0.049 -0.114,-0.145c0,-0.092 0.037,-0.287 0.114,-0.591l0.165,-0.562l-1.066,0.617c-0.975,0.563 -1.612,0.882 -1.913,0.958c-0.225,0.075 -0.467,0.113 -0.733,0.113c-0.709,0 -1.332,-0.254 -1.854,-0.763c-0.526,-0.505 -0.788,-1.114 -0.788,-1.828c0,-0.074 0.018,-0.147 0.055,-0.226c0,-0.226 0.243,-1.183 0.732,-2.867l0.733,-2.42l-0.114,0l-3.489,0l-0.339,0c-0.073,-0.036 -0.11,-0.095 -0.11,-0.171c0,-0.073 0.037,-0.146 0.11,-0.225l1.181,-0.281l1.125,-0.224l1.465,0l0.505,-0.057l0.339,-1.184c0.261,-0.824 0.403,-1.248 0.421,-1.266c0.018,-0.019 0.517,-0.366 1.492,-1.042c1.008,-0.628 1.553,-0.946 1.626,-0.946Zm2.083,4.781c-0.037,0 -0.096,0 -0.169,0c-0.975,0.225 -2.004,0.394 -3.094,0.505c-0.11,0 -0.17,0.011 -0.17,0.027c0,0.016 -0.018,0.083 -0.055,0.194c0,0.077 -0.261,0.993 -0.787,2.756c-0.6,1.877 -0.901,2.907 -0.901,3.097c0,0.036 0.018,0.11 0.059,0.224c0.11,0.414 0.334,0.621 0.673,0.621c0.073,0 0.133,-0.02 0.169,-0.059c0.072,-0.035 1.18,-0.692 3.318,-1.969c0.449,-1.539 0.672,-2.327 0.672,-2.36c0,-0.077 0.078,-0.28 0.229,-0.62c0.037,-0.113 0.147,-0.469 0.334,-1.069c0.188,-0.6 0.284,-0.918 0.284,-0.954c0,-0.188 -0.133,-0.316 -0.394,-0.395c-0.078,0.002 -0.131,0.002 -0.168,0.002Z" style="fill-rule:nonzero;"/>'),
  "Mordent": new Symbol("Mordent", 700, 400, 22, 10, '<path id="Mordent" d="M5.01,0c0.11,0 0.206,0 0.277,0c0.08,0.037 1.611,1.538 4.614,4.5c0.708,-0.861 1.313,-1.593 1.799,-2.193c0.938,-1.161 1.492,-1.84 1.663,-2.026c0.169,-0.185 0.287,-0.278 0.367,-0.278c0.034,0 0.09,0 0.162,0c0.114,0 0.208,0.011 0.283,0.027c0.074,0.018 1.521,1.415 4.331,4.191l0.277,0.277l0.731,-0.9c0.563,-0.673 0.9,-1.049 1.013,-1.123c0.079,0 0.134,0 0.171,0c0.188,0 0.33,0.055 0.421,0.164c0.097,0.114 0.143,0.247 0.143,0.398c0,0.074 -0.01,0.147 -0.025,0.226c-0.019,0.074 -0.777,1.028 -2.28,2.864c-1.496,1.84 -2.284,2.775 -2.359,2.816c-0.111,0.073 -0.244,0.109 -0.395,0.109c-0.074,0 -0.151,-0.018 -0.226,-0.055c-0.074,0 -1.61,-1.502 -4.613,-4.501c-0.709,0.862 -1.312,1.593 -1.799,2.194c-0.938,1.161 -1.487,1.839 -1.657,2.026c-0.17,0.185 -0.293,0.298 -0.366,0.335c-0.038,0 -0.097,0 -0.171,0c-0.113,0 -0.207,-0.01 -0.277,-0.025c-0.079,-0.019 -1.52,-1.435 -4.336,-4.249l-0.277,-0.276l-0.732,0.9c-0.563,0.709 -0.896,1.085 -1.013,1.123c-0.078,0 -0.134,0 -0.17,0c-0.188,0 -0.33,-0.055 -0.422,-0.165c-0.098,-0.114 -0.144,-0.246 -0.144,-0.398c0,-0.073 0,-0.128 0,-0.165c0.037,-0.079 0.815,-1.045 2.335,-2.896c1.52,-1.858 2.316,-2.803 2.391,-2.844c0.042,-0.037 0.078,-0.055 0.113,-0.055c0.035,0 0.055,0 0.055,0c0,0 0.04,-0.001 0.116,-0.001Z" style="fill-rule:nonzero;"/>'),
  "MordentInverted": new Symbol("MordentInverted", 50, 100, 22, 15, '<path id="MordentInverted" d="M10.578,0c0.224,0 0.394,0.114 0.508,0.339c0,0.037 0,0.938 0,2.701l0.055,2.641l1.181,-1.46c0.636,-0.788 1.012,-1.246 1.126,-1.378c0.11,-0.133 0.206,-0.215 0.279,-0.251c0.037,0 0.096,0 0.169,0c0.114,0 0.207,0.018 0.284,0.055c0.073,0.037 1.515,1.442 4.331,4.216l0.279,0.284l0.733,-0.902c0.563,-0.709 0.901,-1.085 1.012,-1.125c0.073,0 0.133,0 0.168,0c0.188,0 0.33,0.059 0.421,0.169c0.096,0.114 0.143,0.247 0.143,0.394c0,0.078 -0.01,0.151 -0.027,0.229c-0.018,0.073 -0.778,1.021 -2.28,2.838c-1.501,1.817 -2.289,2.765 -2.362,2.843c-0.114,0.074 -0.243,0.11 -0.394,0.11c-0.078,0 -0.151,-0.018 -0.225,-0.056c-0.078,-0.037 -1.611,-1.539 -4.614,-4.5l-0.11,0.169l-0.169,0.11l0,5.178c0,1.351 -0.037,2.042 -0.114,2.078l0,0.059c-0.11,0.11 -0.243,0.169 -0.394,0.169c-0.261,0 -0.431,-0.096 -0.504,-0.283c-0.042,-0.074 -0.06,-1.066 -0.06,-2.98l0,-2.87l-1.123,1.405c-0.604,0.751 -0.966,1.19 -1.1,1.323c-0.133,0.133 -0.25,0.197 -0.365,0.197l-0.114,0c-0.11,0 -0.325,-0.15 -0.646,-0.448c-0.32,-0.302 -1.621,-1.575 -3.91,-3.827l-0.279,-0.279l-0.733,0.897c-0.563,0.714 -0.9,1.09 -1.012,1.125c-0.078,0 -0.133,0 -0.168,0c-0.188,0 -0.33,-0.055 -0.421,-0.169c-0.096,-0.11 -0.143,-0.244 -0.143,-0.394c0,-0.074 0.01,-0.139 0.027,-0.197c0.018,-0.055 0.778,-1.001 2.279,-2.837c1.497,-1.836 2.285,-2.792 2.362,-2.871c0.114,-0.073 0.243,-0.11 0.394,-0.11c0.074,0 0.15,0.018 0.225,0.055c0.074,0.037 1.501,1.442 4.275,4.215l0.339,0.284l0.055,-0.055l0.055,-0.114l0,-3.264c0,-2.174 0.018,-3.296 0.06,-3.375c0.037,-0.151 0.128,-0.243 0.279,-0.279c0.077,-0.037 0.154,-0.059 0.228,-0.059Z" style="fill-rule:nonzero;"/>')
};
var MusicSymbolsSvg = _MusicSymbolsSvg;

// src/score_render/textures/NoteheadType.ts
var NoteheadType = /* @__PURE__ */ ((NoteheadType2) => {
  NoteheadType2[NoteheadType2["QUARTER"] = 0] = "QUARTER";
  NoteheadType2[NoteheadType2["HALF"] = 1] = "HALF";
  NoteheadType2[NoteheadType2["WHOLE"] = 2] = "WHOLE";
  return NoteheadType2;
})(NoteheadType || {});

// src/score_render/textures/TextureSet.ts
var TextureSet = class {
  constructor() {
  }
  async createTextures(pixelsPerSpace) {
    this._pixelsPerSpace = pixelsPerSpace;
    await MusicSymbolsSvg.setScale(pixelsPerSpace);
    let symbolsBD = Texture.from(MusicSymbolsSvg.atlasCanvas);
    this.wholeRest = this.getTexture("WholeRest", symbolsBD);
    this.halfRest = this.getTexture("HalfRest", symbolsBD);
    this.quarterRest = this.getTexture("QuarterRest", symbolsBD);
    this.eighthRest = this.getTexture("EighthRest", symbolsBD);
    this.sixteenthRest = this.getTexture("SixteenthRest", symbolsBD);
    this.thirtySecondRest = this.getTexture("ThirtySecondRest", symbolsBD);
    this.sixtyFourthRest = this.getTexture("SixtyFourthRest", symbolsBD);
    this.wholeNoteHead = this.getTexture("WholeNoteHead", symbolsBD);
    this.halfNoteHead = this.getTexture("HalfNoteHead", symbolsBD);
    this.quarterNoteHead = this.getTexture("QuarterNoteHead", symbolsBD);
    this.sharp = this.getTexture("Sharp", symbolsBD);
    this.flat = this.getTexture("Flat", symbolsBD);
    this.natural = this.getTexture("Natural", symbolsBD);
    this.doubleSharp = this.getTexture("DoubleSharp", symbolsBD);
    this.doubleFlat = this.getTexture("DoubleFlat", symbolsBD);
    this.eighthFlagUp = this.getTexture("EighthFlagUp", symbolsBD);
    this.eighthFlagDown = this.getTexture("EighthFlagDown", symbolsBD);
    this.sixteenthFlagUp = this.getTexture("SixteenthFlagUp", symbolsBD);
    this.sixteenthFlagDown = this.getTexture(
      "SixteenthFlagDown",
      symbolsBD
    );
    this.thirtySecondFlagUp = this.getTexture(
      "ThirtySecondFlagUp",
      symbolsBD
    );
    this.thirtySecondFlagDown = this.getTexture(
      "ThirtySecondFlagDown",
      symbolsBD
    );
    this.sixtyFourthFlagUp = this.getTexture(
      "SixtyFourthFlagUp",
      symbolsBD
    );
    this.sixtyFourthFlagDown = this.getTexture(
      "SixtyFourthFlagDown",
      symbolsBD
    );
    this.trebleClef = this.getTexture("TrebleClef", symbolsBD);
    this.bassClef = this.getTexture("BassClef", symbolsBD);
    this.altoClef = this.getTexture("CClef", symbolsBD);
    this.tenorClef = this.getTexture("CClef", symbolsBD);
    this.f = this.getTexture("F", symbolsBD);
    this.ff = this.getTexture("FF", symbolsBD);
    this.fff = this.getTexture("FFF", symbolsBD);
    this.p = this.getTexture("P", symbolsBD);
    this.pp = this.getTexture("PP", symbolsBD);
    this.ppp = this.getTexture("PPP", symbolsBD);
    this.mf = this.getTexture("MF", symbolsBD);
    this.mp = this.getTexture("MP", symbolsBD);
    this.augmentationDot = this.getTexture("Staccato", symbolsBD);
    this.accent = this.getTexture("Accent", symbolsBD);
    this.staccato = this.getTexture("Staccato", symbolsBD);
    this.legato = this.getTexture("Tenuto", symbolsBD);
    this.mordent = this.getTexture("Mordent", symbolsBD);
    this.mordentInverted = this.getTexture("MordentInverted", symbolsBD);
    this.trill = this.getTexture("Trill", symbolsBD);
  }
  getTexture(id, symbolsBD) {
    var sym = MusicSymbolsSvg.symbolsMap[id];
    let scaledTexture = new Texture({
      source: symbolsBD.source,
      frame: new Rectangle(
        sym.scaledX,
        sym.scaledY,
        sym.scaledWidth,
        sym.scaledHeight
      )
    });
    return scaledTexture;
  }
  /**This needs to be rethought because PIXI does not create a copy of
   * original pixels when using clone. Perhaps we can create colored SVG
   * noteheads instead and make them regular symbols.
   */
  /**gets a colored version of a notehead
   * @param noteheadType a value from the NoteheadType enum
   * @param color an int with values for red, green, blue (ex. 0xFF0000)*/
  getColoredNotehead(noteheadType, color) {
    let sprite;
    switch (noteheadType) {
      case 2 /* WHOLE */:
        sprite = new Sprite(this.wholeNoteHead);
        break;
      case 1 /* HALF */:
        sprite = new Sprite(this.halfNoteHead);
        break;
      default:
        sprite = new Sprite(this.quarterNoteHead);
    }
    sprite.tint = color;
    return sprite;
  }
  _disposeOfTextures() {
  }
};

// src/score_render/textures/MusicTextures.ts
var MusicTextures = class {
  static async createTextures(pixelsPerSpace) {
    if (this._textureSets[pixelsPerSpace] != null) {
      return;
    }
    var textureSet = new TextureSet();
    await textureSet.createTextures(pixelsPerSpace);
    this._textureSets[pixelsPerSpace] = textureSet;
  }
  static getTextureSet(pixelsPerSpace) {
    return this._textureSets[pixelsPerSpace];
  }
};
//map of pixels per space to matching texture set
MusicTextures._textureSets = {};

// src/score_render/bitmapRenderer/VisualMeasure.ts
var VisualMeasure = class {
  get scoreX() {
    return this.vSystemRef.scoreX + this.x;
  }
  get scoreY() {
    return this.vSystemRef.scoreY + this.y;
  }
  constructor(x = 0, y = 0, width = 0, height = 0, measureRef = null, vNoteGroups) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    this.measureRef = measureRef;
    this.vNoteGroups = vNoteGroups;
  }
};

// src/score_render/bitmapRenderer/VisualNoteGroup.ts
import {
  AccidentalType,
  ArticulationType,
  BeamState,
  DurationType,
  OrnamentType,
  StemDirection
} from "score_data";
import { Container, Graphics, Sprite as Sprite2, Text } from "pixi.js";

// src/score_render/bitmapRenderer/VNGContent.ts
var VNGContent = class {
  constructor() {
    this.minX = 9999999;
    this.maxX = -9999999;
    this.minY = 9999999;
    this.maxY = -9999999;
  }
  update(minX, maxX, minY, maxY) {
    if (minX < this.minX) {
      this.minX = minX;
    }
    if (maxX > this.maxX) {
      this.maxX = maxX;
    }
    if (minY < this.minY) {
      this.minY = minY;
    }
    if (maxY > this.maxY) {
      this.maxY = maxY;
    }
  }
};

// src/score_render/bitmapRenderer/VisualNoteGroup.ts
var VisualNoteGroup = class extends Container {
  constructor(noteGroup, scoreProps, systemRef) {
    super();
    this._content = new VNGContent();
    this._noteGroup = noteGroup;
    if (this._noteGroup == null) {
      console.log("NoteGroup null - VisualNoteGroup()");
    }
    this._scoreProps = scoreProps;
    this._vSystemRef = systemRef;
    this._init();
  }
  /**sets the color of the note.*/
  setColor(color = 0) {
    if (this._noteheadList == null) {
      return;
    }
    for (let nh of this._noteheadList) {
      nh.tint = color;
    }
  }
  _init() {
    this._visualAttachments = [];
    if (this._noteGroup.visible === false) {
      return;
    }
    this._draw();
  }
  _draw() {
    this._g = new Graphics();
    this.addChild(this._g);
    if (this._noteGroup.isRest) {
      this._drawRest();
    } else {
      this._drawNotes();
      this._drawStem();
    }
    this._addAttachments();
  }
  _drawRest() {
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let restShape;
    let verticalOffset = 0;
    switch (this._noteGroup.durationType) {
      case DurationType.WHOLE:
        restShape = new Sprite2(textures.wholeRest);
        verticalOffset = -0.75;
        break;
      case DurationType.HALF:
        restShape = new Sprite2(textures.halfRest);
        verticalOffset = -0.25;
        restShape.x = -0.35 * restShape.width;
        break;
      case DurationType.QUARTER:
        restShape = new Sprite2(textures.quarterRest);
        break;
      case DurationType.EIGHTH:
        restShape = new Sprite2(textures.eighthRest);
        break;
      case DurationType.SIXTEENTH:
        restShape = new Sprite2(textures.sixteenthRest);
        break;
      case DurationType.THIRTY_SECOND:
        restShape = new Sprite2(textures.thirtySecondRest);
        break;
      case DurationType.SIXTY_FOURTH:
        restShape = new Sprite2(textures.sixtyFourthRest);
        break;
      default:
        restShape = new Sprite2(textures.quarterRest);
        console.log(`Unsupported rest duration! ${this._noteGroup.durationType} VisualNoteGroup.drawRest()`);
    }
    restShape.y = (this._noteGroup.restVPos + verticalOffset) * px - restShape.height / 2;
    if (this._noteGroup.durationType === DurationType.WHOLE) {
      if (this._noteGroup.qNoteDuration === this._noteGroup.voice.measure.stack.qNoteLength) {
        restShape.x = -0.5 * restShape.width;
      }
    }
    this.addChild(restShape);
    for (let i = 0; i < this._noteGroup.numDots; i++) {
      this._g.circle(
        restShape.x + restShape.width + 3 + px * i,
        restShape.y + 0.3 * restShape.height,
        this._scoreProps.noteheadWidth * px / 5
      );
      this._g.fill(0);
    }
    if (restShape.x < this.content.minX) {
      this.content.minX = restShape.x;
    }
    if (restShape.x + restShape.width > this.content.maxX) {
      this.content.maxX = restShape.x + restShape.width;
    }
    this.content.update(
      restShape.x,
      restShape.x + restShape.width,
      restShape.y,
      restShape.y + restShape.height
    );
  }
  _drawNotes() {
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let bottomNote;
    let topNote;
    this._noteheadList = [];
    for (let note of this._noteGroup.notes) {
      if (note.visible === false) {
        continue;
      }
      if (bottomNote == null) {
        bottomNote = note;
      }
      topNote = note;
      let noteShape;
      switch (this._noteGroup.durationType) {
        case DurationType.WHOLE:
          noteShape = new Sprite2(textures.wholeNoteHead);
          this._noteheadType = 2 /* WHOLE */;
          noteShape.tint = 0;
          break;
        case DurationType.HALF:
          noteShape = new Sprite2(textures.halfNoteHead);
          this._noteheadType = 1 /* HALF */;
          noteShape.tint = 0;
          break;
        default:
          noteShape = new Sprite2(textures.quarterNoteHead);
          this._noteheadType = 0 /* QUARTER */;
          noteShape.tint = 0;
      }
      this._noteheadList.push(noteShape);
      if (this._noteGroup.isGrace) {
        noteShape.scale.x *= 0.65;
        noteShape.scale.y *= 0.65;
      }
      noteShape.x = this._noteGroup.stemDirection === StemDirection.DOWN ? note.hPos * px - 1 : note.hPos * px;
      noteShape.y = note.vPos * px - noteShape.height / 2;
      this.addChild(noteShape);
      this._content.update(
        noteShape.x,
        noteShape.x + noteShape.width,
        noteShape.y,
        noteShape.y + noteShape.height
      );
      for (let i = 0; i < this._noteGroup.numDots; i++) {
        let x = (note.hPos + 1.6 * this._scoreProps.noteheadWidth + i) * px;
        let y = px * (note.vPos - 0.5 * ((Math.abs(note.stepsFromTopStaffLine) + 1) % 2));
        let radius = this._scoreProps.noteheadWidth * px / 5;
        this._g.circle(x, y, radius);
        this._g.fill(0);
        this._content.update(
          x - radius / 2,
          x + radius / 2,
          y - radius / 2,
          y + radius / 2
        );
      }
      let accidental = null;
      let vertOffset = 0;
      if (note.showAccidental) {
        switch (note.accidental) {
          case AccidentalType.NONE:
            break;
          case AccidentalType.SHARP:
            accidental = new Sprite2(textures.sharp);
            vertOffset = -0.1;
            break;
          case AccidentalType.FLAT:
            accidental = new Sprite2(textures.flat);
            vertOffset = -0.5;
            break;
          case AccidentalType.NATURAL:
            accidental = new Sprite2(textures.natural);
            vertOffset = -0.1;
            break;
          case AccidentalType.DOUBLE_SHARP:
            accidental = new Sprite2(textures.doubleSharp);
            vertOffset = 0.2;
            break;
          case AccidentalType.DOUBLE_FLAT:
            accidental = new Sprite2(textures.doubleFlat);
            vertOffset = -0.5;
            break;
          case AccidentalType.TRIPLE_SHARP:
            accidental = new Sprite2();
            let sharp = new Sprite2(textures.sharp);
            sharp.x = -2.5 * px;
            let doubSharp = new Sprite2(textures.doubleSharp);
            doubSharp.y = 1.3 * px;
            accidental.addChild(sharp);
            accidental.addChild(doubSharp);
            break;
          case AccidentalType.TRIPLE_FLAT:
            accidental = new Sprite2();
            let flat = new Sprite2(textures.flat);
            flat.x = -1;
            let doubFlat = new Sprite2(textures.doubleFlat);
            accidental.addChild(flat);
            accidental.addChild(doubFlat);
            vertOffset = -0.5;
            break;
          default:
            console.log(`Unrecognized accidental! ${note.accidental} VisualNoteGroup.drawNotes()`);
        }
      }
      if (accidental != null) {
        accidental.scale.x = noteShape.scale.x;
        accidental.scale.y = noteShape.scale.y;
        accidental.y = noteShape.y - accidental.height / 4 + vertOffset * px;
        accidental.x = note.accidentalPos * px;
        this.addChild(accidental);
        this._content.update(
          accidental.x,
          accidental.x + accidental.width,
          accidental.y,
          accidental.y + accidental.height
        );
      }
    }
    if (bottomNote == null) {
      return;
    }
    if (bottomNote.legerLines > 0) {
      let legerWidth = Math.max(
        this._scoreProps.legerLineWidth * px,
        1
      );
      this._g.setStrokeStyle({ width: legerWidth, color: 0 });
      for (let i = 0; i < bottomNote.legerLines; i++) {
        let x1 = -0.2 * this._scoreProps.noteheadWidth * px;
        let y = px * (5 + i);
        this._g.moveTo(x1, y);
        let x2 = (1.2 * this._scoreProps.noteheadWidth + bottomNote.hPos) * px;
        this._g.lineTo(x2, y);
        this._g.stroke();
        this._content.update(x1, x2, y, y + legerWidth);
      }
    }
    if (topNote.legerLines < 0) {
      let legerWidth = Math.max(
        this._scoreProps.legerLineWidth * px,
        1
      );
      this._g.setStrokeStyle({ width: legerWidth, color: 0 });
      let i = 0;
      while (i > topNote.legerLines) {
        let x1 = -0.2 * this._scoreProps.noteheadWidth * px;
        let y = px * (-1 + i);
        this._g.moveTo(x1, y);
        let x2 = (1.2 * this._scoreProps.noteheadWidth + topNote.hPos) * px;
        this._g.lineTo(x2, y);
        this._g.stroke();
        i--;
        this._content.update(x1, x2, y, y + legerWidth);
      }
    }
  }
  _drawStem() {
    if (this._noteGroup.stemDirection === StemDirection.NO_STEM) {
      return;
    }
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let stemWidth = Math.max(this._scoreProps.stemWidth * px, 1);
    this._g.setStrokeStyle({ width: stemWidth, color: 0 });
    let x = this._noteGroup.stemHPos * px;
    let y1 = this._noteGroup.stemStartPos * px;
    this._g.moveTo(x, y1);
    let y2 = this._noteGroup.stemEndPos * px;
    this._g.lineTo(x, y2);
    this._g.stroke();
    if (y2 > y1) {
      this._content.update(x, x + stemWidth, y1, y2);
    } else {
      this._content.update(x, x + stemWidth, y2, y1);
    }
    if (this._noteGroup.beamStates.length === 0 || this._noteGroup.beamStates[0] === BeamState.NONE) {
      let flagShape = null;
      switch (this._noteGroup.durationType) {
        case DurationType.EIGHTH:
          flagShape = this._noteGroup.stemDirection === StemDirection.UP ? new Sprite2(textures.eighthFlagUp) : new Sprite2(textures.eighthFlagDown);
          break;
        case DurationType.SIXTEENTH:
          flagShape = this._noteGroup.stemDirection === StemDirection.UP ? new Sprite2(textures.sixteenthFlagUp) : new Sprite2(textures.sixteenthFlagDown);
          break;
        case DurationType.THIRTY_SECOND:
          flagShape = this._noteGroup.stemDirection === StemDirection.UP ? new Sprite2(textures.thirtySecondFlagUp) : new Sprite2(textures.thirtySecondFlagDown);
          break;
        case DurationType.SIXTY_FOURTH:
          flagShape = this._noteGroup.stemDirection === StemDirection.UP ? new Sprite2(textures.sixteenthFlagUp) : new Sprite2(textures.sixteenthFlagDown);
          break;
        case DurationType.QUARTER:
          break;
        case DurationType.HALF:
          break;
        case DurationType.WHOLE:
          break;
        default:
          console.log(`Unknown duration! ${this._noteGroup.durationType} VisualNoteGroup.drawStem()`);
      }
      if (flagShape != null) {
        if (this._noteGroup.isGrace) {
          flagShape.scale.y = flagShape.scale.x = 0.6;
        }
        flagShape.x = this._noteGroup.stemHPos * px;
        flagShape.y = this._noteGroup.stemDirection === StemDirection.UP ? this._noteGroup.stemEndPos * px : this._noteGroup.stemEndPos * px - flagShape.height;
        this.addChild(flagShape);
        this._content.update(
          flagShape.x,
          flagShape.x + flagShape.width,
          flagShape.y,
          flagShape.y + flagShape.height
        );
      }
    }
  }
  _addAttachments() {
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    if (this._noteGroup.lyric != null) {
      let lyric = this._noteGroup.lyric;
      if (lyric.text != null && lyric.text !== "") {
        let lyricTF = new Text({
          text: lyric.text,
          style: {
            fontFamily: "arial",
            fontSize: px * 2,
            fill: 0,
            align: "left"
          }
        });
        lyricTF.y = -1 * lyric.vPos * px - lyricTF.height + 4;
        lyricTF.x = lyricTF.width / -2 + this._scoreProps.noteheadWidth * px / 2;
        this.addChild(lyricTF);
        this._visualAttachments.push(lyricTF);
      }
    }
    if (this._noteGroup.articulations != null) {
      for (let artic of this._noteGroup.articulations) {
        let articBmp;
        switch (artic.type) {
          case ArticulationType.STACCATO:
            articBmp = new Sprite2(textures.staccato);
            break;
          case ArticulationType.ACCENT:
            articBmp = new Sprite2(textures.accent);
            break;
          case ArticulationType.LEGATO:
            articBmp = new Sprite2(textures.legato);
            break;
          default:
            continue;
        }
        if (articBmp != null) {
          articBmp.x = artic.hPos * px;
          articBmp.y = artic.vPos * px;
          this.addChild(articBmp);
          this._visualAttachments.push(articBmp);
        }
      }
    }
    if (this._noteGroup.ornaments != null) {
      for (let orn of this._noteGroup.ornaments) {
        let ornBmp;
        switch (orn.type) {
          case OrnamentType.INVERTED_MORDENT:
            ornBmp = new Sprite2(textures.mordentInverted);
            break;
          case OrnamentType.MORDENT:
            ornBmp = new Sprite2(textures.mordent);
            break;
          case OrnamentType.TRILL_MARK:
            ornBmp = new Sprite2(textures.trill);
            break;
          default:
            continue;
        }
        if (ornBmp != null) {
          ornBmp.x = orn.hPos * px;
          ornBmp.y = orn.vPos * px;
          this.addChild(ornBmp);
          this._visualAttachments.push(ornBmp);
        }
      }
    }
  }
  get noteGroup() {
    return this._noteGroup;
  }
  get vSystemRef() {
    return this._vSystemRef;
  }
  set vSystemRef(value) {
    this._vSystemRef = value;
  }
  get visualAttachments() {
    return this._visualAttachments;
  }
  set visualAttachments(value) {
    this._visualAttachments = value;
  }
  get scoreX() {
    return this._vSystemRef.scoreX + this.x;
  }
  get scoreY() {
    return this._vSystemRef.scoreY + this.y;
  }
  /**The content object lists positioning info of elements in this
   * VisualNoteGroup relative to an origin that starts on the top staff line.
   * The elements of the NoteGroup may begin above, below, to the left or
   * to the right of that point. To determine the score position of the
   * elements or bounds of this notegroup, combine these values with the
   * VisualNoteGroup's scoreX and scoreY. */
  get content() {
    return this._content;
  }
  get noteheadList() {
    return this._noteheadList.slice(0);
  }
  get noteheadType() {
    return this._noteheadType;
  }
  /**the X/Y position of the VisualNoteGroup as it is originally set. This can
   * be used to save the note's position before it gets moved during 
   * assessment, etc.*/
  get originalPosition() {
    return this._originalPosition;
  }
  set originalPosition(value) {
    this._originalPosition = value;
  }
};

// src/score_render/bitmapRenderer/VisualSystem.ts
import {
  Container as Container2,
  Graphics as Graphics2,
  MSAA_QUALITY,
  Rectangle as Rectangle2,
  Sprite as Sprite3
} from "pixi.js";
var VisualSystem = class extends Container2 {
  constructor(visualScore, system, scoreProperties) {
    super();
    this._visualMeasures = [];
    this._visualSystemStaves = [];
    this._visualScore = visualScore;
    this._systemRef = system;
    this._scoreProps = scoreProperties;
    this._visNoteGroups = [];
    this.g = new Graphics2();
    this.addChild(this.g);
    this.container = new Container2();
    this.addChild(this.container);
    this._textureSprite = new Sprite3();
  }
  /**
   * gets the VisualMeasure object that lies under the passed in Point
   * @param point a Point object relative to the system origin with
   * coordinates of the point to check against each measure in the system
   * @return a VisualMeasure if one is found, otherwise null
   */
  getVisualMeasureUnderPoint(point) {
    for (let vStaff of this._visualSystemStaves) {
      if (vStaff.y <= point.y && vStaff.y + vStaff.height > point.y) {
        for (let vMeas of vStaff.vMeasures) {
          if (vMeas.x <= point.x && vMeas.x + vMeas.width > point.x && vMeas.y <= point.y && vMeas.y + vMeas.height > point.y) {
            return vMeas;
          }
        }
      }
    }
    return null;
  }
  /**
   * gets the VisualNoteGroup object that lies under the passed in Point
   * @param point a Point object relative to the system origin with
   * coordinates of the point to check against each NoteGroup in the system
   * @return a VisualNoteGroup if one is found, otherwise null
   */
  getVisualNoteGroupUnderPoint(point) {
    for (let vng of this._visNoteGroups) {
      if (vng.x <= point.x && vng.x + vng.width > point.x && vng.y <= point.y && vng.y + vng.height > point.y) {
        return vng;
      }
    }
    return null;
  }
  /**gets a Sprite object with the cached system graphics, creating the cache
   * if it doesn't exist.*/
  getTextureSprite() {
    if (this._renderTexture == null) {
      this._generateTexture();
    }
    let sprite = new Sprite3(this._renderTexture);
    sprite.x = this._graphicsBounds.x;
    sprite.y = this._graphicsBounds.y;
    return sprite;
  }
  /**creates or refreshes the texture of cached system graphics.*/
  createOrRefreshTexture() {
    let wasDisplayed = this._textureSprite?.parent === this;
    this.deleteTexture();
    this._generateTexture();
    if (wasDisplayed) {
      this.displayTexture();
    }
  }
  /**displays the texture for this system instead of the live graphics. If
   * the texture doesn't exist, it is created. If it does exist, it is
   * resused.*/
  displayTexture() {
    if (this._renderTexture == null) {
      this._generateTexture();
    }
    if (this.g.parent === this) {
      this.removeChild(this.g);
    }
    if (this.container.parent === this) {
      this.removeChild(this.container);
    }
    if (this._textureSprite.parent !== this) {
      this.addChild(this._textureSprite);
    }
  }
  /**displays the live graphics for this system instead of the cached
   * texture*/
  displayGraphics() {
    if (this._textureSprite.parent === this) {
      this.removeChild(this._textureSprite);
    }
    if (this.g.parent !== this) {
      this.addChildAt(this.g, 0);
    }
    if (this.container.parent !== this) {
      this.addChildAt(this.container, 1);
    }
  }
  /**disposes of all textures that have been generated for this system. */
  deleteTexture() {
    if (this._textureSprite != null) {
      if (this._textureSprite.parent === this) {
        this.removeChild(this._textureSprite);
      }
      this._textureSprite.destroy(true);
      this._textureSprite = null;
      this._renderTexture = null;
      this._graphicsBounds = null;
    }
  }
  ///adds an extra notehead, as for assessment, to the system
  addExtraNotehead(xPos, yPos, color) {
  }
  ///removes any extra noteheads that were added (as for assessment). If any
  ///were present, true is returned, otherwise false.
  removeExtraNoteheads() {
    return true;
  }
  _generateTexture() {
    let bounds = this.getLocalBounds();
    let rect = new Rectangle2(
      Math.floor(bounds.x) - 1,
      Math.floor(bounds.y) - 1,
      Math.ceil(bounds.width) + 2,
      Math.ceil(bounds.height) + 2
    );
    let renderer = this._visualScore.pixiAppHelper.app.renderer;
    this._renderTexture = renderer.generateTexture(this);
    this._textureSprite.texture = this._renderTexture;
    this._textureSprite.x = rect.x;
    this._textureSprite.y = rect.y;
    this._graphicsBounds = rect;
  }
  get visNoteGroups() {
    return this._visNoteGroups;
  }
  get pageRef() {
    return this._pageRef;
  }
  set pageRef(value) {
    this._pageRef = value;
  }
  get scoreX() {
    return this.x + this._pageRef.x;
  }
  get scoreY() {
    return this.y + this._pageRef.y;
  }
  /**
   * the System object used to generate this VisualSystem
   */
  get systemRef() {
    return this._systemRef;
  }
  /**
   * a list of Rectangles containing the dimensions and positions of the
   * measures in the system (positions relative to system origin). Rectangles
   * are ordered one row at a time (all measures of first row, then second
   * row, etc.). */
  get visualMeasures() {
    return this._visualMeasures;
  }
  /**an array of conceptual containers of the VisualMeasures for this system.
   * This is used for faster point searching.	*/
  get visualSystemStaves() {
    return this._visualSystemStaves;
  }
  /**The bounds calculated during caching of the Graphics object for this
   * system.*/
  get graphicsBounds() {
    return this._graphicsBounds.clone();
  }
};
VisualSystem.renderTextureQuality = MSAA_QUALITY.HIGH;

// src/score_render/bitmapRenderer/VisualSystemStaff.ts
var VisualSystemStaff = class {
  constructor(y, height, vSystem, vMeasures) {
    this.y = y;
    this.height = height;
    this.vSystemRef = vSystem;
    this.vMeasures = vMeasures;
  }
};

// src/score_render/bitmapRenderer/SystemRenderer.ts
var _SystemRenderer = class _SystemRenderer {
  constructor(scoreProps) {
    this._scoreProps = scoreProps;
    this._legacyVNGIndex = 0;
  }
  renderSystem(visualScore, system, parts, addedObjects, removedObjects) {
    let visualSystem = new VisualSystem(
      visualScore,
      system,
      this._scoreProps
    );
    system.visibleParts = parts;
    let staffVPosOffsets = system.staffVPosOffsets;
    let numStaves = staffVPosOffsets.length;
    let staffGroupings = system.staffGroupings;
    let stacks = system.measureStacks;
    let numStacks = stacks.length;
    let systemIndent = system.indent;
    let systemWidth = system.systemWidth;
    let staffSpacing = 4 + system.staffSpacing;
    let vStaffPos = 0;
    let px = this._scoreProps.pixelsPerSpace;
    for (let i = 0; i < numStaves; i++) {
      if (parts.indexOf(stacks[0].measures[i].staff.partRef) === -1 || stacks[0].measures[i].staff.visible === false) {
        continue;
      }
      vStaffPos += staffVPosOffsets[i];
      let horizMeasPos = systemIndent;
      this._drawStaff(systemWidth, visualSystem, systemIndent, vStaffPos);
      let vMeasuresOnStaff = [];
      let vngLists = [];
      for (let j = 0; j < numStacks; j++) {
        let cStack = stacks[j];
        let cMeasure = cStack.measures[i];
        let vNoteGroupsInMeasure = this._drawNotes(
          cMeasure,
          systemIndent,
          vngLists,
          visualSystem,
          vStaffPos,
          addedObjects,
          removedObjects
        );
        this._addClefKeyTime(
          cMeasure,
          systemIndent,
          vStaffPos,
          visualSystem,
          vngLists
        );
        this._addAttachments(
          cMeasure,
          systemIndent,
          vStaffPos,
          visualSystem
        );
        if (i === 0) {
          cStack.needsRendering = false;
        }
        let clefOffset = 0;
        if (j + 1 < numStacks && stacks[j + 1].newClef) {
          clefOffset = this._scoreProps.clefWidth + this._scoreProps.clefDistanceFromBarline;
        } else if (cStack.newClef && j > 0) {
          clefOffset = -1 * (this._scoreProps.clefWidth + this._scoreProps.clefDistanceFromBarline);
        }
        let vMeasure = new VisualMeasure(
          horizMeasPos * px,
          vStaffPos * px,
          (cStack.width + clefOffset) * px,
          4 * px,
          cMeasure,
          vNoteGroupsInMeasure
        );
        vMeasure.vSystemRef = visualSystem;
        visualSystem.visualMeasures.push(vMeasure);
        vMeasuresOnStaff.push(vMeasure);
        horizMeasPos += cStack.width + clefOffset;
      }
      let vSystemStaff = new VisualSystemStaff(
        vStaffPos * px,
        4 * px,
        visualSystem,
        vMeasuresOnStaff
      );
      visualSystem.visualSystemStaves.push(vSystemStaff);
      this._drawBeams(vngLists, systemIndent, vStaffPos, visualSystem);
      this._drawTuplets(vngLists, vStaffPos, visualSystem);
      this._drawTies(vngLists, vStaffPos, visualSystem);
      vStaffPos += staffSpacing;
    }
    this._renderVerticalBarlines(
      staffGroupings,
      systemIndent,
      visualSystem,
      vStaffPos - staffSpacing,
      stacks,
      systemWidth
    );
    this._drawRepeats(
      staffGroupings,
      systemIndent,
      visualSystem,
      vStaffPos - staffSpacing,
      stacks
    );
    this._drawSlurs(system, visualSystem, parts);
    return visualSystem;
  }
  _drawStaff(systemWidth, vSystem, systemIndent, vStaffPos) {
    let px = this._scoreProps.pixelsPerSpace;
    let g = vSystem.g;
    g.setStrokeStyle({
      color: 0,
      width: Math.max(this._scoreProps.staffLineWidth * px, 1)
    });
    for (let i = 0; i < 5; i++) {
      g.moveTo(systemIndent * px, (i + vStaffPos) * px);
      g.lineTo(systemWidth * px, (i + vStaffPos) * px);
    }
    g.stroke();
  }
  _drawNotes(measure, systemIndent, vngLists, vSystem, vStaffPos, addedObjects, removedObjects) {
    let vng;
    let px = this._scoreProps.pixelsPerSpace;
    let vNoteGroupsInMeasure = [];
    if (!measure.notesNeedRendering) {
      let numLegacyVNGs = this._legacyVNoteGroups.length;
      for (let i = 0; i < measure.voices.length; i++) {
        if (vngLists.length < i + 1) {
          vngLists.push(new Array());
        }
        for (let noteGroup of measure.voices[i].noteGroups) {
          let loopedOnce = false;
          while (this._legacyVNGIndex < numLegacyVNGs && this._legacyVNoteGroups[this._legacyVNGIndex].noteGroup !== noteGroup) {
            this._legacyVNGIndex++;
            if (this._legacyVNGIndex >= numLegacyVNGs) {
              if (!loopedOnce) {
                loopedOnce = true;
                this._legacyVNGIndex = 0;
              }
            }
          }
          if (this._legacyVNGIndex >= numLegacyVNGs) {
            throw "Looking for a VisualNoteGroup that is not available for re-render! This probably means a Measure with a new note was not marked for re-rendering.";
          }
          vng = this._legacyVNoteGroups[this._legacyVNGIndex];
          let attachments = [];
          for (let attachment of vng.visualAttachments) {
            if (attachment instanceof Graphics3) {
              attachments.push(attachment);
            }
          }
          vng.visualAttachments = attachments;
          vng.spx = systemIndent + measure.stack.startPosition + noteGroup.hPos;
          vng.x = vng.spx * px;
          vng.spy = vStaffPos;
          vng.y = vng.spy * px;
          vng.originalPosition.x = vng.x;
          vng.originalPosition.y = vng.y;
          vSystem.container.addChild(vng);
          vng.vSystemRef = vSystem;
          vSystem.visNoteGroups.push(vng);
          vngLists[i].push(vng);
          vNoteGroupsInMeasure.push(vng);
        }
      }
    } else {
      for (let i = 0; i < measure.voices.length; i++) {
        if (vngLists.length < i + 1) {
          vngLists.push(new Array());
        }
        for (let noteGroup of measure.voices[i].noteGroups) {
          vng = new VisualNoteGroup(
            noteGroup,
            this._scoreProps,
            vSystem
          );
          vng.spx = systemIndent + measure.stack.startPosition + noteGroup.hPos;
          vng.x = vng.spx * px;
          vng.spy = vStaffPos;
          vng.y = vng.spy * px;
          vng.originalPosition = new Point3(vng.x, vng.y);
          vSystem.container.addChild(vng);
          vSystem.visNoteGroups.push(vng);
          vngLists[i].push(vng);
          vNoteGroupsInMeasure.push(vng);
          addedObjects.push(vng);
        }
      }
      if (this._legacyVNoteGroups != null && this._legacyVNoteGroups.length > 0) {
        while (this._legacyVNGIndex < this._legacyVNoteGroups.length && this._legacyVNoteGroups[this._legacyVNGIndex].noteGroup.voice.measure === measure) {
          removedObjects.push(
            this._legacyVNoteGroups[this._legacyVNGIndex]
          );
          this._legacyVNGIndex++;
        }
      }
      measure.notesNeedRendering = false;
    }
    return vNoteGroupsInMeasure;
  }
  _drawBeams(vngLists, systemIndent, vStaffPos, vSystem) {
    let px = this._scoreProps.pixelsPerSpace;
    let currentBeams = {};
    let beamWidth = this._scoreProps.beamWidth;
    let stemEndPos = 0;
    let hPos = 0;
    let vPos = 0;
    let g = vSystem.g;
    for (let vngList of vngLists) {
      for (let vng of vngList) {
        let noteGroup = vng.noteGroup;
        if (!noteGroup.visible) {
          currentBeams = {};
          continue;
        }
        let scale = !noteGroup.isGrace ? 1 : 0.65;
        let stackPos = noteGroup.voice.measure.stack.startPosition;
        beamWidth = this._scoreProps.beamWidth * scale;
        for (let i = 0; i < noteGroup.beamStates.length; i++) {
          if (noteGroup.beamStates[i] !== BeamState2.CONTINUE && noteGroup.beamStates[i] !== BeamState2.NONE) {
            stemEndPos = noteGroup.stemEndPos;
            hPos = systemIndent + stackPos + noteGroup.hPos + noteGroup.stemHPos;
            vPos = noteGroup.stemDirection === StemDirection2.UP ? stemEndPos + beamWidth * 2 * i : stemEndPos - beamWidth * 2 * i;
            vPos += vStaffPos;
          }
          switch (noteGroup.beamStates[i]) {
            case BeamState2.BEGIN:
              currentBeams[i] = new Point3(hPos, vPos);
              break;
            case BeamState2.END:
              let startingPoint = currentBeams[i];
              if (startingPoint != null) {
                g.setStrokeStyle({
                  color: 0,
                  width: beamWidth * px,
                  join: "round",
                  cap: "butt"
                });
                g.moveTo(
                  startingPoint.x * px,
                  startingPoint.y * px
                );
                g.lineTo(hPos * px, vPos * px);
                g.stroke();
              }
              break;
            case BeamState2.CONTINUE:
              break;
            case BeamState2.FORWARD_HOOK:
              g.setStrokeStyle({
                color: 0,
                width: beamWidth * px,
                join: "round",
                cap: "butt"
              });
              g.moveTo(hPos * px, vPos * px);
              g.lineTo(
                (hPos + this._scoreProps.noteheadWidth / 1.5) * px,
                vPos * px
              );
              g.stroke();
              break;
            case BeamState2.BACKWARD_HOOK:
              g.setStrokeStyle({
                color: 0,
                width: beamWidth * px,
                join: "round",
                cap: "butt"
              });
              g.moveTo(hPos * px, vPos * px);
              g.lineTo((hPos - this._scoreProps.noteheadWidth / 1.5) * px, vPos * px);
              g.stroke();
              break;
            case BeamState2.NONE:
              break;
            default:
              console.log(`Unrecognized Beam State! VisualSystem.drawBeams ${noteGroup.beamStates[i]}`);
          }
        }
      }
    }
  }
  _drawTuplets(vngLists, vStaffPos, vSystem) {
    let px = this._scoreProps.pixelsPerSpace;
    let g = vSystem.g;
    let bracketWidth = Math.max(
      1,
      this._scoreProps.tupletBracketWidth * px
    );
    for (let vngList of vngLists) {
      for (let vng of vngList) {
        if (vng.noteGroup.tuplets == null) {
          continue;
        }
        let noteGroup = vng.noteGroup;
        for (let tuplet of noteGroup.tuplets) {
          if (tuplet.firstNote.visible === false || tuplet.endNote.visible === false) {
            continue;
          }
          if (tuplet.firstNote === noteGroup) {
            let tupletTF = new Text2({
              text: tuplet.numerator.toString(),
              style: {
                fontFamily: "Arial",
                align: "center",
                fontSize: 1.7 * px,
                fill: 0
              }
            });
            tupletTF.text = tuplet.numerator.toString();
            let centerX = vng.spx + (tuplet.endNote.hPos - vng.noteGroup.hPos) / 2 + tuplet.firstNote.stemHPos / 2;
            tupletTF.x = centerX * px;
            let vPos = tuplet.vPos + vStaffPos;
            tupletTF.y = vPos * px - tupletTF.height / 2;
            if (tuplet.showBracket) {
              g.setStrokeStyle({
                color: 0,
                width: bracketWidth,
                join: "round",
                cap: "butt"
              });
              let startX = vng.spx;
              let endX = vng.spx + (tuplet.endNote.hPos - vng.noteGroup.hPos) + this._scoreProps.noteheadWidth;
              let hookEndVPos = tuplet.above ? vPos + 1 / 2 : vPos - 1 / 2;
              g.moveTo(startX * px, hookEndVPos * px);
              g.lineTo(startX * px, vPos * px);
              g.lineTo((centerX - 0.3) * px, vPos * px);
              g.moveTo((centerX + 1.2) * px, vPos * px);
              g.lineTo(endX * px, vPos * px);
              g.lineTo(endX * px, hookEndVPos * px);
              g.stroke();
            }
            vSystem.container.addChild(tupletTF);
          }
        }
      }
    }
  }
  _addClefKeyTime(measure, systemIndent, vStaffPos, vSystem, vngLists) {
    let hInsertPos = measure.stack.startPosition + systemIndent;
    let clefList = measure.clefs;
    let clefType = clefList[0].type;
    if (clefList[0].show) {
      hInsertPos += this._scoreProps.clefDistanceFromBarline;
      this._drawClef(
        clefType,
        clefList[0].smallSize,
        hInsertPos,
        vStaffPos,
        vSystem
      );
      hInsertPos += this._scoreProps.clefWidth;
    } else if (measure.stack.newClef) {
      hInsertPos += this._scoreProps.clefDistanceFromBarline + this._scoreProps.clefWidth;
    }
    let i = 1;
    while (i < clefList.length) {
      let clef = clefList[i];
      let breakMiddle = false;
      for (let vngList of vngLists) {
        let numVNGs = vngList.length;
        for (let j = 0; j < numVNGs; j++) {
          if (vngList[j].noteGroup.clef === clef) {
            this._drawClef(
              clef.type,
              clef.smallSize,
              vngList[j].x - this._scoreProps.clefWidth - this._scoreProps.clefDistanceFromBarline,
              vStaffPos,
              vSystem
            );
            breakMiddle = true;
            break;
          }
        }
        if (breakMiddle) {
          break;
        }
      }
      i++;
    }
    if (measure.showKey) {
      hInsertPos += this._scoreProps.clefDistanceFromBarline;
      let displayKey = measure.displayKey;
      let outgoingKey = measure.outgoingKey;
      let sharpPositions = _SystemRenderer.TC_SHARP_POSITIONS;
      let flatPositions = _SystemRenderer.TC_FLAT_POSITIONS;
      switch (clefType) {
        case ClefType.TREBLE:
          sharpPositions = _SystemRenderer.TC_SHARP_POSITIONS;
          flatPositions = _SystemRenderer.TC_FLAT_POSITIONS;
          break;
        case ClefType.BASS:
          sharpPositions = _SystemRenderer.BC_SHARP_POSITIONS;
          flatPositions = _SystemRenderer.BC_FLAT_POSITIONS;
          break;
        case ClefType.TENOR:
          sharpPositions = _SystemRenderer.TENOR_SHARP_POSITIONS;
          flatPositions = _SystemRenderer.TENOR_FLAT_POSITIONS;
          break;
        case ClefType.ALTO:
          sharpPositions = _SystemRenderer.ALTO_SHARP_POSITIONS;
          flatPositions = _SystemRenderer.ALTO_FLAT_POSITIONS;
          break;
        default:
          console.log(`unrecognized clef type! ${clefType} VisualSystem.addClefKeyTime()`);
      }
      let accidentalList = PitchUtils.getAccidentalsForNewKeySignature(displayKey, outgoingKey);
      let numAccidentals = 0;
      let textures = MusicTextures.getTextureSet(
        this._scoreProps.pixelsPerSpace
      );
      for (let i2 = 0; i2 < 7; i2++) {
        if (accidentalList[i2] === 1) {
          this._drawAccidental(
            textures.natural,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            flatPositions[i2],
            vStaffPos,
            vSystem
          );
          numAccidentals++;
        }
      }
      for (let i2 = 7; i2 < 14; i2++) {
        if (accidentalList[i2] === 1) {
          this._drawAccidental(
            textures.natural,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            sharpPositions[i2 - 7],
            vStaffPos,
            vSystem
          );
          numAccidentals++;
        }
      }
      for (let i2 = 14; i2 < 21; i2++) {
        if (accidentalList[i2] === 1) {
          this._drawAccidental(
            textures.flat,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            flatPositions[i2 - 14],
            vStaffPos,
            vSystem,
            -0.1
          );
          numAccidentals++;
        } else if (accidentalList[i2] === 2) {
          this._drawAccidental(
            textures.doubleFlat,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            flatPositions[i2 - 14],
            vStaffPos,
            vSystem,
            -0.1
          );
          numAccidentals++;
        }
      }
      for (let i2 = 21; i2 < 28; i2++) {
        if (accidentalList[i2] === 1) {
          this._drawAccidental(
            textures.sharp,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            sharpPositions[i2 - 21],
            vStaffPos,
            vSystem
          );
          numAccidentals++;
        } else if (accidentalList[i2] === 2) {
          this._drawAccidental(
            textures.doubleSharp,
            hInsertPos + numAccidentals * this._scoreProps.keySigWidth,
            sharpPositions[i2 - 21],
            vStaffPos,
            vSystem
          );
          numAccidentals++;
        }
      }
      hInsertPos += this._scoreProps.keySigWidth * measure.stack.maxKeySize;
    }
    if (measure.showTime) {
      let px = this._scoreProps.pixelsPerSpace;
      hInsertPos += this._scoreProps.clefDistanceFromBarline;
      let text = measure.displayNumBeats == null ? measure.numBeats.toString() + "\n" + measure.beatType.toString() : measure.displayNumBeats.toString() + "\n" + measure.displayBeatType.toString();
      let timeTF = new Text2({
        text,
        style: {
          fontFamily: "Arial",
          align: "center",
          fontSize: 2.8 * px,
          fill: 0,
          leading: -0.8 * px * this._scoreProps.noteheadWidth
        }
      });
      timeTF.x = hInsertPos * px;
      timeTF.y = vStaffPos * px - 0.8 * px;
      vSystem.container.addChild(timeTF);
    }
  }
  _drawClef(clefType, smallSize, xPos, vStaffPos, vSystem) {
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let clefShape;
    switch (clefType) {
      case ClefType.TREBLE:
        clefShape = new Sprite4(textures.trebleClef);
        clefShape.y = smallSize ? (vStaffPos - 1) * px : (vStaffPos - 2) * px;
        break;
      case ClefType.BASS:
        clefShape = new Sprite4(textures.bassClef);
        clefShape.y = smallSize ? (vStaffPos + 1 / 6) * px : vStaffPos * px;
        break;
      case ClefType.TENOR:
        clefShape = new Sprite4(textures.tenorClef);
        clefShape.y = smallSize ? (vStaffPos + 1 / 6) * px : vStaffPos * px;
        break;
      case ClefType.ALTO:
        clefShape = new Sprite4(textures.altoClef);
        clefShape.y = smallSize ? (vStaffPos + 1 / 6) * px : vStaffPos * px;
        break;
      default:
        console.log(`unrecognized clef type! ${clefType} VisualSystem.addClefKeyTime()`);
        clefShape = new Sprite4(textures.trebleClef);
    }
    clefShape.x = xPos * this._scoreProps.pixelsPerSpace;
    if (smallSize) {
      clefShape.scale.x = clefShape.scale.y *= 0.75;
    }
    vSystem.container.addChild(clefShape);
  }
  _drawAccidental(accidentalBD, hInsertPos, stepsFromTopStaffLine, vStaffPos, vSystem, vertOffset = 0) {
    let px = this._scoreProps.pixelsPerSpace;
    let accidentalShape = new Sprite4(accidentalBD);
    accidentalShape.x = hInsertPos * px;
    accidentalShape.y = (vStaffPos + stepsFromTopStaffLine * (1 / 2) + vertOffset) * px - accidentalShape.height / 2;
    vSystem.container.addChild(accidentalShape);
  }
  _addAttachments(cMeasure, systemIndent, vStaffPos, visualSystem) {
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let hOffset = systemIndent + cMeasure.stack.startPosition;
    let attachments = cMeasure.attachments;
    if (attachments != null) {
      let numAttachments = attachments.length;
      for (let i = 0; i < numAttachments; i++) {
        let attachment = attachments[i];
        let visualAttachment = null;
        if (attachment instanceof Dynamic) {
          switch (attachment.type) {
            case DynamicType.F:
              visualAttachment = new Sprite4(textures.f);
              break;
            case DynamicType.FF:
              visualAttachment = new Sprite4(textures.ff);
              break;
            case DynamicType.FFF:
              visualAttachment = new Sprite4(textures.fff);
              break;
            case DynamicType.FFFF:
              visualAttachment = new Sprite4(textures.fff);
              break;
            case DynamicType.P:
              visualAttachment = new Sprite4(textures.p);
              break;
            case DynamicType.PP:
              visualAttachment = new Sprite4(textures.pp);
              break;
            case DynamicType.PPP:
              visualAttachment = new Sprite4(textures.ppp);
              break;
            case DynamicType.PPPP:
              visualAttachment = new Sprite4(textures.ppp);
              break;
            case DynamicType.MF:
              visualAttachment = new Sprite4(textures.mf);
              break;
            case DynamicType.MP:
              visualAttachment = new Sprite4(textures.mp);
              break;
            default:
          }
        } else if (attachment instanceof ChordSymbol) {
          visualAttachment = this._drawChordSymbol(attachment);
        }
        if (visualAttachment != null) {
          visualAttachment.x = (attachment.hPos + hOffset) * px;
          visualAttachment.y = (attachment.vPos + vStaffPos) * px;
          visualSystem.container.addChild(visualAttachment);
        }
      }
    }
  }
  _drawChordSymbol(symbol) {
    let fontHeight = this._scoreProps.chordSymbolFontHeight;
    let px = this._scoreProps.pixelsPerSpace;
    let textures = MusicTextures.getTextureSet(px);
    let size = fontHeight * px;
    let dividerWidth = Math.max(px * 0.25, 1);
    let container = new Container3();
    let rootTf = new Text2({
      text: symbol.rootName,
      style: {
        fontFamily: "Arial",
        fontSize: size,
        fill: 0,
        align: "left"
      }
    });
    container.addChild(rootTf);
    let containerWidth = rootTf.width;
    if (symbol.rootAlteration === 1) {
      let sharp = new Sprite4(textures.sharp);
      sharp.scale.set(0.75, 0.75);
      sharp.x = containerWidth;
      container.addChild(sharp);
      containerWidth = containerWidth + sharp.width;
    } else if (symbol.rootAlteration === -1) {
      let flat = new Sprite4(textures.flat);
      flat.scale.set(0.75, 0.75);
      flat.x = containerWidth;
      container.addChild(flat);
      containerWidth = containerWidth + flat.width;
    }
    if (symbol.displayType != null && symbol.displayType !== "") {
      let typeTf = new Text2({
        text: symbol.displayType,
        style: {
          fontFamily: "Arial",
          fontSize: size * 0.75,
          fill: 0,
          align: "left"
        }
      });
      typeTf.x = containerWidth;
      typeTf.y = 0.5 * px;
      container.addChild(typeTf);
      containerWidth += typeTf.width;
    }
    if (symbol.bassName != null) {
      let g = new Graphics3();
      container.addChild(g);
      g.setStrokeStyle({ color: 0, width: dividerWidth });
      g.moveTo(containerWidth + 0.1 * px, px * fontHeight);
      g.lineTo(containerWidth + 0.5 * px, px * 1);
      g.stroke();
      let bassTf = new Text2({
        text: symbol.bassName,
        style: {
          fontFamily: "Arial",
          fontSize: size * 0.75,
          fill: 0,
          align: "left"
        }
      });
      containerWidth += 0.5 * px;
      bassTf.x = containerWidth + 0.25 * px;
      bassTf.y = 0.75 * px;
      container.addChild(bassTf);
      containerWidth += bassTf.width + 0.25 * px;
      if (symbol.bassAlteration === 1) {
        let sharp = new Sprite4(textures.sharp);
        sharp.scale.set(0.75, 0.75);
        sharp.x = containerWidth;
        sharp.y = 0.75 * px;
        container.addChild(sharp);
        containerWidth += sharp.width;
      } else if (symbol.bassAlteration === -1) {
        let flat = new Sprite4(textures.flat);
        flat.scale.set(0.75, 0.75);
        flat.x = containerWidth;
        flat.y = 0.75 * px;
        container.addChild(flat);
        containerWidth += flat.width;
      }
    }
    container.pivot.x = Math.min(containerWidth / 3, 2 * px);
    return container;
  }
  /// draws the vertical barlines for the system - should only be called after
  /// all staves/parts have been added
  _renderVerticalBarlines(staffGroupings, systemIndent, vSystem, vStaffPos, stacks, systemWidth) {
    let px = this._scoreProps.pixelsPerSpace;
    let g = vSystem.g;
    let lightBarlineWidth = Math.max(
      1,
      this._scoreProps.lightBarlineWidth * px
    );
    let heavyBarlineWidth = Math.max(
      1,
      this._scoreProps.heavyBarlineWidth * px
    );
    if (staffGroupings.length > 1 || staffGroupings.length > 0 && staffGroupings[0] > 1) {
      g.setStrokeStyle({ color: 0, width: lightBarlineWidth });
      g.moveTo(systemIndent * px, 0);
      g.lineTo(systemIndent * px, (vStaffPos + 4) * px);
      g.stroke();
    }
    if (stacks[stacks.length - 1].next == null) {
      g.setStrokeStyle({
        color: 0,
        width: heavyBarlineWidth,
        join: "round",
        cap: "butt"
      });
      g.moveTo(systemWidth * px, 0);
      g.lineTo(systemWidth * px, (vStaffPos + 4) * px);
      g.stroke();
      g.setStrokeStyle({ color: 0, width: lightBarlineWidth });
      g.moveTo((systemWidth - 1) * px, 0);
      g.lineTo(
        (systemWidth - 1) * px,
        (vStaffPos + 4) * px
      );
      g.stroke();
    } else {
      g.setStrokeStyle({ color: 0, width: lightBarlineWidth });
      g.moveTo(systemWidth * px, 0);
      g.lineTo(systemWidth * px, (vStaffPos + 4) * px);
      g.stroke();
    }
    g.setStrokeStyle({ color: 0, width: lightBarlineWidth });
    let vertStartPos = 0;
    for (let stavesInGroup of staffGroupings) {
      let vertEndPos = vertStartPos + (stavesInGroup - 1) * (4 + this._scoreProps.staffSpacing) + 4;
      let horizPos = systemIndent;
      for (let i = 0; i < stacks.length - 1; i++) {
        horizPos += stacks[i].width;
        let clefOffset = stacks[i + 1].newClef ? this._scoreProps.clefWidth + this._scoreProps.clefDistanceFromBarline : 0;
        g.moveTo((horizPos + clefOffset) * px, vertStartPos * px);
        g.lineTo((horizPos + clefOffset) * px, vertEndPos * px);
      }
      vertStartPos += stavesInGroup * (4 + this._scoreProps.staffSpacing);
    }
    g.stroke();
  }
  _drawRepeats(staffGroupings, systemIndent, vSystem, vStaffPos, stacks) {
    let px = this._scoreProps.pixelsPerSpace;
    let g = vSystem.g;
    let vertStartPos = 0;
    for (let stavesInGroup of staffGroupings) {
      let vertEndPos = vertStartPos + (stavesInGroup - 1) * (4 + this._scoreProps.staffSpacing) + 4;
      let horizPos = systemIndent;
      for (let i = 0; i < stacks.length; i++) {
        let stack = stacks[i];
        if (stack.repeatDOs != null) {
          for (let rdo of stack.repeatDOs) {
            let heavyPos;
            let lightPos;
            let dotsPos;
            if (rdo.repeatDirection === RepeatDirection.FORWARD) {
              heavyPos = horizPos - 2 * this._scoreProps.measureLeadIn + (i === 0 ? stack.indentAsSystemLeader : stack.indent);
              if (heavyPos > horizPos) {
                heavyPos += 1;
              }
              lightPos = heavyPos + 1;
              dotsPos = lightPos + 0.5;
            } else if (rdo.repeatDirection === RepeatDirection.BACKWARD) {
              heavyPos = horizPos + stack.width;
              lightPos = heavyPos - 1;
              dotsPos = lightPos - 0.5;
            } else if (rdo.endingType != null) {
              let leftPos = horizPos - this._scoreProps.measureLeadIn + (i === 0 ? stack.indentAsSystemLeader : stack.indent);
              let rightPos = horizPos + stack.width;
              let vPos = rdo.endingVPos;
              g.setStrokeStyle({ color: 0, width: this._scoreProps.lightBarlineWidth * px });
              g.moveTo(leftPos * px, vPos * px);
              g.lineTo(rightPos * px, vPos * px);
              g.stroke();
              if (rdo.endingType === EndingType.START) {
                g.moveTo(leftPos * px, vPos * px);
                g.lineTo(leftPos * px, (vPos + 1.5) * px);
                g.stroke();
                if (rdo.endingNumber != null) {
                  let textPos = leftPos + 1;
                  let tf = new Text2({
                    text: rdo.endingNumber,
                    style: {
                      fontFamily: "arial",
                      fontSize: 12 * px / 12,
                      fill: 0
                    }
                  });
                  tf.x = textPos * px;
                  tf.y = (vPos + 1 / 4) * px;
                  vSystem.container.addChild(tf);
                }
              } else if (rdo.endingType === EndingType.STOP) {
                g.moveTo(rightPos * px, vPos * px);
                g.lineTo(rightPos * px, (vPos + 1.5) * px);
                g.stroke();
              }
            }
            if (heavyPos != null) {
              g.setStrokeStyle({
                color: 0,
                width: this._scoreProps.heavyBarlineWidth * px,
                join: "round",
                cap: "butt"
              });
              g.moveTo(heavyPos * px, vertStartPos * px);
              g.lineTo(heavyPos * px, vertEndPos * px);
              g.stroke();
              g.closePath();
              g.setStrokeStyle({ color: 0, width: this._scoreProps.lightBarlineWidth * px });
              g.moveTo(lightPos * px, vertStartPos * px);
              g.lineTo(lightPos * px, vertEndPos * px);
              g.stroke();
              for (let j = 0; j < stavesInGroup; j++) {
                let vPos = vertStartPos + j * (4 + this._scoreProps.staffSpacing);
                g.circle(
                  dotsPos * px,
                  (vPos + 1.5) * px,
                  px / 5
                );
                g.circle(
                  dotsPos * px,
                  (vPos + 2.5) * px,
                  px / 5
                );
                g.fill();
              }
            }
          }
        }
        horizPos += stacks[i].width;
      }
      vertStartPos += stavesInGroup * (4 + this._scoreProps.staffSpacing);
    }
  }
  /// add the ties for the notes on the current staff
  /// @param	vngLists a list of lists of VisualNoteGroups (each voice is a 
  /// list)
  /// @param	vStaffPos the offset from the top of the system that the current
  /// staff begins
  _drawTies(vngLists, vStaffPos, vSystem) {
    let noteheadWidth = this._scoreProps.noteheadWidth;
    let tieStartOffset = 0.8 * noteheadWidth;
    let tieEndOffset = 0.3 * noteheadWidth;
    let nextVNG;
    for (let i = 0; i < vngLists.length; i++) {
      let vngList = vngLists[i];
      let numNotes = vngList.length;
      for (let j = 0; j < numNotes; j++) {
        let cVNG = vngList[j];
        if (cVNG.noteGroup.visible === false) {
          continue;
        }
        for (let note of cVNG.noteGroup.notes) {
          if (note.visible === false) {
            continue;
          }
          switch (note.tieState) {
            case TieState.START:
              if (j < numNotes - 1) {
                nextVNG = vngList[j + 1];
                let nextNote = nextVNG.noteGroup.notes.find(
                  (e) => e.displayCents === note.displayCents
                );
                if (nextVNG.noteGroup.visible && nextNote != null && nextNote.visible) {
                  this._drawTie(
                    cVNG.spx + note.hPos + tieStartOffset,
                    nextVNG.spx + tieEndOffset,
                    note.vPos + vStaffPos,
                    vSystem,
                    cVNG,
                    note.vPos
                  );
                }
              } else {
                this._drawTie(
                  cVNG.spx + note.hPos + tieStartOffset,
                  vSystem.systemRef.systemWidth,
                  note.vPos + vStaffPos,
                  vSystem,
                  cVNG,
                  note.vPos
                );
              }
              break;
            case TieState.CONTINUE:
              if (j < numNotes - 1) {
                nextVNG = vngList[j + 1];
                let nextNote = nextVNG.noteGroup.notes.find(
                  (e) => e.pitchName === note.pitchName
                );
                if (nextVNG.noteGroup.visible && nextNote != null && nextNote.visible) {
                  this._drawTie(
                    cVNG.spx + note.hPos + tieStartOffset,
                    nextVNG.spx + tieEndOffset,
                    note.vPos + vStaffPos,
                    vSystem,
                    cVNG,
                    note.vPos
                  );
                }
                if (j === 0) {
                  this._drawTie(
                    cVNG.spx - 2,
                    cVNG.spx + tieEndOffset,
                    note.vPos + vStaffPos,
                    vSystem,
                    cVNG,
                    note.vPos
                  );
                }
              } else {
                this._drawTie(
                  cVNG.spx + tieStartOffset,
                  vSystem.systemRef.systemWidth,
                  note.vPos + vStaffPos,
                  vSystem,
                  cVNG,
                  note.vPos
                );
              }
              break;
            case TieState.STOP:
              if (j === 0) {
                this._drawTie(
                  cVNG.spx - 2,
                  cVNG.spx + tieEndOffset,
                  note.vPos + vStaffPos,
                  vSystem,
                  cVNG,
                  note.vPos
                );
              }
              break;
            default:
          }
        }
      }
    }
  }
  _drawTie(x1Val, x2Val, yVal, vSystem, vng, vPosInStaff) {
    let px = this._scoreProps.pixelsPerSpace;
    let curveDown = false;
    if (vng.noteGroup.stemDirection === StemDirection2.UP || vng.noteGroup.stemDirection === StemDirection2.NO_STEM && vPosInStaff > 3) {
      curveDown = true;
    }
    let tieShape = new Graphics3();
    tieShape.setStrokeStyle({ color: 0, width: 1 });
    let yControl = curveDown ? 0.7 : -0.7;
    let length = x2Val - x1Val;
    tieShape.moveTo(0, 0);
    tieShape.bezierCurveTo(
      length * px / 3,
      yControl * px,
      2 * length * px / 3,
      yControl * px,
      length * px,
      0
    );
    tieShape.moveTo(0, 0);
    tieShape.bezierCurveTo(
      length * px / 3,
      yControl * px - 1,
      2 * length * px / 3,
      yControl * px - 1,
      length * px,
      0
    );
    tieShape.moveTo(0, 0);
    tieShape.bezierCurveTo(
      length * px / 3,
      yControl * px - 2,
      2 * length * px / 3,
      yControl * px - 2,
      length * px,
      0
    );
    tieShape.stroke();
    tieShape.x = curveDown ? x1Val * px - 1 : x1Val * px - 0;
    tieShape.y = curveDown ? yVal * px + 6 : yVal * px - 6;
    vSystem.container.addChild(tieShape);
    vng.visualAttachments.push(tieShape);
  }
  _drawSlurs(system, vSystem, parts) {
    if (system.slurSegments == null) {
      return;
    }
    let px = this._scoreProps.pixelsPerSpace;
    let g = vSystem.g;
    g.setStrokeStyle({ color: 0, width: 1 });
    for (let segment of system.slurSegments) {
      if (parts.indexOf(segment.slur.firstNote.voice.measure.staff.partRef) === -1) {
        continue;
      }
      if (segment.slur.firstNote.visible === false || segment.slur.endNote.visible === false) {
        continue;
      }
      let points = segment.points;
      g.moveTo(points[0].x * px, points[0].y * px);
      g.bezierCurveTo(
        points[1].x * px,
        points[1].y * px,
        points[2].x * px,
        points[2].y * px,
        points[3].x * px,
        points[3].y * px
      );
      g.moveTo(points[0].x * px, points[0].y * px);
      g.bezierCurveTo(
        points[1].x * px,
        points[1].y * px - 1,
        points[2].x * px,
        points[2].y * px - 1,
        points[3].x * px,
        points[3].y * px
      );
      g.moveTo(points[0].x * px, points[0].y * px);
      g.bezierCurveTo(
        points[1].x * px,
        points[1].y * px - 2,
        points[2].x * px,
        points[2].y * px - 2,
        points[3].x * px,
        points[3].y * px
      );
      g.stroke();
    }
  }
  /// These are the VisualNoteGroup objects from the previous render session. 
  /// They are re-used when possible.
  get legacyVNoteGroups() {
    return this._legacyVNoteGroups;
  }
  set legacyVNoteGroups(value) {
    this._legacyVNoteGroups = value;
    this._legacyVNGIndex = 0;
  }
};
///positions of accidentals in key signature for each clef, in steps from 
///top staff line
_SystemRenderer.TC_FLAT_POSITIONS = [4, 1, 5, 2, 6, 3, 7];
_SystemRenderer.BC_FLAT_POSITIONS = [6, 3, 7, 4, 8, 5, 9];
_SystemRenderer.TC_SHARP_POSITIONS = [0, 3, -1, 2, 5, 1, 4];
_SystemRenderer.BC_SHARP_POSITIONS = [2, 5, 1, 4, 7, 3, 6];
_SystemRenderer.TENOR_FLAT_POSITIONS = [3, 0, 4, 1, 5, 2, 6];
_SystemRenderer.TENOR_SHARP_POSITIONS = [6, 2, 5, 1, 4, 0, 3];
_SystemRenderer.ALTO_FLAT_POSITIONS = [5, 2, 6, 3, 7, 4, 8];
_SystemRenderer.ALTO_SHARP_POSITIONS = [1, 4, 0, 3, 6, 2, 5];
var SystemRenderer = _SystemRenderer;

// src/score_render/bitmapRenderer/VisualPage.ts
import { Container as Container4 } from "pixi.js";
import { ScoreProperties as ScoreProperties3 } from "score_data";
var _VisualPage = class _VisualPage extends Container4 {
  constructor(visualScore, scoreProps) {
    super();
    this._visualScore = visualScore;
    this._scoreProps = scoreProps;
    this._currentVSystemPos = 0;
    this._visualSystems = [];
  }
  _addSystem(visualSystem) {
    visualSystem.x = this._scoreProps.leftPageMargin;
    visualSystem.y = this._currentVSystemPos;
    this.addChild(visualSystem);
    visualSystem.pageRef = this;
    let scale = this._scoreProps.pixelsPerSpace / ScoreProperties3.DEFAULT_PIXELS_PER_SPACE;
    this._currentVSystemPos += visualSystem.height + _VisualPage.DISTANCE_BETWEEN_SYSTEMS * scale;
    this._visualSystems.push(visualSystem);
  }
  /**
   * removes all VisualSystems from the page
   */
  _removeAllSystems() {
    for (let vSystem of this._visualSystems) {
      if (vSystem.parent === this) {
        this.removeChild(vSystem);
      }
    }
    this._visualSystems = [];
    this._currentVSystemPos = 0;
  }
  /**
   * Replaces one VisualSystem with another
   * @param originalVSystem the VisualSystem you want to replace
   * @param newVSystem the VisualSystem you want to replace it with
   */
  _replaceSystem(originalVSystem, newVSystem) {
    newVSystem.x = originalVSystem.x;
    newVSystem.y = originalVSystem.y;
    this.removeChild(originalVSystem);
    this.addChild(newVSystem);
    newVSystem.pageRef = this;
    for (let i = 0; i < this._visualSystems.length; i++) {
      if (this._visualSystems[i] === originalVSystem) {
        this._visualSystems[i] = newVSystem;
        break;
      }
    }
  }
  get visualSystems() {
    return this._visualSystems;
  }
  get pageHeight() {
    return this._currentVSystemPos - _VisualPage.DISTANCE_BETWEEN_SYSTEMS * (this._scoreProps.pixelsPerSpace / ScoreProperties3.DEFAULT_PIXELS_PER_SPACE);
  }
};
_VisualPage.DISTANCE_BETWEEN_SYSTEMS = 50;
var VisualPage = _VisualPage;

// src/score_render/bitmapRenderer/VisualScore.ts
import { Container as Container5, Graphics as Graphics4, Point as Point4 } from "pixi.js";
import { ScoreProperties as ScoreProperties4 } from "score_data";

// src/score_render/bitmapRenderer/ClickRegion.ts
var ClickRegion = class {
  constructor(scoreX, scoreY, width, height) {
    this.scoreX = scoreX;
    this.scoreY = scoreY;
    this.width = width;
    this.height = height;
  }
};

// src/score_render/bitmapRenderer/VisualScore.ts
var _VisualScore = class _VisualScore extends Container5 {
  constructor(pixiAppHelper, score) {
    super();
    this._clickRegions = [];
    this.debugShowClicks = false;
    this._pah = pixiAppHelper;
    this._score = score;
    this._scoreProps = score.scoreProperties;
    this._visualPages = [];
    this._firstPageVerticalOffset = score.scoreProperties.pixelsPerSpace * score.scoreProperties.firstPageVerticalOffset;
    this._currentVPagePos = this._firstPageVerticalOffset;
    this._init();
  }
  get graphics() {
    return this._g;
  }
  _init() {
    this._g = new Graphics4();
  }
  deleteSystemTextures() {
    let vSystems = this.getVisualSystems();
    for (let vsys of vSystems) {
      vsys.deleteTexture();
    }
  }
  /**
   * adds a single invisible pixel to each of the four corners of the score so
   * that it can be resized properly
   */
  addWidthHeightMarkers() {
    throw "not implemented";
    this._g.setStrokeStyle({ width: 1, color: 0 });
    this._g.lineTo(0, 1);
    this._g.moveTo(this._scoreProps.pageWidth, 0);
    this._g.lineTo(this._scoreProps.pageWidth, 1);
  }
  /**
   * Gets all of the VisualSystem objects in the VisualScore
   * @return
   */
  getVisualSystems() {
    let vSystems = [];
    for (let page of this._visualPages) {
      let systems = page.visualSystems;
      for (let system of systems) {
        vSystems.push(system);
      }
    }
    return vSystems;
  }
  createMeasureToVisualMeasureMap() {
    this._visualMeasuresMap = /* @__PURE__ */ new Map();
    for (let page of this._visualPages) {
      for (let system of page.visualSystems) {
        for (let vm of system.visualMeasures) {
          this._visualMeasuresMap.set(vm.measureRef, vm);
        }
      }
    }
  }
  getVisualMeasureForMeasure(measure) {
    if (this._visualMeasuresMap == null) {
      this.createMeasureToVisualMeasureMap();
    }
    return this._visualMeasuresMap.get(measure);
  }
  /**
   * Gets all of the VisualNoteGroup objects in the score
   */
  getVisualNoteGroups() {
    if (this._visualNoteGroupsCache != null) {
      return this._visualNoteGroupsCache;
    }
    let vNoteGroups = new Array();
    for (let page of this._visualPages) {
      let vsystems = page.visualSystems;
      for (let vsystem of vsystems) {
        let vnoteGroups = vsystem.visNoteGroups;
        for (let vng of vnoteGroups) {
          vNoteGroups.push(vng);
        }
      }
    }
    this._visualNoteGroupsCache = vNoteGroups;
    return vNoteGroups;
  }
  clearVisualNoteGroupCache() {
    this._visualNoteGroupsCache = null;
  }
  ///returns a [Map] of the [NoteGroups] in the score to their VisualNoteGroup
  ///counterparts.
  getNgToVngMap() {
    let vngList = this.getVisualNoteGroups();
    let map = /* @__PURE__ */ new Map();
    for (let vng of vngList) {
      map.set(vng.noteGroup, vng);
    }
    return map;
  }
  /**
   * gets the VisualSystem object that lies under the passed in Point
   * @param point a Point object relative to the score origin with coordinates
   * of the point to check against each system in the score
   * @return a VisualSystem if one is found, otherwise null
   */
  getVisualSystemUnderPoint(point) {
    for (let page of this.visualPages) {
      if (page.y <= point.y && page.y + page.pageHeight > point.y && page.x <= point.x && page.x + this.scoreWidth > point.x) {
        for (let visSystem of page.visualSystems) {
          if (visSystem.scoreX <= point.x && visSystem.scoreX + visSystem.width > point.x && visSystem.scoreY <= point.y && visSystem.scoreY + visSystem.height > point.y) {
            return visSystem;
          }
        }
      }
    }
    return null;
  }
  /**
   * gets the VisualMeasure object that lies under the passed in Point
   * @param point a Point object relative to the score origin with
   * coordinates of the point to check against each measure in the system
   * @return a VisualMeasure if one is found, otherwise null
   * 
   * Note - in general gridSearch will be faster
   */
  getVisualMeasureUnderPoint(point) {
    let sys = this.getVisualSystemUnderPoint(point);
    if (sys == null) {
      return null;
    }
    let sysPoint = new Point4(point.x - sys.scoreX, point.y - sys.scoreY);
    return sys.getVisualMeasureUnderPoint(sysPoint);
  }
  /**
   * gets the VisualNoteGroup object that lies under the passed in Point
   * @param point a Point object relative to the score origin with
   * coordinates of the point to check against each measure in the system
   * @return a VisualMeasure if one is found, otherwise null
   * 
   * Note - in general gridSearch will be much faster
   */
  getVisualNoteGroupUnderPoint(point) {
    for (let vSys of this.getVisualSystems()) {
      let sysPoint = new Point4(
        point.x - vSys.scoreX,
        point.y - vSys.scoreY
      );
      let vng = vSys.getVisualNoteGroupUnderPoint(sysPoint);
      if (vng != null) {
        return vng;
      }
    }
  }
  addClickRegion(region, invalidateGrid) {
    this._clickRegions.push(region);
    if (invalidateGrid) {
      this._grid = null;
    }
  }
  removeClickRegion(region, invalidateGrid) {
    for (let i = this._clickRegions.length - 1; i >= 0; i--) {
      if (this._clickRegions[i] === region) {
        this._clickRegions.splice(i, 1);
      }
    }
    if (invalidateGrid) {
      this._grid = null;
    }
  }
  /**checks the score for a VisualSystem, VisualMeasure, VisualNoteGroup and
   * ClickRegion under the point indicated in the PointDetails.scorePoint
   * property. Set PointDetails.matchMeasure and PointDetails.matchNoteGroup
   * to indicate whether the search should include those types of objects. The
   * details are added to the passed in object.
   * 
   * This search uses a grid for fast searching, but the grid must be created
   * once after the score has been rendered, and this operation is expensive.
   * It's a good idea to run this method once after a render whenever there
   * isn't much going on. If the details param is null, no search will occur,
   * but the grid will be created if it doesn't exist yet.
   */
  gridSearch(details = null) {
    if (this._grid == null) {
      this._createSearchGrid();
    }
    if (details == null) {
      return;
    }
    let x = details.scorePoint.x;
    let y = details.scorePoint.y;
    if (this.debugShowClicks) {
      var circle = new Graphics4();
      circle.circle(0, 0, 3);
      circle.fill(16711680);
      circle.x = x;
      circle.y = y;
      this.addChild(circle);
    }
    let cellRow = Math.floor(y / _Cell.cellHeight);
    let cellCol = Math.floor(x / _Cell.cellWidth);
    if (cellRow >= this._grid.length || cellCol >= this._grid[0].length || cellRow < 0 || cellCol < 0) {
      return;
    }
    let cell = this._grid[cellRow][cellCol];
    for (let vSys of cell.vSystems) {
      if (vSys.scoreX <= x && vSys.scoreX + vSys.width > x && vSys.scoreY <= y && vSys.scoreY + vSys.height > y) {
        details.visualSystem = vSys;
        break;
      }
    }
    if (details.matchMeasure) {
      for (let vMeas of cell.vMeasures) {
        if (vMeas.scoreX <= x && vMeas.scoreX + vMeas.width > x && vMeas.scoreY <= y && vMeas.scoreY + vMeas.height > y) {
          details.visualMeasure = vMeas;
          break;
        }
      }
    }
    if (details.matchNoteGroup) {
      for (let vng of cell.vNoteGroups) {
        if (vng.scoreX + vng.content.minX <= x && vng.scoreX + vng.content.maxX > x && vng.scoreY + vng.content.minY <= y && vng.scoreY + vng.content.maxY > y) {
          details.visualNoteGroup = vng;
          break;
        }
        if (this.debugShowClicks) {
          var rect = new Graphics4();
          rect.setStrokeStyle({ color: 16711680, width: 1 });
          let c = vng.content;
          rect.rect(0, 0, c.maxX - c.minX, c.maxY - c.minY);
          rect.x = vng.scoreX + c.minX;
          rect.y = vng.scoreY + c.minY;
          this.addChild(rect);
        }
      }
    }
    if (details.matchClickRegions) {
      for (let region of cell.regions) {
        if (region.scoreX <= x && region.scoreX + region.width > x && region.scoreY <= y && region.scoreY + region.height > y) {
          details.clickRegion = region;
          break;
        }
      }
    }
  }
  _addPage(page) {
    page.x = 0;
    page.y = this._currentVPagePos;
    this.addChild(page);
    let scale = this._scoreProps.pixelsPerSpace / ScoreProperties4.DEFAULT_PIXELS_PER_SPACE;
    this._currentVPagePos += page.pageHeight + _VisualScore.DISTANCE_BETWEEN_PAGES * scale;
    this._visualPages.push(page);
  }
  _removeAllPages() {
    for (let page of this._visualPages) {
      if (page.parent === this) {
        this.removeChild(page);
      }
    }
    this._visualPages = [];
    this._currentVPagePos = this._firstPageVerticalOffset;
  }
  /**
   * adds mouse over note name labels to the notes
   */
  //	void addNoteNameLabels() {
  //		_noteLabeler = new NoteLabeler(this);
  //	}
  /**
   * removes mouse over note name labels
   */
  //	void removeNoteNameLabels() {
  //		_noteLabeler.kill();
  //		_noteLabeler = null;
  //	}
  /**
   * performs cleanup tasks
   */
  prepareForRemoval() {
    this.deleteSystemTextures();
    this.destroy({
      children: true,
      texture: true,
      context: true
    });
  }
  _createSearchGrid() {
    let numCols = Math.ceil(this.scoreWidth / _Cell.cellWidth);
    let numRows = Math.ceil(this.scoreHeight / _Cell.cellHeight);
    this._grid = [];
    for (let row = 0; row < numRows; row++) {
      let rowOfCells = [];
      this._grid.push(rowOfCells);
      for (let col = 0; col < numCols; col++) {
        rowOfCells.push(new _Cell());
      }
    }
    let minX;
    let maxX;
    let minY;
    let maxY;
    let vSystems = this.getVisualSystems();
    for (let vSys of vSystems) {
      minX = vSys.scoreX;
      maxX = vSys.scoreX + vSys.width;
      minY = vSys.scoreY;
      maxY = vSys.scoreY + vSys.height;
      this._setCellMatches(minX, maxX, minY, maxY, vSys);
      for (let vMeas of vSys.visualMeasures) {
        minX = vSys.scoreX + vMeas.x;
        maxX = minX + vMeas.width;
        minY = vSys.scoreY + vMeas.y;
        maxY = minY + vMeas.height;
        this._setCellMatches(minX, maxX, minY, maxY, vMeas);
      }
      for (let vng of vSys.visNoteGroups) {
        minX = vSys.scoreX + vng.x;
        maxX = minX + vng.width;
        minY = vSys.scoreY + vng.y;
        maxY = minY + vng.height;
        this._setCellMatches(minX, maxX, minY, maxY, vng);
      }
    }
    for (let region of this._clickRegions) {
      minX = region.scoreX;
      maxX = minX + region.width;
      minY = region.scoreY;
      maxY = minY + region.height;
      this._setCellMatches(minX, maxX, minY, maxY, region);
    }
  }
  _setCellMatches(minX, maxX, minY, maxY, obj) {
    if (minX < 0) {
      minX = 0;
    }
    if (minY < 0) {
      minY = 0;
    }
    if (maxX > this.scoreWidth) {
      maxX = this.scoreWidth;
    }
    if (maxY > this.scoreHeight) {
      maxY = this.scoreHeight;
    }
    if (minX >= maxX || minY >= maxY) {
      return;
    }
    let isVNG = obj instanceof VisualNoteGroup;
    let isVMeas = !isVNG && obj instanceof VisualMeasure;
    let isVSys = !isVNG && !isVMeas && obj instanceof VisualSystem;
    let isReg = !isVNG && !isVMeas && !isVSys && obj instanceof ClickRegion;
    let grid = this._grid;
    let startCellRow = Math.floor(minY / _Cell.cellHeight);
    let startCellCol = Math.floor(minX / _Cell.cellWidth);
    let row = startCellRow;
    while (row * _Cell.cellHeight < maxY) {
      let col = startCellCol;
      while (col * _Cell.cellWidth < maxX) {
        if (isVNG) {
          grid[row][col].vNoteGroups.add(obj);
        } else if (isVMeas) {
          grid[row][col].vMeasures.add(obj);
        } else if (isVSys) {
          grid[row][col].vSystems.add(obj);
        } else if (isReg) {
          grid[row][col].regions.add(obj);
        }
        col++;
      }
      row++;
    }
  }
  get pixiAppHelper() {
    return this._pah;
  }
  /**
   * The visual pages that are children of this VisualScore
   */
  get visualPages() {
    return this._visualPages;
  }
  set visualPages(value) {
    this._visualPages = value;
  }
  /**
   * The ScoreProperties object from the Score
   */
  get scoreProps() {
    return this._scoreProps;
  }
  /**
   * The Score object used to generate this VisualScore
   */
  get score() {
    return this._score;
  }
  /**gets the width of the score in pixels using the pageWidth from
   * ScoreProperties */
  get scoreWidth() {
    return this._score.scoreProperties.pageWidth * this._score.scoreProperties.pixelsPerSpace;
  }
  /**gets the calculated height of the rendered score in pixels */
  get scoreHeight() {
    return this._currentVPagePos - _VisualScore.DISTANCE_BETWEEN_PAGES;
  }
  get firstPageVerticalOffset() {
    return this._firstPageVerticalOffset;
  }
  set firstPageVerticalOffset(value) {
    this._firstPageVerticalOffset = value;
  }
};
//displays note name tool tips on notes upon mouseover
//NoteLabeler _noteLabeler;
_VisualScore.DISTANCE_BETWEEN_PAGES = 50;
var VisualScore = _VisualScore;
var _Cell = class {
  constructor() {
    this.vSystems = /* @__PURE__ */ new Set();
    this.vMeasures = /* @__PURE__ */ new Set();
    this.vNoteGroups = /* @__PURE__ */ new Set();
    this.regions = /* @__PURE__ */ new Set();
  }
};
_Cell.cellWidth = 200;
_Cell.cellHeight = 200;

// src/score_render/bitmapRenderer/BitmapMusicRenderer.ts
var _BitmapMusicRenderer = class _BitmapMusicRenderer {
  constructor(pixiAppHelper) {
    _BitmapMusicRenderer._renderersInUse++;
    if (_BitmapMusicRenderer._renderersInUse > _BitmapMusicRenderer.maxRenderers) {
      throw "Max number of BitmapMusicRenderers exceeded";
    }
    this._pah = pixiAppHelper;
  }
  /**
   * Creates a VisualScore object from the passed in Score object
   * @param	score the Score object that provides the source
   * @param	partNames the list of parts you want rendered
   * @return	a VisualScore object that can be displayed
   */
  renderScore(score, parts = null) {
    if (this._visualScore != null) {
      this._visualScore.prepareForRemoval();
    }
    this._score = score;
    this._visualScore = new VisualScore(this._pah, score);
    this._addedObjects = [];
    this._removedObjects = [];
    this._parts = parts == null ? score.parts : parts;
    let systems = score.getSystems();
    this._systemRenderer = new SystemRenderer(score.scoreProperties);
    let vPages = [];
    let cVPage = null;
    for (let system of systems) {
      let vSystem = this._systemRenderer.renderSystem(
        this._visualScore,
        system,
        this._parts,
        this._addedObjects,
        this._removedObjects
      );
      if (system.newPage) {
        cVPage = new VisualPage(
          this._visualScore,
          score.scoreProperties
        );
        vPages.push(cVPage);
      }
      if (cVPage != null) {
        cVPage._addSystem(vSystem);
      }
      system.needsRendering = false;
    }
    for (let i = 0; i < vPages.length; i++) {
      this._visualScore._addPage(vPages[i]);
    }
    return this._visualScore;
  }
  reRender() {
    if (this._visualScore != null) {
      this._visualScore.deleteSystemTextures();
    }
    this._addedObjects = [];
    this._removedObjects = [];
    let systems = this._score.getSystems();
    let vSystems = this._visualScore.getVisualSystems();
    let vPages = this._visualScore.visualPages;
    this._systemRenderer.legacyVNoteGroups = this._visualScore.getVisualNoteGroups();
    this._visualScore.clearVisualNoteGroupCache();
    for (let vPage of vPages) {
      vPage._removeAllSystems();
    }
    this._visualScore._removeAllPages();
    let numSystems = systems.length;
    let pageIndex = -1;
    systems[0].newPage = true;
    for (let i2 = 0; i2 < numSystems; i2++) {
      let cSystem = systems[i2];
      if (cSystem.newPage) {
        pageIndex++;
        if (pageIndex >= vPages.length) {
          vPages[pageIndex] = new VisualPage(
            this._visualScore,
            this._score.scoreProperties
          );
        }
      }
      if (!cSystem.needsRendering) {
        let vSystemFound = false;
        for (let vSystem of vSystems) {
          if (vSystem.systemRef === cSystem) {
            vPages[pageIndex]._addSystem(vSystem);
            vSystemFound = true;
            break;
          }
        }
        if (!vSystemFound) {
          vPages[pageIndex]._addSystem(
            this._systemRenderer.renderSystem(
              this._visualScore,
              cSystem,
              this._parts,
              this._addedObjects,
              this._removedObjects
            )
          );
        }
      } else {
        vPages[pageIndex]._addSystem(this._systemRenderer.renderSystem(
          this._visualScore,
          cSystem,
          this._parts,
          this._addedObjects,
          this._removedObjects
        ));
        cSystem.needsRendering = false;
      }
    }
    let i = vPages.length - 1;
    while (i >= 0) {
      if (vPages[i].visualSystems.length === 0) {
        vPages.pop();
      } else {
        break;
      }
      i--;
    }
    for (let i2 = 0; i2 < vPages.length; i2++) {
      this._visualScore._addPage(vPages[i2]);
    }
  }
  /**
   * returns all VisualNoteGroup objects in the score (a means of accessing
   * all notes in the song)
   * @return Vector of VisualNoteGroup objects
   */
  getVisualNoteGroups() {
    return this._visualScore.getVisualNoteGroups();
  }
  /**
   * contains DisplayObjects that were added during the latest render
   */
  get addedObjects() {
    return this._addedObjects;
  }
  set addedObjects(value) {
    this._addedObjects = value;
  }
  /**
   * contains DisplayObjects that were removed during the latest render
   */
  get removedObjects() {
    return this._removedObjects;
  }
  set removedObjects(value) {
    this._removedObjects = value;
  }
};
/**The maximum instances of this class that are allowed to be created. This
 * exists for memory management purposes. When renderScore is called,
 * existing VisualSystem texture images are disposed of to start fresh
 * with the new render. The BitmapMusicRenderer handles this, so if a new
 * renderer is created each time a score is rendered, it could prevent this
 * cleanup. In general it's best to re-use the same BitmapMusicRenderer over
 * and over. An exception would be if you need to have more than one
 * VisualScore visible at a time.
*/
_BitmapMusicRenderer.maxRenderers = 1;
_BitmapMusicRenderer._renderersInUse = 0;
var BitmapMusicRenderer = _BitmapMusicRenderer;

// src/score_render/bitmapRenderer/PointDetails.ts
var PointDetails = class {
  constructor(scorePoint, matchMeasure, matchNoteGroup) {
    this.scorePoint = scorePoint;
    this.matchMeasure = matchMeasure;
    this.matchNoteGroup = matchNoteGroup;
  }
};

// src/score_render/pixi/PixiAppHelper.ts
import { UPDATE_PRIORITY } from "pixi.js";
var PixiAppHelper = class {
  /**
   * Create and initialize the pixiApp before passing to the PixiAppHelper.
   * 
   * Call stopTicker() to stop the app Ticker and startTicker to restart.
   * Generally we leave it on, set renderOnRequest = true, and call
   * requestRender if we want to manage drawing.
   * 
   * To stop the other Tickers, use
   * Ticker.shared.stop();
   * Ticker.system.stop();
   * 
   * Notes when calling pixiApp.init()...
   * 1. leave sharedTicker false and generally avoid using it. Use
   * app.ticker instead.
   * 2. Leave  resizeTo null and instead call resizeCanvas or resizeToParent.
   * Call these from a window resize handler (or other times needed). Listen
   * for renderer's resize event to respond to the resize completion. (Only
   * one class should listen to the window resize and call resize methods.) If
   * you want automatic resizing, use setAutoResizeElement(),
   * enableAutoResize() and disableAutoResize(). These will enable the window
   * resize event handler and dispatch the same resize event from the
   * renderer.
   * 3. Set autoDensity to true to make resizing set both the canvas and css
   * width/height. It's best to leave this false if CSS is auto resizing the
   * canvas.
   */
  constructor(pixiApp) {
    this._renderOnRequest = false;
    this._needsRender = false;
    this._autoResizeEnabled = false;
    this._app = pixiApp;
    this._onAppTick = this._onAppTick.bind(this);
    this._app.ticker.remove(this._app.render, this._app);
    this._app.ticker.add(this._onAppTick, this, UPDATE_PRIORITY.LOW);
  }
  get app() {
    return this._app;
  }
  get renderOnRequest() {
    return this._renderOnRequest;
  }
  set renderOnRequest(value) {
    this._renderOnRequest = value;
  }
  resizeCanvas(width, height) {
    this._app.renderer.resize(width, height);
  }
  resizeToParent() {
    let parent = this._app.canvas.parentElement;
    if (parent == null) {
      return;
    }
    this.resizeCanvas(parent.clientWidth, parent.clientHeight);
  }
  /**usually this is called automatically when the window resizes. But if
   * the canvas resizes without a window resize, it may not get triggered, so
   * you can call this manually. */
  resizeToAutoResizeElement() {
    if (this._autoResizeElement != null) {
      this.resizeCanvas(
        this._autoResizeElement.clientWidth,
        this._autoResizeElement.clientHeight
      );
    }
  }
  setAutoResizeElement(el) {
    this._autoResizeElement = el;
    if (this._autoResizeEnabled) {
      this._app.resizeTo = el;
    }
  }
  enableAutoResize() {
    this._autoResizeEnabled = true;
    if (this._autoResizeElement != null) {
      this._app.resizeTo = this._autoResizeElement;
    }
  }
  disableAutoResize() {
    this._autoResizeEnabled = false;
    this._app.resizeTo = null;
  }
  startTicker() {
    this._app.ticker.start();
  }
  stopTicker() {
    this._app.ticker.stop();
  }
  requestRender() {
    this._needsRender = true;
  }
  _onAppTick() {
    if (!this._renderOnRequest || this._needsRender) {
      this._app.render();
      this._needsRender = false;
    }
  }
};
export {
  BitmapMusicRenderer,
  ClickRegion,
  MusicSymbolsSvg,
  MusicTextures,
  NoteheadType,
  PixiAppHelper,
  PointDetails,
  Symbol,
  SystemRenderer,
  TextureSet,
  VNGContent,
  VisualMeasure,
  VisualNoteGroup,
  VisualPage,
  VisualScore,
  VisualSystem
};
